import React, { useMemo, useState, useEffect, useRef, useCallback, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  DSS,
  Bollinger,
  ATR,
  RSI,
  ADX,
  Swing,
  SMA,
  EMA,
  EMACloud,
  TrendlineV1,
  VWAP,
  SMC,
} from '@andywilliams/indicators';
import { DWLFChart, TradeJournalOverlay, useChartAnimations } from '@andywilliams/dwlf-charting';
import { keyLevelEventsInRange } from '../features/markets/keyLevels';
import { adxSeries as computeAdxSeries, adxEvents, adxPaneTitle, ADX_GUIDE_LEVELS, ADX_COLORS } from '../features/markets/adxPane';
import { goldenDeathCrossEvents, CROSSOVER_META, smaReclaimEvents } from '../features/markets/smaCrossover';
import {
  smcExtraEvents, latestPerId, weinsteinStageEvents, squeezeSpans, buildSqueezeBand,
} from '../features/markets/chartEventFamilies';
import { atrPercentSeries, atrRegimeEvents, atrPaneTitle } from '../features/markets/atrIndicator';
import {
  rsiSeries as computeRsiSeries, rsiEvents, rsiPaneTitle, RSI_GUIDE_LEVELS, divergenceSegments, divergenceOscillator,
  divergenceDirection, dssSourceMatches, DIVERGENCE_COLORS,
} from '../features/markets/rsiDivergence';
import { fibGuides } from '../features/markets/cycleFib';
import {
  computeCycleTrendlines, cycleTrendlineSeries, CYCLE_TRENDLINE_TIMEFRAMES,
} from '../features/markets/cycleTrendlines';
import {
  addToWatchlist,
  removeFromWatchlist,
  fetchIndicatorSettings,
} from '../features/markets/marketsSlice';

// Utility functions from marketDetailUtils
import {
  normalizeNumericArray,
  normalizeStringArray,
  reconcileCustomEventSelections,
  arraysEqual,
} from '../features/markets/marketDetailUtils';

// Chart Annotations
import useChartAnnotations from '../hooks/useChartAnnotations';
import AnnotationToolbar from './chart/AnnotationToolbar';
import AnnotationEditPopover from './chart/AnnotationEditPopover';
import TextAnnotationInput from './chart/TextAnnotationInput';
import CustomEventLegend from './chart/CustomEventLegend';
import ChartToolbarDropdown from './chart/ChartToolbarDropdown';
import { alertGuides } from './chart/alertGuides';

// DWLFLoader for animation overlay
import DWLFLoader from './DWLFLoader';
import {
  bandTimeframe, buildCycleDepthZone, buildCycleWindowBand, buildLegacyStateBand, buildPastCycleWindows, isStoreTimeframe,
} from '../features/markets/cycleWindowBand';
import { buildStageBand } from '../features/markets/stageBand';
import useIsDark from '../hooks/useIsDark';
import useSchemeColors from '../hooks/useSchemeColors';
import { candleStyle } from './chart/candleStyle';
import { seriesValuesAt } from './chart/seriesValuesAt';
import { swingPivotLines } from '../utils/swingTooltip';
import { computeTrendlineBreakSegments } from '../utils/trendlines';
import { olderHistoryStep } from '../utils/olderHistoryStep';
import { smcOptionsFor, fvgIsLive, fvgFill } from '../utils/smcZones';
import useTimeframeSwitchMove from '../hooks/useTimeframeSwitchMove';

/* eslint-disable indent */

// True when a keystroke is destined for a text field, so chart-level keyboard
// shortcuts must keep their hands off it — Shift is a capital letter first and
// a quick-measure modifier second.
const isEditableTarget = (target) => {
  if (!target || typeof target.tagName !== 'string') {
    return false;
  }
  const tag = target.tagName.toUpperCase();
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable === true;
};

// Muted variants of the saturated event colours used by the marker
// bands above the chart. The full-saturation versions are still used
// elsewhere (callouts, badges, etc.) for visual consistency, but in
// the marker band the saturated versions felt too "video-game-HUD".
// These are HSL-desaturated by ~30% — same hue, less saturation,
// slightly darker. Falls back to the original colour if it's not in
// this map.
//
// The first three are the custom-event group colours from
// getEventColor (bullish/bearish/neutral). The remaining five are the
// indicator-event colours from INDICATOR_EVENT_META.
const MUTED_EVENT_COLORS = {
  '#22c55e': '#4d8f6a', // custom: bullish green (also confirmed cycle low)
  '#ef4444': '#c46868', // custom: bearish red (also confirmed cycle high)
  '#3b82f6': '#5b7eb3', // custom: neutral blue
  '#FBC02D': '#a8893e', // indicator: swing yellow
  '#E91E63': '#a55670', // indicator: dss pink
  '#FFD54F': '#b89e5e', // indicator: bollinger yellow
  '#FF8A80': '#b07d7a', // indicator: trendline coral
  '#26A69A': '#3f8580', // indicator: support/resistance teal
  '#0ea5e9': '#4d7d96', // indicator: cycle low live (sky blue)
  '#f97316': '#a87553', // indicator: cycle high live (orange)
};

const SMA_OPTIONS = [
  { period: 10, color: '#FFD700', label: 'SMA 10' },
  { period: 20, color: '#FF6347', label: 'SMA 20' },
  { period: 50, color: '#32CD32', label: 'SMA 50' },
  { period: 100, color: '#20B2AA', label: 'SMA 100' },
  { period: 200, color: '#9370DB', label: 'SMA 200' },
];

const EMA_OPTIONS = [
  { period: 10, color: '#1E90FF', label: 'EMA 10' },
  { period: 20, color: '#FF8C00', label: 'EMA 20' },
  { period: 50, color: '#00CED1', label: 'EMA 50' },
  { period: 200, color: '#FF1493', label: 'EMA 200' },
];
const DEFAULT_EMA_CLOUD_LENGTHS = EMA_OPTIONS.map(({ period }) => period);

const INDICATOR_EVENT_META = {
  swing: { color: '#FBC02D', label: 'Swing' },
  dss: { color: '#E91E63', label: 'DSS' },
  atr: { color: '#FF7043', label: 'ATR' },
  rsi: { color: '#AB47BC', label: 'RSI' },
  adx: { color: ADX_COLORS.adx, label: 'ADX' },
  bollinger: { color: '#FFD54F', label: 'Bollinger' },
  trendline: { color: '#FF8A80', label: 'Trendline' },
  keyLevels: { color: '#26A69A', label: 'Key Levels' },
  vwap: { color: '#B388FF', label: 'VWAP' },
  cycleFib: { color: '#FFB74D', label: 'Cycle Fib' },
  smc: { color: '#90A4AE', label: 'SMC' },
  weinstein: { color: '#64748B', label: 'Weinstein Stage' },
};

// VWAP anchor periods the chart overlay can render. Each is an
// independently-toggleable overlay (a user may show monthly + quarterly
// together). Colours are a purple family — shades distinguish the
// anchors while reading as one indicator. `line` is the VWAP line,
// `band` the ±σ bands.
const VWAP_ANCHOR_OPTIONS = [
  { key: 'month', label: 'VWAP Monthly', shortLabel: 'Monthly', line: '#B388FF', band: '#9575CD' },
  { key: 'quarter', label: 'VWAP Quarterly', shortLabel: 'Quarterly', line: '#CE93D8', band: '#BA68C8' },
  { key: 'year', label: 'VWAP Yearly', shortLabel: 'Yearly', line: '#7E57C2', band: '#673AB7' },
  // `all` anchors once at the first bar and never re-anchors — the
  // all-time volume-weighted cost basis (a macro reference line).
  { key: 'all', label: 'VWAP Full History', shortLabel: 'Full History', line: '#5E35B1', band: '#4527A0' },
];
const VWAP_ANCHOR_KEYS = new Set(VWAP_ANCHOR_OPTIONS.map((option) => option.key));

// Smart Money Concepts overlays the chart computes client-side from the candle
// history (recompute-on-read — never stored as annotations). Each key is a
// family the SMC detector emits; toggled independently like VWAP anchors.
const SMC_OVERLAY_OPTIONS = [
  { key: 'fvg', label: 'Fair Value Gaps', shortLabel: 'Fair Value Gaps', color: '#4CAF50' },
  { key: 'orderBlocks', label: 'Order Blocks', shortLabel: 'Order Blocks', color: '#2196F3' },
  { key: 'structure', label: 'BOS / ChoCH', shortLabel: 'BOS / ChoCH', color: '#FF9800' },
  { key: 'liquidity', label: 'Liquidity Pools', shortLabel: 'Liquidity', color: '#9C27B0' },
  { key: 'premiumDiscount', label: 'Premium / Discount', shortLabel: 'Prem/Disc', color: '#607D8B' },
  { key: 'strongWeak', label: 'Strong / Weak H-L', shortLabel: 'Strong/Weak', color: '#00897B' },
];
const SMC_OVERLAY_KEYS = new Set(SMC_OVERLAY_OPTIONS.map((option) => option.key));
// Cap each family so a long history doesn't flood the chart — keep the most
// recent still-relevant instances (mirrors the key-levels "closest N" rule).
const SMC_MAX_PER_FAMILY = 12;
// How many bars back a *filled* FVG stays drawn as a faded "ghost" before it's
// dropped. Filled gaps are spent context, not forward targets, so we keep only
// recent ones and fade them by age. Conservative by default to keep candles clean.
const FVG_FILLED_GHOST_BARS = 40;

// Map raw market-data candles to the canonical { t, o, h, l, c, v }
// shape the indicator overlays consume. Volume is optional and kept
// genuinely null when absent (never coerced to 0) so the VWAP volume
// gate sees a no-volume bar rather than a zero one. `_virtual` gap-fill
// bars and rows with incomplete OHLC are dropped.
const mapRawCandlesToOhlcv = (rawCandles) => {
  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  };

  return (rawCandles || [])
    .filter((candle) => candle && !candle._virtual)
    .map((candle) => {
      const timestamp = (() => {
        if (typeof candle.t === 'number') {
          return candle.t;
        }
        const source = candle.date || candle.datetime || candle.timestamp;
        if (!source) {
          return null;
        }
        const time = new Date(source).getTime();
        return Number.isFinite(time) ? time : null;
      })();

      const open = toNumber(candle.open ?? candle.o ?? candle.Open ?? candle.openPrice ?? candle.open_price);
      const high = toNumber(candle.high ?? candle.h ?? candle.High ?? candle.highPrice ?? candle.high_price);
      const low = toNumber(candle.low ?? candle.l ?? candle.Low ?? candle.lowPrice ?? candle.low_price);
      const close = toNumber(candle.close ?? candle.c ?? candle.Close ?? candle.closePrice ?? candle.close_price);
      const rawVolume = candle.volume ?? candle.v ?? candle.Volume ?? candle.vol;
      const volume = rawVolume === null || rawVolume === undefined || rawVolume === ''
        ? null
        : (Number.isFinite(Number(rawVolume)) ? Number(rawVolume) : null);

      if (!Number.isFinite(timestamp) || open === null || high === null || low === null || close === null) {
        return null;
      }

      return { t: timestamp, o: open, h: high, l: low, c: close, v: volume };
    })
    .filter(Boolean);
};

const formatPriceValue = (value) => {
  if (!Number.isFinite(value)) {
    return '';
  }
  const absolute = Math.abs(value);
  if (absolute >= 1000) {
    return value.toFixed(2);
  }
  if (absolute >= 100) {
    return value.toFixed(2);
  }
  if (absolute >= 1) {
    return value.toFixed(3);
  }
  return value.toFixed(4);
};

const formatLiveCycleStatusLabel = (status) => {
  if (!status) {
    return 'provisional';
  }
  if (status === 'confirmed') {
    return 'developing';
  }
  return status;
};

const FALLBACK_DAILY_INTERVAL = 86_400_000;

// Short calendar date for tooltip lines — "28 Apr 2026" rather than
// the full ISO. Matches the chart's existing crosshair date format.
const formatTooltipDate = (timestamp) => {
  if (!Number.isFinite(timestamp)) {return null;}
  try {
    return new Date(timestamp).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return null;
  }
};

// Day and time, for a sub-daily swing's pivot and confirming bar.
const formatTooltipDateTime = (timestamp) => {
  if (!Number.isFinite(timestamp)) {return null;}
  try {
    return new Date(timestamp).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return null;
  }
};

// True when two timestamps would render as different calendar dates
// in the tooltip. Compares the formatted strings rather than UTC
// components: `formatTooltipDate` uses `toLocaleDateString` (browser-
// local timezone), so a UTC-day check could disagree with what the
// user actually sees — producing either redundant identical "Pivot:
// 29 Apr / Detected: 29 Apr" lines or wrongly-suppressed differences
// at day boundaries straddling local-vs-UTC midnight. Comparing the
// formatted output is bulletproof: the suppression decision uses the
// exact strings the user reads.
const isDifferentDay = (a, b) => {
  const formattedA = formatTooltipDate(a);
  const formattedB = formatTooltipDate(b);
  return formattedA !== null && formattedB !== null && formattedA !== formattedB;
};

const describeIndicatorEvent = (event, meta, price) => {
  const parts = [];
  const baseName = meta?.displayName
    ? `${meta.displayName}: ${event?.name || event?.id}`
    : (event?.name || event?.id);
  if (baseName) {
    const priceText = Number.isFinite(price) ? ` @ ${formatPriceValue(price)}` : '';
    parts.push(`• ${baseName}${priceText}`);
  }
  const payload = event?.payload || {};
  const fallback = event?.description;
  // For confirmed cycle events, surface BOTH the pivot date (where the
  // low/high actually printed) and the emission date (when the cron
  // run that detected it ran). Confirmation criteria frequently fire
  // retrospectively at the pivot bar itself, collapsing `confirmTime`
  // to `pivotTime`, which would imply same-bar detection — never
  // possible. `emittedAt` (the library's anchor candle at the moment
  // of the emit) is the honest "we knew this on" answer. Suppress the
  // Detected line when same calendar day to keep the tooltip concise.
  if (event?.id === 'cycle.low.confirmed' || event?.id === 'cycle.high.confirmed') {
    const pivotDate = formatTooltipDate(payload.pivotTime);
    // Live-events path (registerEvents on liveCycleLowEvents/highs) doesn't
    // carry `emittedAt` in payload — its `event.t` IS already the emission
    // time (mapped from stored eventTimestamp/sourceTimestamp). Confirmed-
    // pivots path adds `payload.emittedAt` explicitly. Prefer payload when
    // available, fall back to `event.t` so both code paths get a "Detected"
    // line without duplication or special-casing upstream.
    const emittedAt = Number.isFinite(payload.emittedAt)
      ? payload.emittedAt
      : (Number.isFinite(event?.t) ? event.t : null);
    const detectedDate = formatTooltipDate(emittedAt);
    if (pivotDate) {parts.push(`Pivot: ${pivotDate}`);}
    if (detectedDate && isDifferentDay(payload.pivotTime, emittedAt)) {
      parts.push(`Detected: ${detectedDate}`);
    }
  }
  parts.push(...swingPivotLines(event, formatTooltipDate, formatTooltipDateTime));
  const detail = (() => {
    switch (event?.id) {
      case 'ema.cross.above':
        return `Price crossed above EMA (${formatPriceValue(payload.average)})`;
      case 'ema.cross.below':
        return `Price crossed below EMA (${formatPriceValue(payload.average)})`;
      case 'ema_bullish_alignment':
        return 'EMA stack turned bullish';
      case 'ema_bearish_alignment':
        return 'EMA stack turned bearish';
      case 'ema_bullish_alignment_sustained':
        return `EMA bullish alignment held for ${payload?.streak ?? payload?.threshold ?? 'N'} candles`;
      case 'ema_bearish_alignment_sustained':
        return `EMA bearish alignment held for ${payload?.streak ?? payload?.threshold ?? 'N'} candles`;
      case 'trendline_breach':
      case 'trendline_breach_bullish':
      case 'trendline_breach_bearish':
        return 'Price wicked beyond the trendline';
      case 'trendline_break':
      case 'trendline_break_bullish':
      case 'trendline_break_bearish':
        return 'Candle close broke the trendline';
      default:
        return fallback || '';
    }
  })();
  if (detail) {
    parts.push(detail);
  }
  return parts.join('\n');
};

// Build the per-group summary lines shown in the indicator event
// tooltip. When a single candle has multiple events of the same kind
// (e.g. 20 distinct support/resistance levels firing on one bar), we
// collapse them into a single line so the tooltip stays scannable
// instead of repeating "Support Level Identified @ X" twenty times.
//
// Group key is `${displayName}::${event.name || event.id}` so distinct
// indicators (or distinct event subtypes within one indicator like
// "Support Level Identified" vs "Resistance Level Identified") still
// get their own bucket.
//
// Solo events render via the existing describeIndicatorEvent path so
// the format is unchanged. Deduped buckets render with an Nx prefix
// and a list of the first few prices, with a "+M more" suffix when
// the bucket is larger than the display limit.
const SR_DEDUPE_PRICE_LIMIT = 5;
const buildIndicatorEventSummaries = (group) => {
  const buckets = new Map();
  group.events.forEach((entry) => {
    const { event, meta } = entry;
    const displayName = meta?.displayName || '';
    const name = event?.name || event?.id || '';
    const key = `${displayName}::${name}`;
    if (!buckets.has(key)) {
      buckets.set(key, []);
    }
    buckets.get(key).push(entry);
  });
  const lines = [];
  buckets.forEach((entries) => {
    if (entries.length === 1) {
      const { event, meta, price } = entries[0];
      lines.push(describeIndicatorEvent(event, meta, Number.isFinite(price) ? price : group.price));
      return;
    }
    const first = entries[0];
    const displayName = first.meta?.displayName;
    const name = first.event?.name || first.event?.id || 'event';
    const headerName = displayName
      ? `${displayName}: ${entries.length}× ${name}`
      : `${entries.length}× ${name}`;
    const validPrices = entries
      .map((entry) => (Number.isFinite(entry.price) ? entry.price : null))
      .filter((price) => price !== null);
    let priceLine = '';
    if (validPrices.length > 0) {
      const shown = validPrices
        .slice(0, SR_DEDUPE_PRICE_LIMIT)
        .map(formatPriceValue)
        .join(', ');
      const remaining = validPrices.length - SR_DEDUPE_PRICE_LIMIT;
      priceLine = remaining > 0 ? `@ ${shown} (+${remaining} more)` : `@ ${shown}`;
    }
    lines.push(priceLine ? `• ${headerName}\n${priceLine}` : `• ${headerName}`);
  });
  return lines;
};

const DEFAULT_DSS_PARAMS = Object.freeze({
  length: 13,
  smooth1: 8,
  signal: 3,
});

const DEFAULT_SWING_LOOKBACKS = Object.freeze({
  daily: 5,
  weekly: 3,
  hourly: 5,
});

/**
 * @typedef {import('@andywilliams/indicators').WeeklyCycleLowEvent} WeeklyCycleLowEvent
 * @typedef {import('@andywilliams/indicators').WeeklyCycleHighEvent} WeeklyCycleHighEvent
 * @typedef {import('@andywilliams/indicators').LiveCycleLowState} LiveCycleLowState
 * @typedef {import('@andywilliams/indicators').LiveCycleHighState} LiveCycleHighState
 */

const DEFAULT_BOLLINGER_PARAMS = Object.freeze({
  length: 20,
  basis: 'sma',
  source: 'close',
  standardDeviation: 2,
  offset: 0,
});

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

const hexToRgb = (hex) => {
  if (!hex) {
    return { r: 0, g: 0, b: 0 };
  }
  const sanitized = hex.replace('#', '');
  const value = sanitized.length === 3
    ? sanitized.split('').map((c) => c + c).join('')
    : sanitized.padEnd(6, '0');
  const r = parseInt(value.substring(0, 2), 16);
  const g = parseInt(value.substring(2, 4), 16);
  const b = parseInt(value.substring(4, 6), 16);
  return { r, g, b };
};

const scaleColorIntensity = (hex, intensity, alpha) => {
  const { r, g, b } = hexToRgb(hex);
  const scale = clamp(intensity, 0, 1.5);
  const toChannel = (channel) => Math.round(clamp(channel * scale, 0, 255));
  const a = clamp(alpha, 0, 1);
  return `rgba(${toChannel(r)}, ${toChannel(g)}, ${toChannel(b)}, ${a})`;
};

const getCycleLengthUnit = (timeframeKey) => {
  if (timeframeKey === 'daily') {
    return { unitMs: 24 * 60 * 60 * 1000, unitLabel: 'd' };
  }
  if (timeframeKey === 'hourly') {
    return { unitMs: 60 * 60 * 1000, unitLabel: 'h' };
  }
  return { unitMs: 7 * 24 * 60 * 60 * 1000, unitLabel: 'w' };
};


const setsEqual = (left, right) => {
  if (!(left instanceof Set) || !(right instanceof Set)) {
    return left === right;
  }

  if (left.size !== right.size) {
    return false;
  }

  for (const value of left) {
    if (!right.has(value)) {
      return false;
    }
  }

  return true;
};

// The mutation that applies a saved boolean preference, when it differs from the live toggle ([] otherwise).
const booleanPreferenceMutation = ({ prefs, key, current, set }) => (
  typeof prefs?.[key] === 'boolean' && prefs[key] !== current ? [() => set(prefs[key])] : []
);

// The entries of `next` that differ from `previous`: what a preference save must send.
const changedPreferences = (previous, next) => Object.fromEntries(
  Object.entries(next).filter(([key, value]) => previous?.[key] !== value),
);

/**
 * DWLFMarketChart – drop-in replacement for the previous MarketChart component
 * built on top of the dwlf-charting library.
 *
 * For now we migrate the most critical visual features: candlesticks plus
 * support / resistance, trendlines, Fibonacci levels, alert lines, swing
 * markers, and trade annotations.
 */
const DWLFMarketChart = ({
  isMobile,
  title,
  symbol,
  marketData = [],
  timeframe = 'daily',
  timeframeOptions = [],
  selectedTimeframe,
  onTimeframeChange,
  supportResistance,
  keyLevelEvents,
  cycleFibEvents = [],
  divergenceEvents = [],
  trendlines = [],
  dailyTrendlines = [],
  hourlyTrendlines = [],
  weeklyTrendlines = [],
  chartPreferences = null,
  axisColors = null,
  crosshairSnapMode = 'series',
  showCrosshairPriceLabel = true,
  showAdaptiveGrid = true,
  onChartPreferencesChange,
  alerts = [],
  fibLevels = [],
  autoShowFib = false,
  swingPoints = [],
  weeklySwingPoints = [],
  customEvents = [],
  customEventDefinitions = [],
  customEventDefinitionsLoaded = false,
  onSelectedCustomEventIdsChange,
  tradeSignals = [],
  tradeOverlays = null,
  systemTradeSignals = [],
  userTradeSignals = [],
  tradeJournalTrades = [],
  showDailyTrendlines = true,
  showWeeklyTrendlines = true,
  showHourlyTrendlines = true,
  // DWLF-184: offers the Trendlines V3 beta layer (admin only; the page decides).
  cycleTrendlinesBeta = false,
  onToggleDailyTrendlines,
  onToggleWeeklyTrendlines,
  onToggleHourlyTrendlines,
  /** @type {WeeklyCycleLowEvent[]} */
  weeklyCycleLows = [],
  /** @type {WeeklyCycleHighEvent[]} */
  weeklyCycleHighs = [],
  // WEEKLY and DAILY: the STORED cycle window per side from GET /v2/cycles/windows
  // (DWLF-113 ①): { low: CycleWindow | null, high: CycleWindow | null,
  // verification }. The chart draws exactly opens → closes (and the hard-max
  // box) from these dates. Null side ⇒ no band. No fallback computation.
  cycleWindows = null,
  weinsteinStage = null,
  // HOURLY: the live state machine's quantile band (q25..q75 in bars
  // + lastConfirmedPivotTime), as before ②a; mean ± 25 % of visible pivots
  // when the row is missing or thin.
  cycleWindowState = null,
  onShowStagesChange,
  onShowFibChange,
  pastCycleWindows = null,
  onShowPastWindowsChange,
  /** @type {LiveCycleLowState | null} */
  liveCycleLow = null,
  /** @type {LiveCycleHighState | null} */
  liveCycleHigh = null,
  liveCycleLowEvents = [],
  liveCycleHighEvents = [],
  // Called when the view is panned back to the oldest candle held, to load older ones (DWLF-426).
  onReachOldestCandles,
  // Annotation sidebar integration
  onAnnotationsChange,
  onSelectedAnnotationChange,
  annotationRef,
}) => {
  const dispatch = useDispatch();
  const effectiveSelectedTimeframe = selectedTimeframe || timeframe;
  const watchlist = useSelector((state) => state.markets.watchlist || []);
  const indicatorSettings = useSelector((state) => state.markets.indicatorSettings);
  const indicatorSettingsStatus = useSelector(
    (state) => state.markets.indicatorSettingsStatus || 'idle'
  );
  const isInWatchlist = watchlist.includes(symbol);
  const chartRef = useRef(null);
  const isDark = useIsDark();
  const scheme = useSchemeColors();
  // The chart is SVG with fill attributes, so it takes resolved Signal colours (not CSS variables).
  const chartPalette = useMemo(() => {
    const surface = { background: scheme.bg, grid: scheme.ink(0.06), text: scheme.ink(0.7), tooltipBackground: scheme.elevated };
    return { dark: surface, light: surface };
  }, [scheme]);
  const chartContainerRef = useRef(null); // DOM ref for chart container
  // Set to true when a two-point placement commits on mouseup, so the
  // synthesized click that follows (if it fires) is skipped to avoid
  // double-creating the annotation.
  const justPlacedOnMouseUpRef = useRef(false);
  // Last canvas info reported by the chart's onCanvasMouseMove. Used by the
  // window-level mouseup listener during placement, so we can commit even if
  // the release lands outside any pane.
  const lastCanvasInfoRef = useRef(null);

  // Chart Annotations - Phase 1 (Issue #14 fix: direct destructuring)
  const {
    annotations,
    allAnnotations,
    activeTool: annotationTool,
    selectedAnnotationId,
    pendingTextAnnotation,
    pendingFirstPoint,
    editPopoverAnnotation,
    setActiveTool: setAnnotationTool,
    updateAnnotation,
    deleteAnnotation,
    rearmAlertLine,
    retiredAlertIds,
    clearAnnotations,
    toggleAnnotationVisibility,
    handleChartClick: handleAnnotationChartClick,
    handleAnnotationMouseMove,
    handleBrushMouseMove,
    handleBrushMouseUp,
    handleChartHover: handleAnnotationChartHover,
    selectAnnotation,
    openAnnotationPopover,
    deselectAnnotation,
    completePendingTextAnnotation,
    cancelPendingTextAnnotation,
    isPlacing: isPlacingAnnotation,
    isPlacingTwoPoint,
    isBrushDrawing,
  } = useChartAnnotations(symbol, effectiveSelectedTimeframe, { availableTimeframes: timeframeOptions });

  // Track edit popover position
  const [annotationPopoverPosition, setAnnotationPopoverPosition] = useState({ x: 200, y: 200 });

  // Helper function to open annotation popover at the correct position
  const openAnnotationPopoverAtPosition = useCallback((id, info) => {
    // Set popover position before opening
    if (info?.screenX !== undefined && info?.screenY !== undefined) {
      setAnnotationPopoverPosition({ x: info.screenX, y: info.screenY });
    } else if (chartRef.current) {
      // Fallback: position popover near chart center if no coords provided
      const rect = chartRef.current.getBoundingClientRect?.() || 
                   chartRef.current.container?.getBoundingClientRect?.();
      if (rect) {
        setAnnotationPopoverPosition({ 
          x: rect.left + rect.width / 2, 
          y: rect.top + 100 
        });
      }
    }
    openAnnotationPopover(id);
  }, [openAnnotationPopover]);

  const [textInputPosition, setTextInputPosition] = useState({ x: 200, y: 200 });

  // Notify parent when annotations change
  useEffect(() => {
    if (onAnnotationsChange) {
      onAnnotationsChange(allAnnotations);
    }
  }, [allAnnotations, onAnnotationsChange]);

  // Notify parent when selected annotation changes
  useEffect(() => {
    if (onSelectedAnnotationChange) {
      onSelectedAnnotationChange(selectedAnnotationId);
    }
  }, [selectedAnnotationId, onSelectedAnnotationChange]);

  // Expose methods to parent via ref
  useImperativeHandle(annotationRef, () => ({
    /**
     * Scroll/pan the chart to center on the given annotation
     */
    scrollToAnnotation: (annotation) => {
      if (!chartRef.current || !annotation) {
        return;
      }
      
      // Get the timestamp to center on
      let targetTime = null;
      
      // Different annotation types have different coordinate fields
      if (annotation.time !== undefined) {
        targetTime = annotation.time;
      } else if (annotation.time1 !== undefined) {
        // Two-point annotations: use midpoint
        targetTime = (annotation.time1 + (annotation.time2 || annotation.time1)) / 2;
      } else if (annotation.points && annotation.points.length > 0) {
        // Brush annotations: use center of points
        const times = annotation.points.map(p => p.t);
        targetTime = times.reduce((a, b) => a + b, 0) / times.length;
      } else if (annotation.anchorTime !== undefined) {
        // Anchored VWAP: anchored on a single candle timestamp.
        targetTime = annotation.anchorTime;
      }
      
      if (targetTime !== null && chartRef.current.scrollToTimestamp) {
        chartRef.current.scrollToTimestamp(targetTime);
      }
    },
    
    /**
     * Select an annotation by ID or annotation object
     */
    selectAnnotation: (annotationOrId) => {
      const id = typeof annotationOrId === 'string' ? annotationOrId : annotationOrId?.id;
      if (id) {
        selectAnnotation(id);
      }
    },
    
    /**
     * Open annotation popover by ID or annotation object
     */
    openAnnotationPopover: (annotationOrId) => {
      const id = typeof annotationOrId === 'string' ? annotationOrId : annotationOrId?.id;
      if (id) {
        // List-initiated open: no click coords, so anchor the popover at a
        // predictable on-screen spot rather than wherever the last chart
        // click left it (which could be off-screen).
        setAnnotationPopoverPosition({ x: 220, y: 140 });
        openAnnotationPopover(id);
      }
    },
    
    /**
     * Toggle visibility of an annotation
     */
    toggleAnnotationVisibility: (annotationOrId) => {
      const id = typeof annotationOrId === 'string' ? annotationOrId : annotationOrId?.id;
      if (id) {
        toggleAnnotationVisibility(id);
      }
    },
  }), [selectAnnotation, openAnnotationPopover, toggleAnnotationVisibility]);

  const chartAppearanceKey = useMemo(() => {
    const paletteKey = axisColors && typeof axisColors === 'object'
      ? `${axisColors.dark || ''}|${axisColors.light || ''}`
      : 'default';
    return [
      paletteKey,
      crosshairSnapMode || 'series',
      showCrosshairPriceLabel ? 'label:on' : 'label:off',
      showAdaptiveGrid ? 'grid:on' : 'grid:off',
    ].join('::');
  }, [axisColors?.dark, axisColors?.light, crosshairSnapMode, showCrosshairPriceLabel, showAdaptiveGrid]);

  const hasFibLevels = Array.isArray(fibLevels) && fibLevels.length > 0;

  // Expose hovered candle info (for now just internal state)
  const [showSwings, setShowSwings] = useState(false);
  const [showSystemTrades, setShowSystemTrades] = useState(false);
  // Per-strategy selections for user trades. Mirrors customEventSelections:
  // there is no master "show user trades" toggle — visibility is derived
  // from whether any strategies are selected. The user picks which of
  // their custom strategies to show via the My Data dropdown.
  const [userStrategySelections, setUserStrategySelections] = useState(new Set());
  const [showTradeJournal, setShowTradeJournal] = useState(false);
  const legacyTradesAvailable = Array.isArray(tradeSignals) && tradeSignals.length > 0;
  const [showFib, setShowFib] = useState(autoShowFib && hasFibLevels);
  const didAutoShowFib = useRef(false);
  const preferredShowFib = useMemo(
    () => (typeof chartPreferences?.showFib === 'boolean' ? chartPreferences.showFib : undefined),
    [chartPreferences]
  );
  // Toggle Support / Resistance visibility
  const [showSR, setShowSR] = useState(false);
  const [localShowDailyTrendlines, setLocalShowDailyTrendlines] = useState(showDailyTrendlines);
  const [localShowWeeklyTrendlines, setLocalShowWeeklyTrendlines] = useState(showWeeklyTrendlines);
  const [showCycleTrendlines, setShowCycleTrendlines] = useState(false);
  const [localShowHourlyTrendlines, setLocalShowHourlyTrendlines] = useState(showHourlyTrendlines);
  const [showDSS, setShowDSS] = useState(false);
  const [showBollinger, setShowBollinger] = useState(false);
  // DWLF-446: ATR % of close in its own pane, with the jobs' atr.regime.* events.
  const [showATR, setShowATR] = useState(false);
  const [showRSI, setShowRSI] = useState(false);
  const [showADX, setShowADX] = useState(false);
  // Set of active VWAP anchor periods ('month' | 'quarter' | 'year').
  const [vwapAnchors, setVwapAnchors] = useState(() => new Set());
  // Active SMC overlay families ('fvg' | 'orderBlocks' | 'structure').
  const [smcOverlays, setSmcOverlays] = useState(() => new Set());
  // DWLF-447 (Andy): event markers are opt-in PER INDICATOR. Each menu row (and the VWAP / SMC /
  // moving-average sections) has its own events switch, all off by default; `eventLayers` holds the
  // keys switched on and is saved. A family's markers draw only while its indicator AND its events
  // switch are on.
  const [eventLayers, setEventLayers] = useState(() => new Set());
  const eventsOn = useCallback((key) => eventLayers.has(key), [eventLayers]);
  const toggleEventLayer = useCallback((key) => setEventLayers((prev) => {
    const next = new Set(prev);
    if (next.has(key)) { next.delete(key); } else { next.add(key); }
    return next;
  }), []);
  const showIndicatorEvents = eventLayers.size > 0;
  const [customEventSelections, setCustomEventSelections] = useState(new Set());
  const [showEMACloud, setShowEMACloud] = useState(false);
  const [activeIndicatorEventGroup, setActiveIndicatorEventGroup] = useState(null);
  const [activeCustomEventGroup, setActiveCustomEventGroup] = useState(null);
  const [activeUserTradeGroup, setActiveUserTradeGroup] = useState(null);
  // The candle the user is currently hovering (set by the crosshair
  // callback). Used to render the OHLC info panel in the top-left
  // corner of the chart.
  const [hoveredCandle, setHoveredCandle] = useState(null);
  // Ref to priceCandles — initialised to null here and updated after
  // the priceCandles useMemo runs (see below). The crosshair callback
  // reads from the ref so it can look up candles without adding
  // priceCandles to its dependency array.
  const priceCandlesRef = useRef(null);
  // Cursor position relative to chartContainerRef, used to anchor the
  // custom-event hover tooltip next to the user's pointer.
  const [cursorPos, setCursorPos] = useState(null);
  const [showWeeklyCycleLows, setShowWeeklyCycleLows] = useState(true);
  const [showWeeklyCycleHighs, setShowWeeklyCycleHighs] = useState(true);
  // Weinstein weekly stage band (DWLF-382). Off by default; saved in chart preferences (DWLF-447).
  // Reported up so the page fetches the stage only while the band is shown.
  const [showStages, setShowStages] = useState(false);
  useEffect(() => {
    onShowStagesChange?.(showStages);
    return () => onShowStagesChange?.(false);
  }, [showStages, onShowStagesChange]);
  // Reported up so the page fetches the stored cycle-fib events only while Fib Levels is on.
  useEffect(() => {
    onShowFibChange?.(showFib);
    return () => onShowFibChange?.(false);
  }, [showFib, onShowFibChange]);
  // Past cycle windows (DWLF-444): every closed window with where its pivot actually formed. Off by default,
  // saved in the chart preferences, and reported up so the page fetches the history only while shown.
  // Like the stage band it is an opt-in layer, deliberately NOT part of the Cycle lens.
  // One toggle per side, so the lows can be studied without the highs, saved per symbol (Andy, 15-Oct).
  const [showPastLowWindows, setShowPastLowWindows] = useState(false);
  const [showPastHighWindows, setShowPastHighWindows] = useState(false);
  useEffect(() => {
    onShowPastWindowsChange?.({ low: showPastLowWindows, high: showPastHighWindows });
    return () => onShowPastWindowsChange?.({ low: false, high: false });
  }, [showPastLowWindows, showPastHighWindows, onShowPastWindowsChange]);
  const [showLiveWeeklyCycleLows, setShowLiveWeeklyCycleLows] = useState(true);
  const [showLiveWeeklyCycleHighs, setShowLiveWeeklyCycleHighs] = useState(true);
  // Projected next-cycle window bands (DWLF-440) — toggled independently of the pivot markers.
  const [showCycleLowWindow, setShowCycleLowWindow] = useState(true);
  const [showCycleHighWindow, setShowCycleHighWindow] = useState(true);
  // Toolbar layer dropdowns. We split what was a single "Overlays"
  // dropdown into three focused buckets — Indicators / Cycles / My
  // Data — each with its own open/close state. Custom events and
  // moving averages are nested sub-sections inside their respective
  // top-level dropdowns.
  const [showIndicatorsDropdown, setShowIndicatorsDropdown] = useState(false);
  const [showCyclesDropdown, setShowCyclesDropdown] = useState(false);
  const [showMyDataDropdown, setShowMyDataDropdown] = useState(false);
  const [activeSMAs, setActiveSMAs] = useState(new Set());
  const [activeEMAs, setActiveEMAs] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState(() => ({
    movingAverages: false,
    vwap: false,
    smc: false,
    customEvents: false,
    userStrategies: false,
  }));
  const syncingPreferencesRef = useRef(false);
  const chartPreferencesSnapshotRef = useRef(null);
  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    if (indicatorSettingsStatus === 'idle') {
      dispatch(fetchIndicatorSettings());
    }
  }, [indicatorSettingsStatus, dispatch]);

  // Handle brush drawing completion on document mouseup
  useEffect(() => {
    if (!isBrushDrawing) {
      return;
    }
    document.addEventListener('mouseup', handleBrushMouseUp);
    return () => document.removeEventListener('mouseup', handleBrushMouseUp);
  }, [isBrushDrawing, handleBrushMouseUp]);

  // Commit two-point annotation placements on window mouseup. The chart's
  // onChartCanvasClick is unreliable for the second click (browser sometimes
  // suppresses click events when the cursor jitters during the press, or the
  // release lands between panes / outside the chart). Mouseup always fires.
  useEffect(() => {
    if (!pendingFirstPoint || !annotationTool) {
      return undefined;
    }
    // Reset on each fresh placement so a stale flag from a prior gesture
    // can't suppress the current commit.
    justPlacedOnMouseUpRef.current = false;
    // Clear any canvas info left over from a prior interaction. onCanvas-
    // MouseMove is only wired while isPlacingTwoPoint is true, so without
    // this reset, a fast second click without intervening mouse movement
    // could read coordinates from before the current placement started.
    lastCanvasInfoRef.current = null;
    const handler = (event) => {
      // Only treat left-click releases as placement commits — right/middle
      // clicks must not drop an annotation (and the resulting dedup flag
      // would then silently swallow the user's next intentional left-click).
      if (event.button !== 0) return;
      // Only commit if the release happened inside the chart area, so a
      // mouseup on a toolbar button or somewhere else on the page can't
      // accidentally drop the annotation.
      const containerEl = chartContainerRef.current;
      if (containerEl && event.target instanceof Node && !containerEl.contains(event.target)) {
        return;
      }
      const info = lastCanvasInfoRef.current;
      if (!info || !Number.isFinite(info.time) || !Number.isFinite(info.price)) {
        return;
      }
      justPlacedOnMouseUpRef.current = true;
      // Belt-and-braces: clear the dedup flag on the next macrotask so it
      // can't leak past the synthesized click. The chart's onChartCanvasClick
      // also clears it (when a click does fire), but the browser sometimes
      // suppresses the click after a gesture with movement, leaving the flag
      // stuck and silently swallowing subsequent canvas clicks. setTimeout(0)
      // fires *after* the click would have, so the dedup still works.
      setTimeout(() => {
        justPlacedOnMouseUpRef.current = false;
      }, 0);
      const result = handleAnnotationChartClick({
        time: info.time,
        price: info.price,
        screenX: info.screenX,
        screenY: info.screenY,
      });
      if (result && !result.pending && info.screenX !== undefined && info.screenY !== undefined) {
        setAnnotationPopoverPosition({ x: info.screenX, y: info.screenY });
      }
    };
    window.addEventListener('mouseup', handler);
    return () => window.removeEventListener('mouseup', handler);
  }, [pendingFirstPoint, annotationTool, handleAnnotationChartClick]);

  // Shift-hold quick measure shortcut (like TradingView)
  const quickMeasureActiveRef = useRef(false);
  // "Shift is still holding the quick measure open" — NOT "Shift is physically
  // down". The chord branch below clears it while the key is still held, on
  // purpose: once Shift turns out to be the start of a chord it has stopped
  // holding anything open, and leaving it true would wedge the tool armed for
  // the rest of the chord.
  const shiftHoldsMeasureRef = useRef(false);

  // The Shift listeners live on `document` and would otherwise act on whatever
  // these values were when they were registered. Re-registering happens in a
  // passive effect, which a fast Shift tap can outrun — so mirror them on every
  // render and have the disarm read the refs rather than the closure.
  const annotationToolRef = useRef(annotationTool);
  annotationToolRef.current = annotationTool;
  const pendingFirstPointLatestRef = useRef(pendingFirstPoint);
  pendingFirstPointLatestRef.current = pendingFirstPoint;

  // Put the quick-measure tool away once nothing is holding it open. Leaving it
  // armed is worse than it looks: an armed tool also makes every existing
  // annotation click-through, so a stuck tool means nothing on the chart can be
  // selected or moved.
  const disarmQuickMeasure = useCallback(() => {
    if (!quickMeasureActiveRef.current) {
      return;
    }
    // Still measuring by hand, or mid-placement — not ours to interrupt.
    if (shiftHoldsMeasureRef.current || pendingFirstPointLatestRef.current) {
      return;
    }
    // The user picked a different tool while Shift was down. That choice is
    // theirs to keep; just forget that we ever armed one.
    if (annotationToolRef.current !== 'measure') {
      quickMeasureActiveRef.current = false;
      return;
    }
    setAnnotationTool(null);
    annotationToolRef.current = null;
    quickMeasureActiveRef.current = false;
  }, [setAnnotationTool]);

  // Level-triggered, not edge-triggered. The Shift keyup that lands mid-measure
  // is deliberately ignored — but it is the only keyup that press will deliver,
  // so the end of the placement has to re-check too. Escaping a measurement
  // clears the pending anchor and leaves the tool selected, so without this it
  // would stay armed. (Completing one does clear it — createMeasureAnnotation
  // calls setActiveTool(null) — where this only tidies the armed flag.)
  useEffect(() => {
    disarmQuickMeasure();
  }, [annotationTool, pendingFirstPoint, disarmQuickMeasure]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== 'Shift') {
        // Another key while quick measure is armed means that Shift was the
        // start of a chord — Shift+Tab, a capital letter — and not a request
        // to measure. Stand down, so the annotation layer isn't left
        // click-through for the rest of the chord. A measurement already
        // under way is still protected by the guard inside the disarm.
        if (quickMeasureActiveRef.current) {
          shiftHoldsMeasureRef.current = false;
          disarmQuickMeasure();
        }
        return;
      }
      if (e.repeat) {
        return;
      }
      // Never steal Shift from typing — it is just a capital letter to a text
      // field (annotation text, the edit popover, any search box).
      if (isEditableTarget(e.target)) {
        return;
      }
      // Shift as part of a chord (Cmd+Shift+Tab and friends) is never a
      // request to measure.
      if (e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }
      shiftHoldsMeasureRef.current = true;
      // An already-active tool wins, so Shift can't hijack a placement
      // half-way through. A *selected* annotation no longer blocks arming:
      // that was why Shift did nothing when the cursor sat over an existing
      // box. The selection is deliberately left alone — dropping it here
      // would close the edit popover on any stray Shift.
      if (!annotationToolRef.current) {
        setAnnotationTool('measure');
        // Mark it armed for the listeners now, not a render later, so an
        // immediate keyup disarms the tool instead of only the flag.
        annotationToolRef.current = 'measure';
        quickMeasureActiveRef.current = true;
      }
    };
    const handleKeyUp = (e) => {
      if (e.key === 'Shift') {
        shiftHoldsMeasureRef.current = false;
        disarmQuickMeasure();
      }
    };
    const handleWindowBlur = () => {
      shiftHoldsMeasureRef.current = false;
      disarmQuickMeasure();
    };
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    // A keyup delivered to another window never reaches this document, so
    // without this the tool stays armed across an app-switch.
    window.addEventListener('blur', handleWindowBlur);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [setAnnotationTool, disarmQuickMeasure]);

  // The page's bucketing rule, shared (`bandTimeframe`), so the chart and the
  // page cannot disagree on which band a timeframe gets.
  const normalizedTimeframeKey = useMemo(
    () => bandTimeframe(effectiveSelectedTimeframe || timeframe || ''),
    [effectiveSelectedTimeframe, timeframe],
  );

  const resolvedDssParams = useMemo(() => {
    const base = indicatorSettings?.dss;
    const toNumber = (field) => {
      const fallback = DEFAULT_DSS_PARAMS[field];
      const raw = base?.[field];
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        return raw;
      }
      if (typeof raw === 'string') {
        const parsed = Number(raw.trim());
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    return {
      length: toNumber('length'),
      smooth1: toNumber('smooth1'),
      signal: toNumber('signal'),
    };
  }, [indicatorSettings]);

  const resolvedBollingerParams = useMemo(() => {
    const base = indicatorSettings?.bollinger;

    const normalizeBasis = (value) => {
      if (typeof value !== 'string') {
        return DEFAULT_BOLLINGER_PARAMS.basis;
      }
      const lower = value.trim().toLowerCase();
      return lower === 'ema' ? 'ema' : 'sma';
    };

    const normalizeSource = (value) => {
      if (typeof value !== 'string') {
        return DEFAULT_BOLLINGER_PARAMS.source;
      }
      const lower = value.trim().toLowerCase();
      return ['open', 'high', 'low', 'close'].includes(lower)
        ? lower
        : DEFAULT_BOLLINGER_PARAMS.source;
    };

    const toNumber = (raw, fallback, { allowFloat = true } = {}) => {
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        if (!allowFloat) {
          const rounded = Math.round(raw);
          return Number.isFinite(rounded) ? rounded : fallback;
        }
        return raw;
      }
      if (typeof raw === 'string') {
        const parsed = Number(raw.trim());
        if (Number.isFinite(parsed)) {
          if (!allowFloat) {
            const rounded = Math.round(parsed);
            return Number.isFinite(rounded) ? rounded : fallback;
          }
          return parsed;
        }
      }
      const parsed = Number(raw);
      if (Number.isFinite(parsed)) {
        if (!allowFloat) {
          const rounded = Math.round(parsed);
          return Number.isFinite(rounded) ? rounded : fallback;
        }
        return parsed;
      }
      return fallback;
    };

    return {
      length: Math.max(1, toNumber(base?.length, DEFAULT_BOLLINGER_PARAMS.length, { allowFloat: false })),
      basis: normalizeBasis(base?.basis),
      source: normalizeSource(base?.source),
      standardDeviation: Math.max(0, toNumber(base?.standardDeviation, DEFAULT_BOLLINGER_PARAMS.standardDeviation)),
      offset: toNumber(base?.offset, DEFAULT_BOLLINGER_PARAMS.offset, { allowFloat: false }),
    };
  }, [indicatorSettings]);

  const resolvedSwingLookbacks = useMemo(() => {
    const base = indicatorSettings?.swing;

    const toPositiveInteger = (field) => {
      const fallback = DEFAULT_SWING_LOOKBACKS[field];
      const raw = base?.[field];
      const parsed = Number(raw);
      if (Number.isFinite(parsed) && parsed > 0) {
        return Math.round(parsed);
      }
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        const numeric = Number(trimmed);
        if (Number.isFinite(numeric) && numeric > 0) {
          return Math.round(numeric);
        }
      }
      return fallback;
    };

    return {
      daily: toPositiveInteger('daily'),
      weekly: toPositiveInteger('weekly'),
      hourly: toPositiveInteger('hourly'),
    };
  }, [indicatorSettings]);

  const resolvedSwingSweepBars = useMemo(() => {
    const raw = indicatorSettings?.swing?.sweepBars;
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
      return Math.round(raw);
    }
    if (typeof raw === 'string') {
      const parsed = Number(raw.trim());
      if (Number.isFinite(parsed) && parsed > 0) {
        return Math.round(parsed);
      }
    }
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) {
      return Math.round(parsed);
    }
    return 1;
  }, [indicatorSettings]);

  const customEventDefinitionList = useMemo(() => {
    if (!Array.isArray(customEventDefinitions)) {
      return [];
    }
    return customEventDefinitions
      .map((event) => {
        const id =
          event?.eventId
          ?? event?.id
          ?? event?.customEventId;
        if (!id) {
          return null;
        }
        return {
          id,
          name: event?.name || event?.eventName || String(id),
        };
      })
      .filter(Boolean);
  }, [customEventDefinitions]);
  const customEventIdSet = useMemo(
    () => new Set(customEventDefinitionList.map((event) => event.id)),
    [customEventDefinitionList],
  );
  const customEventNameMap = useMemo(() => {
    const map = new Map();
    customEventDefinitionList.forEach((event) => {
      if (event?.id && event?.name) {
        map.set(event.id, event.name);
      }
    });
    return map;
  }, [customEventDefinitionList]);
  const orderedCustomEventSelections = useMemo(() => {
    if (!customEventSelections.size) {
      return [];
    }
    if (!customEventDefinitionList.length) {
      return Array.from(customEventSelections);
    }
    const definedIds = customEventDefinitionList.map((event) => event.id);
    const definedSet = new Set(definedIds);
    const selectedDefined = definedIds.filter((id) =>
      customEventSelections.has(id),
    );
    const extras = Array.from(customEventSelections).filter(
      (id) => !definedSet.has(id),
    );
    return [...selectedDefined, ...extras];
  }, [customEventSelections, customEventDefinitionList]);
  const showCustomEvents = customEventSelections.size > 0;

  // Build the list of unique custom strategies represented in
  // userTradeSignals. Each entry { id, name, count } feeds the My
  // Data dropdown's "Custom Strategies" sub-section. We extract from
  // the trades themselves so we don't need to plumb through a separate
  // strategies prop — strategies with zero signals on the current
  // symbol simply don't appear, mirroring the custom events pattern.
  const userStrategyDefinitionList = useMemo(() => {
    if (!Array.isArray(userTradeSignals) || userTradeSignals.length === 0) {
      return [];
    }
    const map = new Map();
    userTradeSignals.forEach((trade) => {
      const id =
        trade?.strategy
        ?? trade?.strategyId
        ?? trade?.strategy_type
        ?? trade?.strategyName
        ?? trade?.strategyDescription;
      if (!id) {
        return;
      }
      const name =
        trade?.strategyName
        || trade?.strategyDescription
        || trade?.strategy
        || String(id);
      const existing = map.get(id);
      if (existing) {
        existing.count += 1;
      } else {
        map.set(id, { id, name, count: 1 });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [userTradeSignals]);
  const userStrategyIdSet = useMemo(
    () => new Set(userStrategyDefinitionList.map((strategy) => strategy.id)),
    [userStrategyDefinitionList],
  );
  const orderedUserStrategySelections = useMemo(() => {
    if (!userStrategySelections.size) {
      return [];
    }
    if (!userStrategyDefinitionList.length) {
      return Array.from(userStrategySelections);
    }
    const definedIds = userStrategyDefinitionList.map((strategy) => strategy.id);
    const definedSet = new Set(definedIds);
    const selectedDefined = definedIds.filter((id) =>
      userStrategySelections.has(id),
    );
    const extras = Array.from(userStrategySelections).filter(
      (id) => !definedSet.has(id),
    );
    return [...selectedDefined, ...extras];
  }, [userStrategySelections, userStrategyDefinitionList]);
  const showUserTrades = userStrategySelections.size > 0;

  useEffect(() => {
    setLocalShowDailyTrendlines(showDailyTrendlines);
  }, [showDailyTrendlines]);

  useEffect(() => {
    setLocalShowWeeklyTrendlines(showWeeklyTrendlines);
  }, [showWeeklyTrendlines]);

  useEffect(() => {
    setLocalShowHourlyTrendlines(showHourlyTrendlines);
  }, [showHourlyTrendlines]);

  const lastChartPreferencesRef = useRef(null);

  useEffect(() => {
    if (!chartPreferences || typeof chartPreferences !== 'object') {
      lastChartPreferencesRef.current = chartPreferences || null;
      return;
    }

    if (chartPreferences === lastChartPreferencesRef.current) {
      return;
    }

    lastChartPreferencesRef.current = chartPreferences;

    const mutations = [];

    if (typeof chartPreferences.showSwings === 'boolean' && chartPreferences.showSwings !== showSwings) {
      mutations.push(() => setShowSwings(chartPreferences.showSwings));
    }

    if (typeof chartPreferences.showSystemTrades === 'boolean' && chartPreferences.showSystemTrades !== showSystemTrades) {
      mutations.push(() => setShowSystemTrades(chartPreferences.showSystemTrades));
    }

    if (typeof chartPreferences.showTradeJournal === 'boolean' && chartPreferences.showTradeJournal !== showTradeJournal) {
      mutations.push(() => setShowTradeJournal(chartPreferences.showTradeJournal));
    }

    if (
      typeof chartPreferences.showTrades === 'boolean'
      && typeof chartPreferences.showSystemTrades !== 'boolean'
    ) {
      const legacyValue = chartPreferences.showTrades;
      if (legacyValue !== showSystemTrades) {
        mutations.push(() => setShowSystemTrades(legacyValue));
      }
    }

    if (typeof chartPreferences.showFib === 'boolean' && chartPreferences.showFib !== showFib) {
      mutations.push(() => {
        didAutoShowFib.current = true;
        setShowFib(chartPreferences.showFib);
      });
    }

    if (typeof chartPreferences.showSR === 'boolean' && chartPreferences.showSR !== showSR) {
      mutations.push(() => setShowSR(chartPreferences.showSR));
    }

    if (typeof chartPreferences.showDSS === 'boolean' && chartPreferences.showDSS !== showDSS) {
      mutations.push(() => setShowDSS(chartPreferences.showDSS));
    }

    if (typeof chartPreferences.showATR === 'boolean' && chartPreferences.showATR !== showATR) {
      mutations.push(() => setShowATR(chartPreferences.showATR));
    }

    if (typeof chartPreferences.showRSI === 'boolean' && chartPreferences.showRSI !== showRSI) {
      mutations.push(() => setShowRSI(chartPreferences.showRSI));
    }
    if (typeof chartPreferences.showADX === 'boolean' && chartPreferences.showADX !== showADX) {
      mutations.push(() => setShowADX(chartPreferences.showADX));
    }

    if (typeof chartPreferences.showStages === 'boolean' && chartPreferences.showStages !== showStages) {
      mutations.push(() => setShowStages(chartPreferences.showStages));
    }

    if (typeof chartPreferences.showBollinger === 'boolean' && chartPreferences.showBollinger !== showBollinger) {
      mutations.push(() => setShowBollinger(chartPreferences.showBollinger));
    }

    if (Array.isArray(chartPreferences.vwapAnchors)) {
      // An explicit array (including []) is authoritative — an empty
      // array clears anchors carried over from a previous symbol.
      const nextAnchors = new Set(
        chartPreferences.vwapAnchors.filter((anchor) => VWAP_ANCHOR_KEYS.has(anchor)),
      );
      if (!setsEqual(vwapAnchors, nextAnchors)) {
        mutations.push(() => setVwapAnchors(nextAnchors));
      }
    } else if (chartPreferences.showVWAP === true) {
      // Back-compat: the pre-anchor-picker overlay persisted a boolean
      // `showVWAP`. Migrate a legacy "on" to the monthly anchor.
      if (vwapAnchors.size === 0) {
        mutations.push(() => setVwapAnchors(new Set(['month'])));
      }
    } else if (chartPreferences.showVWAP === false && vwapAnchors.size > 0) {
      // Back-compat: a legacy explicit `showVWAP: false` must still
      // clear anchors carried over from a previously-viewed symbol.
      mutations.push(() => setVwapAnchors(new Set()));
    }

    if (Array.isArray(chartPreferences.smcOverlays)) {
      const nextSmc = new Set(
        chartPreferences.smcOverlays.filter((key) => SMC_OVERLAY_KEYS.has(key)),
      );
      if (!setsEqual(smcOverlays, nextSmc)) {
        mutations.push(() => setSmcOverlays(nextSmc));
      }
    }

    if (Array.isArray(chartPreferences.eventLayers)) {
      const nextLayers = new Set(chartPreferences.eventLayers.filter((key) => typeof key === 'string'));
      if (!setsEqual(eventLayers, nextLayers)) {
        mutations.push(() => setEventLayers(nextLayers));
      }
    }

    if (typeof chartPreferences.showEMACloud === 'boolean' && chartPreferences.showEMACloud !== showEMACloud) {
      mutations.push(() => setShowEMACloud(chartPreferences.showEMACloud));
    }

    if (
      typeof chartPreferences.showWeeklyCycleLows === 'boolean'
      && chartPreferences.showWeeklyCycleLows !== showWeeklyCycleLows
    ) {
      mutations.push(() => setShowWeeklyCycleLows(chartPreferences.showWeeklyCycleLows));
    }

    if (
      typeof chartPreferences.showWeeklyCycleHighs === 'boolean'
      && chartPreferences.showWeeklyCycleHighs !== showWeeklyCycleHighs
    ) {
      mutations.push(() => setShowWeeklyCycleHighs(chartPreferences.showWeeklyCycleHighs));
    }

    if (
      typeof chartPreferences.showLiveWeeklyCycleLows === 'boolean'
      && chartPreferences.showLiveWeeklyCycleLows !== showLiveWeeklyCycleLows
    ) {
      mutations.push(() => setShowLiveWeeklyCycleLows(chartPreferences.showLiveWeeklyCycleLows));
    }

    if (
      typeof chartPreferences.showLiveWeeklyCycleHighs === 'boolean'
      && chartPreferences.showLiveWeeklyCycleHighs !== showLiveWeeklyCycleHighs
    ) {
      mutations.push(() => setShowLiveWeeklyCycleHighs(chartPreferences.showLiveWeeklyCycleHighs));
    }

    if (
      typeof chartPreferences.showCycleLowWindow === 'boolean'
      && chartPreferences.showCycleLowWindow !== showCycleLowWindow
    ) {
      mutations.push(() => setShowCycleLowWindow(chartPreferences.showCycleLowWindow));
    }

    if (
      typeof chartPreferences.showCycleHighWindow === 'boolean'
      && chartPreferences.showCycleHighWindow !== showCycleHighWindow
    ) {
      mutations.push(() => setShowCycleHighWindow(chartPreferences.showCycleHighWindow));
    }

    // Past windows per side (DWLF-444): saved per symbol like every other layer, so a chart reopens as it was left.
    mutations.push(
      ...booleanPreferenceMutation({ prefs: chartPreferences, key: 'showPastLowWindows', current: showPastLowWindows, set: setShowPastLowWindows }),
      ...booleanPreferenceMutation({ prefs: chartPreferences, key: 'showPastHighWindows', current: showPastHighWindows, set: setShowPastHighWindows }),
      // DWLF-184: the V3 beta layer, saved like any other so a soak survives a reload.
      ...booleanPreferenceMutation({ prefs: chartPreferences, key: 'showCycleTrendlines', current: showCycleTrendlines, set: setShowCycleTrendlines }),
    );

    if (Array.isArray(chartPreferences.activeSMAs)) {
      const normalizedSMAs = normalizeNumericArray(chartPreferences.activeSMAs);
      const nextSMAs = new Set(normalizedSMAs);
      if (!setsEqual(activeSMAs, nextSMAs)) {
        mutations.push(() => setActiveSMAs(nextSMAs));
      }
    }

    if (Array.isArray(chartPreferences.activeEMAs)) {
      const normalizedEMAs = normalizeNumericArray(chartPreferences.activeEMAs);
      const nextEMAs = new Set(normalizedEMAs);
      if (!setsEqual(activeEMAs, nextEMAs)) {
        mutations.push(() => setActiveEMAs(nextEMAs));
      }
    }

    if (Array.isArray(chartPreferences.customEventSelections)) {
      const normalizedSelections = normalizeStringArray(
        chartPreferences.customEventSelections,
      );
      // Drop selections whose definition no longer exists (e.g. a deleted
      // custom event) once the definition list is authoritative — otherwise a
      // stale id keeps the "Custom Events (N)" count + My Data badge lit and
      // re-fetches the deleted event's orphaned fires. Guarded so the async
      // definitions-load gap can't wipe valid saved selections.
      const filteredSelections = reconcileCustomEventSelections(
        normalizedSelections,
        customEventIdSet,
        customEventDefinitionsLoaded,
      );
      const nextSelections = new Set(filteredSelections);
      if (!setsEqual(customEventSelections, nextSelections)) {
        mutations.push(() => setCustomEventSelections(nextSelections));
      }
    }

    if (Array.isArray(chartPreferences.userStrategySelections)) {
      const normalizedStrategySelections = normalizeStringArray(
        chartPreferences.userStrategySelections,
      );
      const filteredStrategySelections = userStrategyIdSet.size > 0
        ? normalizedStrategySelections.filter((id) => userStrategyIdSet.has(id))
        : normalizedStrategySelections;
      const nextStrategySelections = new Set(filteredStrategySelections);
      if (!setsEqual(userStrategySelections, nextStrategySelections)) {
        mutations.push(() => setUserStrategySelections(nextStrategySelections));
      }
    }

    if (!mutations.length) {
      return;
    }

    syncingPreferencesRef.current = true;
    mutations.forEach((applyMutation) => applyMutation());
    setTimeout(() => {
      syncingPreferencesRef.current = false;
    }, 0);
  }, [
    chartPreferences,
    showSwings,
    showSystemTrades,
    showTradeJournal,
    showFib,
    showSR,
    showDSS,
    showATR,
    showRSI,
    showADX,
    showStages,
    showBollinger,
    vwapAnchors,
    smcOverlays,
    eventLayers,
    showEMACloud,
    showWeeklyCycleLows,
    showWeeklyCycleHighs,
    showLiveWeeklyCycleLows,
    showLiveWeeklyCycleHighs,
    showCycleLowWindow,
    showCycleHighWindow,
    showPastLowWindows,
    showPastHighWindows,
    showCycleTrendlines,
    activeSMAs,
    activeEMAs,
    customEventSelections,
    customEventIdSet,
    customEventDefinitionsLoaded,
    userStrategySelections,
    userStrategyIdSet,
  ]);

  const appliedCustomEventDefaultsRef = useRef(false);

  useEffect(() => {
    if (appliedCustomEventDefaultsRef.current) {
      return;
    }
    if (!customEventDefinitionList.length) {
      return;
    }
    if (Array.isArray(chartPreferences?.customEventSelections)) {
      setCustomEventSelections(new Set(chartPreferences.customEventSelections));
      appliedCustomEventDefaultsRef.current = true;
      return;
    }
    if (chartPreferences?.showCustomEvents === false) {
      setCustomEventSelections(new Set());
      appliedCustomEventDefaultsRef.current = true;
      return;
    }
    if (chartPreferences?.showCustomEvents === true) {
      setCustomEventSelections(new Set(customEventDefinitionList.map((event) => event.id)));
      appliedCustomEventDefaultsRef.current = true;
      return;
    }
    setCustomEventSelections(new Set());
    appliedCustomEventDefaultsRef.current = true;
  }, [
    chartPreferences?.customEventSelections,
    chartPreferences?.showCustomEvents,
    customEventDefinitionList,
  ]);

  // Surface the toggled-on custom-event ids to the parent so it can fetch
  // fires for ONLY those events (scoped by customEventId) — and refetch when
  // one is toggled on — instead of pulling every custom event's fires and
  // sharing a single 200-row page across them (which silently dropped older
  // fires for users with many custom events). Fires on mount (after the
  // defaults above apply) and on every toggle.
  useEffect(() => {
    if (typeof onSelectedCustomEventIdsChange !== 'function') {
      return;
    }
    onSelectedCustomEventIdsChange(Array.from(customEventSelections));
  }, [customEventSelections, onSelectedCustomEventIdsChange]);

  // First-load defaults for user trade strategy selections — mirrors
  // the custom events pattern. Reads userStrategySelections from
  // chartPreferences if present; otherwise honours the legacy
  // showUserTrades / showTrades boolean by either selecting all
  // strategies (true) or none (false). Defaults to none when neither
  // is set.
  const appliedUserStrategyDefaultsRef = useRef(false);

  useEffect(() => {
    if (appliedUserStrategyDefaultsRef.current) {
      return;
    }
    if (!userStrategyDefinitionList.length) {
      return;
    }
    if (Array.isArray(chartPreferences?.userStrategySelections)) {
      setUserStrategySelections(new Set(chartPreferences.userStrategySelections));
      appliedUserStrategyDefaultsRef.current = true;
      return;
    }
    const legacyAllOn =
      chartPreferences?.showUserTrades === true
      || (chartPreferences?.showUserTrades === undefined
        && chartPreferences?.showTrades === true);
    const legacyAllOff =
      chartPreferences?.showUserTrades === false
      || (chartPreferences?.showUserTrades === undefined
        && chartPreferences?.showTrades === false);
    if (legacyAllOn) {
      setUserStrategySelections(new Set(userStrategyDefinitionList.map((strategy) => strategy.id)));
      appliedUserStrategyDefaultsRef.current = true;
      return;
    }
    if (legacyAllOff) {
      setUserStrategySelections(new Set());
      appliedUserStrategyDefaultsRef.current = true;
      return;
    }
    setUserStrategySelections(new Set());
    appliedUserStrategyDefaultsRef.current = true;
  }, [
    chartPreferences?.userStrategySelections,
    chartPreferences?.showUserTrades,
    chartPreferences?.showTrades,
    userStrategyDefinitionList,
  ]);

  // Auto-expand the Moving Averages sub-section when SMAs or EMAs
  // become non-empty (e.g. preferences restoring saved selections at
  // mount time). The sub-section starts collapsed by default, so
  // without this the user wouldn't see their restored MAs in the
  // dropdown until they manually expanded the group.
  useEffect(() => {
    if (activeSMAs.size === 0 && activeEMAs.size === 0) {
      return;
    }
    setExpandedSections((prev) => {
      if (prev.movingAverages) {
        return prev;
      }
      return {
        ...prev,
        movingAverages: true,
      };
    });
  }, [activeSMAs, activeEMAs]);

  useEffect(() => {
    const currentSnapshot = {
      showSwings,
      showSystemTrades,
      showTradeJournal,
      showTrades: showSystemTrades || showUserTrades,
      showFib,
      showSR,
      showDSS,
      showATR,
      showRSI,
      showADX,
      showStages,
      showBollinger,
      vwapAnchors: Array.from(vwapAnchors).sort(),
      smcOverlays: Array.from(smcOverlays).sort(),
      eventLayers: Array.from(eventLayers).sort(),
      showEMACloud,
      showWeeklyCycleLows,
      showWeeklyCycleHighs,
      showLiveWeeklyCycleLows,
      showLiveWeeklyCycleHighs,
      showCycleLowWindow,
      showCycleHighWindow,
      showPastLowWindows,
      showPastHighWindows,
      showCycleTrendlines,
      activeSMAs: Array.from(activeSMAs).sort((a, b) => a - b),
      activeEMAs: Array.from(activeEMAs).sort((a, b) => a - b),
      customEventSelections: orderedCustomEventSelections,
      userStrategySelections: orderedUserStrategySelections,
    };

    if (!onChartPreferencesChange) {
      chartPreferencesSnapshotRef.current = currentSnapshot;
      return;
    }

    if (syncingPreferencesRef.current) {
      chartPreferencesSnapshotRef.current = currentSnapshot;
      return;
    }

    const previousSnapshot = chartPreferencesSnapshotRef.current || {};
    const updates = {};

    if (previousSnapshot.showSwings !== showSwings) {
      updates.showSwings = showSwings;
    }
    if (previousSnapshot.showSystemTrades !== showSystemTrades) {
      updates.showSystemTrades = showSystemTrades;
    }
    if (previousSnapshot.showTradeJournal !== showTradeJournal) {
      updates.showTradeJournal = showTradeJournal;
    }
    if (previousSnapshot.showTrades !== currentSnapshot.showTrades) {
      updates.showTrades = currentSnapshot.showTrades;
    }
    if (previousSnapshot.showFib !== showFib) {
      updates.showFib = showFib;
    }
    if (previousSnapshot.showSR !== showSR) {
      updates.showSR = showSR;
    }
    if (previousSnapshot.showDSS !== showDSS) {
      updates.showDSS = showDSS;
    }
    if (previousSnapshot.showATR !== showATR) {
      updates.showATR = showATR;
    }
    if (previousSnapshot.showRSI !== showRSI) {
      updates.showRSI = showRSI;
    }
    if (previousSnapshot.showADX !== showADX) {
      updates.showADX = showADX;
    }
    if (previousSnapshot.showStages !== showStages) {
      updates.showStages = showStages;
    }
    if (previousSnapshot.showBollinger !== showBollinger) {
      updates.showBollinger = showBollinger;
    }
    const prevVwapAnchors = Array.isArray(previousSnapshot.vwapAnchors) ? previousSnapshot.vwapAnchors : [];
    if (!arraysEqual(prevVwapAnchors, currentSnapshot.vwapAnchors)) {
      updates.vwapAnchors = currentSnapshot.vwapAnchors;
    }
    const prevSmcOverlays = Array.isArray(previousSnapshot.smcOverlays) ? previousSnapshot.smcOverlays : [];
    if (!arraysEqual(prevSmcOverlays, currentSnapshot.smcOverlays)) {
      updates.smcOverlays = currentSnapshot.smcOverlays;
    }
    const prevEventLayers = Array.isArray(previousSnapshot.eventLayers) ? previousSnapshot.eventLayers : [];
    if (!arraysEqual(prevEventLayers, currentSnapshot.eventLayers)) {
      updates.eventLayers = currentSnapshot.eventLayers;
    }
    if (previousSnapshot.showEMACloud !== showEMACloud) {
      updates.showEMACloud = showEMACloud;
    }
    if (previousSnapshot.showWeeklyCycleLows !== showWeeklyCycleLows) {
      updates.showWeeklyCycleLows = showWeeklyCycleLows;
    }
    if (previousSnapshot.showWeeklyCycleHighs !== showWeeklyCycleHighs) {
      updates.showWeeklyCycleHighs = showWeeklyCycleHighs;
    }
    if (previousSnapshot.showLiveWeeklyCycleLows !== showLiveWeeklyCycleLows) {
      updates.showLiveWeeklyCycleLows = showLiveWeeklyCycleLows;
    }
    if (previousSnapshot.showLiveWeeklyCycleHighs !== showLiveWeeklyCycleHighs) {
      updates.showLiveWeeklyCycleHighs = showLiveWeeklyCycleHighs;
    }
    if (previousSnapshot.showCycleLowWindow !== showCycleLowWindow) {
      updates.showCycleLowWindow = showCycleLowWindow;
    }
    if (previousSnapshot.showCycleHighWindow !== showCycleHighWindow) {
      updates.showCycleHighWindow = showCycleHighWindow;
    }
    Object.assign(updates, changedPreferences(previousSnapshot, { showPastLowWindows, showPastHighWindows, showCycleTrendlines }));
    const prevSMAs = Array.isArray(previousSnapshot.activeSMAs) ? previousSnapshot.activeSMAs : [];
    const prevEMAs = Array.isArray(previousSnapshot.activeEMAs) ? previousSnapshot.activeEMAs : [];

    if (!arraysEqual(prevSMAs, currentSnapshot.activeSMAs)) {
      updates.activeSMAs = currentSnapshot.activeSMAs;
    }

    if (!arraysEqual(prevEMAs, currentSnapshot.activeEMAs)) {
      updates.activeEMAs = currentSnapshot.activeEMAs;
    }

    const prevCustomEventSelections = Array.isArray(previousSnapshot.customEventSelections)
      ? previousSnapshot.customEventSelections
      : [];
    if (!arraysEqual(prevCustomEventSelections, currentSnapshot.customEventSelections)) {
      updates.customEventSelections = currentSnapshot.customEventSelections;
    }

    const prevUserStrategySelections = Array.isArray(previousSnapshot.userStrategySelections)
      ? previousSnapshot.userStrategySelections
      : [];
    if (!arraysEqual(prevUserStrategySelections, currentSnapshot.userStrategySelections)) {
      updates.userStrategySelections = currentSnapshot.userStrategySelections;
    }

    if (Object.keys(updates).length > 0) {
      onChartPreferencesChange(updates);
    }

    chartPreferencesSnapshotRef.current = currentSnapshot;
  }, [
    showSwings,
    showSystemTrades,
    showUserTrades,
    showTradeJournal,
    showFib,
    showSR,
    showDSS,
    showATR,
    showRSI,
    showADX,
    showStages,
    showBollinger,
    vwapAnchors,
    smcOverlays,
    eventLayers,
    showEMACloud,
    showWeeklyCycleLows,
    showWeeklyCycleHighs,
    showLiveWeeklyCycleLows,
    showLiveWeeklyCycleHighs,
    showCycleLowWindow,
    showCycleHighWindow,
    showPastLowWindows,
    showPastHighWindows,
    showCycleTrendlines,
    activeSMAs,
    activeEMAs,
    orderedCustomEventSelections,
    orderedUserStrategySelections,
    onChartPreferencesChange,
  ]);

  // Toggle SMA
  const toggleSMA = (period) => {
    setActiveSMAs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(period)) {
        newSet.delete(period);
      } else {
        newSet.add(period);
      }
      return newSet;
    });
  };

  const toggleVwapAnchor = (anchor) => {
    setVwapAnchors((prev) => {
      const next = new Set(prev);
      if (next.has(anchor)) {
        next.delete(anchor);
      } else {
        next.add(anchor);
      }
      return next;
    });
  };

  const toggleSmcOverlay = (key) => {
    setSmcOverlays((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleEMA = (period) => {
    setActiveEMAs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(period)) {
        newSet.delete(period);
      } else {
        newSet.add(period);
      }
      return newSet;
    });
  };

  const handleToggleFib = useCallback(() => {
    didAutoShowFib.current = true;
    setShowFib((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!autoShowFib) {
      didAutoShowFib.current = false;
    }
  }, [autoShowFib]);

  useEffect(() => {
    if (preferredShowFib === false) {
      return;
    }
    if (autoShowFib && hasFibLevels && !didAutoShowFib.current) {
      setShowFib(true);
      didAutoShowFib.current = true;
    }
  }, [autoShowFib, hasFibLevels, preferredShowFib]);

  useEffect(() => {
    if (!hasFibLevels) {
      setShowFib(false);
    }
  }, [hasFibLevels]);

  // Transform incoming candle format to dwlf-charting format.
  const normalizeDate = useCallback((candle) => {
    if (!candle) {
      return null;
    }

    if (candle.date) {
      return candle.date;
    }

    if (candle.datetime) {
      try {
        const trimmed = `${candle.datetime}`.trim();
        if (!trimmed) {
          return null;
        }
        const candidate = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
        const withZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(candidate) ? candidate : `${candidate}Z`;
        return new Date(withZone).toISOString();
      } catch {
        return null;
      }
    }

    if (candle.weekStartDate) {
      return candle.weekStartDate;
    }

    if (candle.timestamp) {
      try {
        const iso = new Date(candle.timestamp).toISOString();
        if ((timeframe || '').toLowerCase() === 'hourly') {
          return iso;
        }
        return iso.split('T')[0];
      } catch (error) {
        return null;
      }
    }

    return null;
  }, [timeframe]);

  const data = useMemo(() => {
    if (!marketData || marketData.length === 0) {
      return [];
    }

    // Ensure chronological order (ascending date)
    const sorted = [...marketData]
      .map((candle) => {
        const date = normalizeDate(candle);
        if (!date) {
          return null;
        }
        return {
          ...candle,
          date,
          dayKey: typeof date === 'string' ? date.slice(0, 10) : undefined,
        };
      })
      .filter(Boolean)
      .sort((a, b) => new Date(a.date) - new Date(b.date));

    const lowerTimeframe = (timeframe || '').toLowerCase();
    if (lowerTimeframe === 'weekly' || lowerTimeframe === 'hourly') {
      return sorted;
    }

    const DAY_MS = 86_400_000;
    const filled = [];

    for (let i = 0; i < sorted.length; i++) {
      const candle = sorted[i];
      filled.push({ ...candle });

      const nextReal = sorted[i + 1];
      if (!nextReal) {
        break;
      }

      const currentDate = new Date(candle.date);
      let nextDate = new Date(currentDate.getTime() + DAY_MS);
      const targetTime = new Date(nextReal.date).getTime();

      while (nextDate.getTime() < targetTime) {
        const day = nextDate.toISOString().split('T')[0];
        filled.push({
          date: day,
          dayKey: day,
          _virtual: true,
        });
        nextDate = new Date(nextDate.getTime() + DAY_MS);
      }
    }

    return filled;
  }, [marketData, timeframe, normalizeDate]);

  // Mobile shows a trailing window: ~5 years weekly, 240 bars hourly, and 12 months daily (daily
  // entries include a filler entry per calendar day without a candle, so 365 entries is a year).
  // Panning back to its start widens it by the same amount again, before older history is fetched.
  const MOBILE_WINDOW_ENTRIES = { weekly: 260, hourly: 240 };
  const mobileWindowBars = MOBILE_WINDOW_ENTRIES[(timeframe || '').toLowerCase()] || 365;
  const [mobileWindowSteps, setMobileWindowSteps] = useState(1);

  const displayData = useMemo(
    () => (isMobile ? data.slice(-(mobileWindowBars * mobileWindowSteps)) : data),
    [data, isMobile, mobileWindowBars, mobileWindowSteps],
  );

  const priceCandles = useMemo(() => mapRawCandlesToOhlcv(displayData), [displayData]);
  priceCandlesRef.current = priceCandles;

  const candleTimes = useMemo(() => priceCandles.map((candle) => candle.t), [priceCandles]);
  const isHourly = (timeframe || '').toLowerCase() === 'hourly';
  const panInitialVisible = isHourly ? (isMobile ? 160 : 260) : (isMobile ? 180 : 320);
  const panExtraSlots = (isHourly ? 20 : 30) * 3;

  // On mobile a switch widens the trailing window to reach the date on screen, so the move has
  // bars to land on; otherwise (including a date before the new timeframe's first bar, where there
  // is no move) the window starts again at one step.
  const handleSeriesSwitch = (from) => {
    if (!isMobile || !from || data.length === 0 || from.endTime < Date.parse(data[0].date)) {
      setMobileWindowSteps(1);
      return;
    }
    const newer = data.filter((entry) => Date.parse(entry.date) > from.endTime).length;
    setMobileWindowSteps(1 + Math.ceil(newer / mobileWindowBars));
  };

  const { recordViewRange, visibleRange: switchRange, onSettled: handleSwitchSettled } = useTimeframeSwitchMove({
    symbol,
    timeframe,
    candleTimes,
    visibleBars: panInitialVisible,
    extraSlots: panExtraSlots,
    chartRef,
    onSwitch: handleSeriesSwitch,
  });

  const handleViewportChange = useCallback(
    (viewport) => {
      recordViewRange(viewport);
      const step = olderHistoryStep(viewport, displayData.length, data.length);
      if (step === 'widen') {
        setMobileWindowSteps((steps) => steps + 1);
      } else if (step === 'fetch') {
        onReachOldestCandles?.();
      }
    },
    [data.length, displayData.length, onReachOldestCandles, recordViewRange],
  );

  // The full (un-windowed) candle history. `displayData` is a trailing
  // window on mobile / some timeframes; the VWAP overlay must accumulate
  // from the true calendar-month open, so it is computed from this
  // fuller set and then clipped to the visible window for rendering.
  const priceCandlesFull = useMemo(() => mapRawCandlesToOhlcv(data), [data]);

  // Animation preference — on by default, user can disable in settings
  const [animationEnabled, setAnimationEnabled] = useState(() => {
    return localStorage.getItem('dwlf-animation-enabled') === 'true';
  });

  useEffect(() => {
    const handler = (e) => setAnimationEnabled(e.detail.enabled);
    window.addEventListener('dwlf-animation-preference-changed', handler);
    return () => window.removeEventListener('dwlf-animation-preference-changed', handler);
  }, []);

  // Chart Animation Integration - placed after priceCandles to avoid TDZ
  const { animationState, controls: animationControls } = useChartAnimations({
    totalCandles: priceCandles.length,
    autoStart: animationEnabled,
  });

  const phase = animationState?.phase;
  const { skipToEnd, resetAnimation } = animationControls || {};

  // If animations disabled, skip to end immediately
  useEffect(() => {
    if (!animationEnabled && skipToEnd && phase !== 'complete') {
      skipToEnd();
    }
  }, [animationEnabled, skipToEnd, phase]);

  // Reset animation when symbol or timeframe changes
  const prevSymbolTimeframeRef = useRef(`${symbol}|${effectiveSelectedTimeframe}`);
  useEffect(() => {
    const key = `${symbol}|${effectiveSelectedTimeframe}`;
    if (key !== prevSymbolTimeframeRef.current) {
      prevSymbolTimeframeRef.current = key;
      if (resetAnimation) {resetAnimation();}
    }
  }, [symbol, effectiveSelectedTimeframe, resetAnimation]);

  // Wire skipToEnd to chart area click during animation
  const handleChartAreaClick = useCallback((_info) => {
    if (animationEnabled && phase !== 'complete' && phase !== 'idle' && skipToEnd) {
      skipToEnd();
      return true;
    }
    return false;
  }, [animationEnabled, phase, skipToEnd]);

  // Control overlay visibility with delayed unmounting for smooth fade-out
  const [overlayVisible, setOverlayVisible] = useState(animationEnabled);
  useEffect(() => {
    if (!animationEnabled || phase === 'complete') {
      const timer = setTimeout(() => setOverlayVisible(false), animationEnabled ? 300 : 0);
      return () => clearTimeout(timer);
    } else {
      setOverlayVisible(true);
    }
  }, [phase]);

  const averageCandleInterval = useMemo(() => {
    if (priceCandles.length < 2) {
      return FALLBACK_DAILY_INTERVAL;
    }
    let total = 0;
    let count = 0;
    for (let i = 1; i < priceCandles.length; i += 1) {
      const diff = priceCandles[i].t - priceCandles[i - 1].t;
      if (Number.isFinite(diff) && diff > 0) {
        total += diff;
        count += 1;
      }
    }
    if (!count) {
      return FALLBACK_DAILY_INTERVAL;
    }
    return total / count;
  }, [priceCandles]);

  const dssSeries = useMemo(() => {
    if (!showDSS || priceCandles.length === 0) {
      return null;
    }

    try {
      const result = DSS.computeDSS(priceCandles, resolvedDssParams);

      const transformSeries = (series) => {
        if (!Array.isArray(series)) {
          return [];
        }

        return series
          .filter((point) => point && Number.isFinite(point.t) && Number.isFinite(point.v))
          .map((point) => ({ t: point.t, v: point.v }));
      };

      const dss = transformSeries(result.dss);
      const signal = transformSeries(result.signal);

      if (!dss.length && !signal.length) {
        return null;
      }

      return { dss, signal, params: result.params };
    } catch (error) {
      console.error('DWLFMarketChart: Failed to compute DSS', error);
      return null;
    }
  }, [showDSS, priceCandles, resolvedDssParams]);

  // DWLF-446: ATR as a percent of close, the series the jobs' atr.regime.* events rank
  // (features/markets/atrIndicator: over the full series, clipped to the visible window).
  const atrSeries = useMemo(() => {
    if (!showATR || priceCandles.length === 0) {
      return null;
    }
    try {
      return atrPercentSeries(ATR, {
        fullCandles: priceCandlesFull, from: priceCandles[0].t, to: priceCandles[priceCandles.length - 1].t,
      });
    } catch (error) {
      console.error('DWLFMarketChart: Failed to compute ATR', error);
      return null;
    }
  }, [showATR, priceCandles, priceCandlesFull]);

  // DWLF-338: RSI(14), the jobs' series (features/markets/rsiDivergence: full series, clipped).
  const rsiSeries = useMemo(() => {
    if (!showRSI || priceCandles.length === 0) {
      return null;
    }
    try {
      return computeRsiSeries(RSI, {
        fullCandles: priceCandlesFull, from: priceCandles[0].t, to: priceCandles[priceCandles.length - 1].t,
      });
    } catch (error) {
      console.error('DWLFMarketChart: Failed to compute RSI', error);
      return null;
    }
  }, [showRSI, priceCandles, priceCandlesFull]);

  // DWLF-340: ADX / DMI(14), the jobs' series (features/markets/adxPane: full series, clipped).
  const adxSeries = useMemo(() => {
    if (!showADX || priceCandles.length === 0) {
      return null;
    }
    try {
      return computeAdxSeries(ADX, {
        fullCandles: priceCandlesFull, from: priceCandles[0].t, to: priceCandles[priceCandles.length - 1].t,
      });
    } catch (error) {
      console.error('DWLFMarketChart: Failed to compute ADX', error);
      return null;
    }
  }, [showADX, priceCandles, priceCandlesFull]);

  // DWLF-338: the stored divergences of the oscillators whose events are switched on. They are
  // daily (daily pivots, daily readings), so only the daily chart draws them.
  const shownDivergenceOscillators = useMemo(() => new Set(normalizedTimeframeKey !== 'daily' ? [] : [
    ...(showRSI && eventsOn('rsi') ? ['rsi'] : []),
    ...(showDSS && eventsOn('dss') ? ['dss'] : []),
  ]), [normalizedTimeframeKey, showRSI, showDSS, eventsOn]);

  const divergencePriceSeries = useMemo(() => {
    if (shownDivergenceOscillators.size === 0 || priceCandles.length === 0) {
      return [];
    }
    const window = { from: priceCandles[0].t, to: priceCandles[priceCandles.length - 1].t };
    return [...shownDivergenceOscillators].flatMap((oscillator) => (
      divergenceSegments(divergenceEvents, { oscillator, on: 'price', ...window })
    ));
  }, [shownDivergenceOscillators, divergenceEvents, priceCandles]);

  // DWLF-184 (admin beta): Trendlines V3 through the confirmed cycle pivots, beside the existing
  // lines, on the daily and weekly charts. Computed over the full candles, drawn in the window.
  const cycleTrendlineLines = useMemo(() => {
    if (!cycleTrendlinesBeta || !showCycleTrendlines || !CYCLE_TRENDLINE_TIMEFRAMES.includes(normalizedTimeframeKey)) {
      return [];
    }
    try {
      return computeCycleTrendlines(priceCandlesFull, weeklyCycleLows, weeklyCycleHighs);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('DWLFMarketChart: Failed to compute cycle trendlines', error);
      return [];
    }
  }, [cycleTrendlinesBeta, showCycleTrendlines, normalizedTimeframeKey, priceCandlesFull, weeklyCycleLows, weeklyCycleHighs]);

  const cycleTrendlinePriceSeries = useMemo(() => {
    if (cycleTrendlineLines.length === 0 || priceCandles.length === 0 || priceCandlesFull.length === 0) {
      return [];
    }
    return cycleTrendlineSeries(cycleTrendlineLines, {
      lastT: priceCandlesFull[priceCandlesFull.length - 1].t,
      from: priceCandles[0].t,
      to: priceCandles[priceCandles.length - 1].t,
    });
  }, [cycleTrendlineLines, priceCandles, priceCandlesFull]);

  const bollingerSeries = useMemo(() => {
    if (!showBollinger || priceCandles.length === 0) {
      return [];
    }

    try {
      const result = Bollinger.computeBollingerBands(priceCandles, resolvedBollingerParams);

      const transformSeries = (series) => {
        if (!Array.isArray(series)) {
          return [];
        }
        return series
          .filter((point) => point && Number.isFinite(point.t) && Number.isFinite(point.v))
          .map((point) => ({ t: point.t, v: point.v }));
      };

      const upper = transformSeries(result.upper);
      const middle = transformSeries(result.middle);
      const lower = transformSeries(result.lower);

      if (!upper.length && !middle.length && !lower.length) {
        return [];
      }

      const series = [];
      if (upper.length) {
        series.push({
          key: 'Bollinger Upper',
          type: 'line',
          data: upper,
          style: { color: '#4FC3F7', lineWidth: 1.5 },
        });
      }
      if (middle.length) {
        series.push({
          key: 'Bollinger Middle',
          type: 'line',
          data: middle,
          style: { color: '#FDD663', lineWidth: 1 },
        });
      }
      if (lower.length) {
        series.push({
          key: 'Bollinger Lower',
          type: 'line',
          data: lower,
          style: { color: '#4FC3F7', lineWidth: 1.5, dashed: true },
        });
      }

      return series;
    } catch (error) {
      console.error('DWLFMarketChart: Failed to compute Bollinger Bands', error, {
        params: resolvedBollingerParams,
      });
      return [];
    }
  }, [showBollinger, priceCandles, resolvedBollingerParams]);

  // VWAP overlay — one volume-weighted average price line (plus its ±1σ
  // and ±2σ bands) per active anchor period. Each anchor re-anchors at
  // the start of its calendar period, so the overlay is emitted as one
  // set of line segments per period: the chart has no null-gap support,
  // so a fresh segment per period renders the re-anchor as a clean break
  // rather than a line dragged across the discontinuity.
  //
  // The VWAP is computed from the FULL candle history so every period
  // accumulates from its true open, then clipped to the visible window
  // for rendering — this keeps the leftmost visible period correct even
  // when `displayData` is a trailing window that starts mid-period.
  // Volumeless instruments (spot metals / FX) return hasVolume:false and
  // render nothing — VWAP is meaningless without volume.
  const vwapSeries = useMemo(() => {
    if (vwapAnchors.size === 0 || priceCandlesFull.length === 0 || priceCandles.length === 0) {
      return [];
    }

    // Bounds of the chart's visible window — values are computed over
    // full history, but only this range is drawn.
    const visibleStart = priceCandles[0].t;
    const visibleEnd = priceCandles[priceCandles.length - 1].t;
    const series = [];

    for (const option of VWAP_ANCHOR_OPTIONS) {
      if (!vwapAnchors.has(option.key)) {
        continue;
      }
      try {
        const result = VWAP.computeVwapSeries(priceCandlesFull, { anchor: option.key });
        if (!result?.hasVolume || !Array.isArray(result.points) || result.points.length === 0) {
          continue;
        }

        // Split the points into one segment per anchor period.
        const segments = [];
        let current = [];
        for (const point of result.points) {
          if (point.isAnchor && current.length > 0) {
            segments.push(current);
            current = [];
          }
          current.push(point);
        }
        if (current.length > 0) {
          segments.push(current);
        }

        for (const segment of segments) {
          // Clip to the visible window — VWAP values stay correct (they
          // accumulated from the true period open), only rendering is bounded.
          const visible = segment.filter((p) => p.t >= visibleStart && p.t <= visibleEnd);
          if (visible.length === 0) {
            continue;
          }
          const label = new Date(segment[0].anchorT).toISOString().slice(0, 10);

          series.push({
            key: `VWAP-${option.key} ${label}`,
            type: 'line',
            data: visible.map((p) => ({ t: p.t, v: p.vwap })),
            style: { color: option.line, lineWidth: 1.75 },
          });

          for (const sigma of [1, 2]) {
            const banded = visible
              .map((p) => ({ t: p.t, band: (p.bands || []).find((b) => b.sigma === sigma) }))
              .filter((d) => d.band);
            if (!banded.length) {
              continue;
            }
            series.push({
              key: `VWAP-${option.key} +${sigma}σ ${label}`,
              type: 'line',
              data: banded.map((d) => ({ t: d.t, v: d.band.upper })),
              style: { color: option.band, lineWidth: 1, dashed: sigma === 2 },
            });
            series.push({
              key: `VWAP-${option.key} -${sigma}σ ${label}`,
              type: 'line',
              data: banded.map((d) => ({ t: d.t, v: d.band.lower })),
              style: { color: option.band, lineWidth: 1, dashed: sigma === 2 },
            });
          }
        }
      } catch (error) {
        console.error(`DWLFMarketChart: Failed to compute VWAP (${option.key})`, error);
      }
    }

    return series;
  }, [vwapAnchors, priceCandlesFull, priceCandles]);

  // Discretionary anchored-VWAP annotations (Mode B). For each anchored_vwap
  // annotation, the VWAP is computed from the user's clicked candle forward:
  // slice the full candle history to bars >= anchorTime and run the detector
  // with the `all` anchor, which anchors once at the slice's first bar. Same
  // full-history-then-clip discipline as the systematic VWAP overlay above, so
  // the line is correct even when the visible window starts after the anchor.
  // Rendered as chart series (not via the generic annotation layer, which has
  // no VWAP math); volumeless instruments render nothing.
  const anchoredVwapSeries = useMemo(() => {
    if (!annotations?.length || priceCandlesFull.length === 0 || priceCandles.length === 0) {
      return [];
    }
    const visibleStart = priceCandles[0].t;
    const visibleEnd = priceCandles[priceCandles.length - 1].t;
    const series = [];

    for (const ann of annotations) {
      if (ann.type !== 'anchored_vwap' || ann.visible === false) {
        continue;
      }
      const anchorTime = Number(ann.anchorTime);
      if (!Number.isFinite(anchorTime)) {
        continue;
      }
      const slice = priceCandlesFull.filter((c) => c.t >= anchorTime);
      if (slice.length < 2) {
        continue;
      }
      try {
        const result = VWAP.computeVwapSeries(slice, { anchor: 'all' });
        if (!result?.hasVolume || !Array.isArray(result.points) || result.points.length === 0) {
          continue;
        }
        const lineColor = ann.color || '#B388FF';
        const bandColor = ann.bandColor || '#9575CD';
        const lineWidth = Number.isFinite(ann.lineWidth) ? ann.lineWidth : 1.75;
        const sigmas = ann.resolution === '1' ? [1] : ann.resolution === '2' ? [2] : [1, 2];

        const visible = result.points.filter((p) => p.t >= visibleStart && p.t <= visibleEnd);
        if (visible.length === 0) {
          continue;
        }

        series.push({
          key: `AVWAP-${ann.id}`,
          type: 'line',
          data: visible.map((p) => ({ t: p.t, v: p.vwap })),
          style: { color: lineColor, lineWidth },
        });

        for (const sigma of sigmas) {
          const banded = visible
            .map((p) => ({ t: p.t, band: (p.bands || []).find((b) => b.sigma === sigma) }))
            .filter((d) => d.band);
          if (!banded.length) {
            continue;
          }
          series.push({
            key: `AVWAP-${ann.id} +${sigma}σ`,
            type: 'line',
            data: banded.map((d) => ({ t: d.t, v: d.band.upper })),
            style: { color: bandColor, lineWidth: 1, dashed: sigma === 2 },
          });
          series.push({
            key: `AVWAP-${ann.id} -${sigma}σ`,
            type: 'line',
            data: banded.map((d) => ({ t: d.t, v: d.band.lower })),
            style: { color: bandColor, lineWidth: 1, dashed: sigma === 2 },
          });
        }
      } catch (error) {
        console.error('DWLFMarketChart: Failed to compute anchored VWAP', error);
      }
    }

    return series;
  }, [annotations, priceCandlesFull, priceCandles]);

  const computedSwingPoints = useMemo(() => {
    if (!showSwings || priceCandles.length === 0) {
      return [];
    }

    const timeframeKey = normalizedTimeframeKey;

    const lookback = resolvedSwingLookbacks[timeframeKey] ?? DEFAULT_SWING_LOOKBACKS[timeframeKey] ?? DEFAULT_SWING_LOOKBACKS.daily;

    if (!Number.isFinite(lookback) || lookback <= 0) {
      return [];
    }

    try {
      const result = Swing.computeSwings(priceCandles, { lookback });
      return result.points.map((point) => {
        const iso = new Date(point.t).toISOString();
        const dayKey = iso.split('T')[0];
        const normalizedDate = timeframeKey === 'hourly' ? iso : dayKey;
        return {
          date: normalizedDate,
          dayKey,
          timestamp: point.t,
          type: point.type,
          price: point.price,
        };
      });
    } catch (error) {
      console.error('DWLFMarketChart: Failed to compute swing points', error, {
        lookback,
        timeframe: timeframeKey,
      });
      return [];
    }
  }, [showSwings, priceCandles, resolvedSwingLookbacks, normalizedTimeframeKey]);

  // An alert line draws its own alert, and an alert this session replaced or cancelled is gone, so
  // neither is drawn as an alert guide.
  const hiddenAlertGuideIds = useMemo(() => new Set([
    ...(allAnnotations || []).filter((a) => a.type === 'alert_line' && a.alertId).map((a) => a.alertId),
    ...(retiredAlertIds || []),
  ]), [allAnnotations, retiredAlertIds]);

  const basePricePaneGuides = useMemo(() => {
    const guides = [];

    if (showSR && supportResistance) {
      const { support = [], resistance = [] } = supportResistance;

      // Server key levels (DWLF-333): every served level is in play, nearest
      // first, drawn from its earliest anchor to the right edge. Opacity
      // scales with score within each side.
      const getMaxScore = (levels) => {
        const maxScore = levels.reduce((max, level) => {
          const score = level?.score;
          return typeof score === 'number' && Number.isFinite(score) ? Math.max(max, score) : max;
        }, 0);
        return maxScore > 0 ? maxScore : null;
      };

      const getOpacityForLevel = (level, maxScore, index) => {
        const score = level?.score;
        if (typeof score === 'number' && Number.isFinite(score) && maxScore) {
          return Math.max(0.3, Math.min(1, score / maxScore));
        }
        return Math.max(0.3, 1 - (index * 0.07));
      };

      const pushLevels = (levels, sideLabel, baseColor) => {
        const maxScore = getMaxScore(levels);
        levels
          .filter((level) => Number.isFinite(Number(level?.level)))
          .forEach((level, index) => {
            guides.push({
              y: Number(level.level),
              dashed: true,
              color: `rgba(${baseColor}, ${getOpacityForLevel(level, maxScore, index)})`,
              label: index === 0 ? sideLabel : `${sideLabel.charAt(0)}${index + 1}`,
              startTime: level.formationTime,
            });
          });
      };

      pushLevels(support, 'Support', '0, 191, 255');
      pushLevels(resistance, 'Resistance', '255, 107, 107');
    }

    if (showFib && Array.isArray(fibLevels) && fibLevels.length > 0) {
      guides.push(...fibGuides(fibLevels[0]));
    }

    guides.push(...alertGuides(alerts, hiddenAlertGuideIds));

    return guides;
  }, [showSR, supportResistance, showFib, fibLevels, alerts, hiddenAlertGuideIds]);

  const candleByDate = useMemo(() => {
    const map = new Map();
    displayData.forEach((candle) => {
      if (!candle || candle._virtual) {
        return;
      }
      const keys = new Set();
      const normalized = normalizeDate(candle);
      if (normalized) {
        keys.add(normalized);
      }
      if (typeof candle.date === 'string') {
        keys.add(candle.date);
      }
      if (typeof candle.dayKey === 'string') {
        keys.add(candle.dayKey);
      }
      if (typeof candle.datetime === 'string') {
        keys.add(candle.datetime);
      }
      if (candle.timestamp) {
        try {
          const iso = new Date(candle.timestamp).toISOString();
          keys.add(iso);
          keys.add(iso.split('T')[0]);
        } catch {
          // ignore invalid timestamp
        }
      }
      keys.forEach((key) => {
        if (!key) {
          return;
        }
        map.set(key, candle);
        if (typeof key === 'string') {
          const shortKey = key.includes('T') ? key.split('T')[0] : key;
          map.set(shortKey, candle);
        }
      });
    });
    return map;
  }, [displayData, normalizeDate]);

  const activeSwingPoints = useMemo(() => {
    if (computedSwingPoints.length > 0) {
      return computedSwingPoints;
    }
    const lower = (timeframe || '').toLowerCase();
    if (lower === 'weekly') {
      return Array.isArray(weeklySwingPoints) ? weeklySwingPoints : [];
    }
    return Array.isArray(swingPoints) ? swingPoints : [];
  }, [computedSwingPoints, timeframe, swingPoints, weeklySwingPoints]);

  const swingMarkerSeries = useMemo(() => {
    if (!showSwings || !Array.isArray(activeSwingPoints) || activeSwingPoints.length === 0) {
      return [];
    }

    const highPoints = [];
    const lowPoints = [];

    activeSwingPoints.forEach((point) => {
      if (!point) {
        return;
      }
      const key = point.date || point.datetime || point.timestamp || point.dayKey;
      if (!key) {
        return;
      }
      const candle = candleByDate.get(key);
      if (!candle) {
        return;
      }

      const resolvePrice = (type) => {
        if (Number.isFinite(point?.price)) {
          return Number(point.price);
        }
        if (type === 'high') {
          return Number(
            candle.high ?? candle.h ?? candle.High ?? candle.highPrice ?? candle.high_price,
          );
        }
        return Number(
          candle.low ?? candle.l ?? candle.Low ?? candle.lowPrice ?? candle.low_price,
        );
      };

      const type = (point.type || '').toLowerCase() === 'high' ? 'high' : 'low';
      const price = resolvePrice(type);
      if (!Number.isFinite(price)) {
        return;
      }

      let timestamp = Number(candle.t);
      if (!Number.isFinite(timestamp)) {
        const source = candle.date || candle.datetime || candle.timestamp || key;
        const time = new Date(source).getTime();
        if (Number.isFinite(time)) {
          timestamp = time;
        }
      }
      if (!Number.isFinite(timestamp)) {
        return;
      }

      const datum = {
        t: timestamp,
        v: price,
      };

      if (type === 'high') {
        highPoints.push({
          ...datum,
          shape: 'arrow-down',
          color: '#FF8A80',
          size: 6,
          offsetY: -8,
        });
      } else {
        lowPoints.push({
          ...datum,
          shape: 'arrow-up',
          color: '#81D4FA',
          size: 6,
          offsetY: 8,
        });
      }
    });

    const series = [];
    if (highPoints.length) {
      series.push({
        key: 'Swing Highs',
        type: 'marker',
        data: highPoints,
        style: {
          color: '#FF8A80',
          markerShape: 'arrow-down',
          markerSize: 6,
          markerOffsetY: -8,
        },
      });
    }
    if (lowPoints.length) {
      series.push({
        key: 'Swing Lows',
        type: 'marker',
        data: lowPoints,
        style: {
          color: '#81D4FA',
          markerShape: 'arrow-up',
          markerSize: 6,
          markerOffsetY: 8,
        },
      });
    }

    return series;
  }, [showSwings, activeSwingPoints, candleByDate]);

  const tradeLayers = useMemo(() => {
    const THIRTY_CANDLES_MS = 29 * 86_400_000;

    // System trades keep the existing zone-based representation —
    // they're sparse, curated, and the rectangles read fine at low
    // density. User trades are now markers (see below) so they're
    // built on a separate path.
    const systemAggregated = [];

    if (legacyTradesAvailable) {
      systemAggregated.push(...tradeSignals);
    }

    if (showSystemTrades && Array.isArray(systemTradeSignals) && systemTradeSignals.length > 0) {
      systemAggregated.push(...systemTradeSignals);
    }

    // User trades — filter by selected strategies before doing any
    // work. Strategies with zero selections contribute nothing.
    const filteredUserTrades = (showUserTrades && Array.isArray(userTradeSignals))
      ? userTradeSignals.filter((trade) => {
          const id =
            trade?.strategy
            ?? trade?.strategyId
            ?? trade?.strategy_type
            ?? trade?.strategyName
            ?? trade?.strategyDescription;
          return id && userStrategySelections.has(id);
        })
      : [];

    const systemMarkerPoints = [];
    const systemPositionData = [];
    const userTradeLongMarkerPoints = [];
    const userTradeShortMarkerPoints = [];
    const userTradeGroups = [];
    const manualMarkerSeries = [];
    const manualPositionSeries = [];
    const manualGuides = [];

    const systemMarkerColors = {
      entry: '#2962FF',
      exit: '#4CAF50',
    };
    const systemZoneColors = {
      risk: 'rgba(255, 107, 107, 0.25)',
      reward: 'rgba(76, 175, 80, 0.25)',
    };

    // System trades — unchanged from the previous implementation.
    systemAggregated.forEach((trade) => {
      if (!trade) {
        return;
      }
      const entryPrice = Number(trade.initialPrice ?? trade.entryPrice);
      const stopPrice = Number(trade.stopLossLevel ?? trade.stopPrice);
      const takePrice = Number(trade.target3R ?? trade.takeProfitLevel ?? trade.takeProfit);
      const startRaw = trade.date ?? trade.entryDate;
      if (!Number.isFinite(entryPrice) || !Number.isFinite(stopPrice) || !Number.isFinite(takePrice) || !startRaw) {
        return;
      }

      const startTime = new Date(startRaw).getTime();
      if (!Number.isFinite(startTime)) {
        return;
      }

      const endRaw = trade.exitDate ?? trade.closeDate ?? null;
      const computedEnd = endRaw ? new Date(endRaw).getTime() : startTime + THIRTY_CANDLES_MS;
      const endTime = Number.isFinite(computedEnd) ? computedEnd : undefined;

      const messages = [];

      const entryLine = Number.isFinite(entryPrice) ? `Entry: $${entryPrice.toFixed(2)}` : null;
      const stopLine = Number.isFinite(stopPrice) ? `SL: $${stopPrice.toFixed(2)}` : null;
      const textParts = [
        trade.strategyDescription || trade.strategy || trade.strategyName || trade.strategy_type,
        trade.reasonSummary,
        entryLine,
        stopLine,
      ].filter(Boolean);
      if (textParts.length) {
        const riskDistance = Math.abs((entryPrice ?? 0) - (stopPrice ?? entryPrice));
        let anchorPrice = entryPrice;
        if (riskDistance > 0) {
          const shift = riskDistance * 0.9;
          anchorPrice = entryPrice > stopPrice ? entryPrice + shift : entryPrice - shift;
        }
        messages.push({
          t: startTime,
          v: anchorPrice,
          text: textParts.join('\n'),
        });
      }

      if (trade.strategyDetails && Array.isArray(trade.strategyDetails.events)) {
        trade.strategyDetails.events.forEach((event) => {
          if (!event?.date) {
            return;
          }
          const eventTime = new Date(event.date).getTime();
          if (!Number.isFinite(eventTime)) {
            return;
          }
          const eventCandle = candleByDate.get(event.date) || candleByDate.get(event.date.split('T')[0]);
          const eventPrice = Number(
            event.price
              ?? eventCandle?.close
              ?? eventCandle?.c
              ?? eventCandle?.high
              ?? eventCandle?.h
              ?? entryPrice,
          );
          if (!Number.isFinite(eventPrice)) {
            return;
          }
          messages.push({
            t: eventTime,
            v: eventPrice,
            text: event.commentary || event.type || 'event',
          });
        });
      }

      systemPositionData.push({
        start: startTime,
        end: endTime,
        entry: entryPrice,
        stop: stopPrice,
        target: takePrice,
        messages,
        riskColor: systemZoneColors.risk,
        rewardColor: systemZoneColors.reward,
        bubbleColor: 'rgba(0,0,0,0.85)',
        textColor: 'white',
        fontSize: 11,
        pointer: true,
        padding: 6,
      });

      systemMarkerPoints.push({
        t: startTime,
        v: entryPrice,
        label: 'ENTRY',
        shape: 'circle',
        color: systemMarkerColors.entry,
        size: 7,
        offsetY: -12,
        textColor: systemMarkerColors.entry,
        textOffsetY: -6,
      });

      const exitPrice = Number(trade.exitPrice ?? trade.closePrice);
      if (endTime && Number.isFinite(exitPrice)) {
        systemMarkerPoints.push({
          t: endTime,
          v: exitPrice,
          label: 'EXIT',
          shape: 'circle',
          color: systemMarkerColors.exit,
          size: 7,
          offsetY: -12,
          textColor: systemMarkerColors.exit,
          textOffsetY: -6,
        });
      }
    });

    // User trades — new marker-based representation.
    //
    // Each trade renders as one triangle anchored to its entry
    // candle. Direction is inferred from the price geometry:
    //   - long  : target > entry > stop  → ▲ below the candle low
    //   - short : target < entry < stop  → ▼ above the candle high
    //
    // Colours come from the muted event palette so the user trade
    // markers feel like part of the same family as custom + indicator
    // event markers, not a foreign decorative element.
    //
    // The full risk/reward zones are intentionally NOT rendered as
    // chart geometry — they live inside the cursor-following hover
    // tooltip instead, which scales gracefully to many trades.
    // A single warm amber accent for all user trade markers. Using
    // green/red here causes the markers to blend into the candles
    // (green longs vanish on green candles, red shorts on red). The
    // triangle direction (▲ / ▼) already communicates long/short, so
    // the colour's job is purely *visibility*, not direction encoding.
    const TRADE_MARKER_COLOUR = '#c9a227';
    filteredUserTrades.forEach((trade) => {
      const entryPrice = Number(trade.initialPrice ?? trade.entryPrice);
      const stopPrice = Number(trade.stopLossLevel ?? trade.stopPrice);
      const takePrice = Number(trade.target3R ?? trade.takeProfitLevel ?? trade.takeProfit);
      const startRaw = trade.date ?? trade.entryDate;
      if (!Number.isFinite(entryPrice) || !Number.isFinite(stopPrice) || !Number.isFinite(takePrice) || !startRaw) {
        return;
      }
      const startTime = new Date(startRaw).getTime();
      if (!Number.isFinite(startTime)) {
        return;
      }
      const endRaw = trade.exitDate ?? trade.closeDate ?? null;
      const endTime = endRaw ? new Date(endRaw).getTime() : null;
      const exitPrice = Number(trade.exitPrice ?? trade.closePrice);

      const isLong = takePrice > entryPrice;
      const direction = isLong ? 'long' : 'short';
      const colour = TRADE_MARKER_COLOUR;

      const targetCollection = isLong ? userTradeLongMarkerPoints : userTradeShortMarkerPoints;
      targetCollection.push({
        t: startTime,
        v: entryPrice,
        color: colour,
        size: 6,
      });

      userTradeGroups.push({
        timestamp: startTime,
        entryPrice,
        stopPrice,
        takePrice,
        endTime,
        exitPrice: Number.isFinite(exitPrice) ? exitPrice : null,
        direction,
        strategyId:
          trade?.strategy
          ?? trade?.strategyId
          ?? trade?.strategy_type
          ?? trade?.strategyName
          ?? trade?.strategyDescription,
        strategyName:
          trade?.strategyName
          || trade?.strategyDescription
          || trade?.strategy
          || 'Strategy',
        reasonSummary: trade?.reasonSummary || null,
        active: trade?.active === true || trade?.active === 'true' || (!endRaw && !Number.isFinite(exitPrice)),
      });
    });

    const toTimestamp = (value) => {
      if (!value && value !== 0) {
        return null;
      }
      const date = new Date(value);
      const time = date.getTime();
      return Number.isFinite(time) ? time : null;
    };

    const normalizeOverlayConfig = (config) => {
      if (!config || typeof config !== 'object') {
        return [];
      }
      const overlays = [];
      const direction = (config.direction || '').toLowerCase();
      const isShort = direction === 'short' || direction === 'sell';
      const entryPrice = Number(config.entryPrice);
      const entryDate = config.entryDate;
      const stopPrice = Number(config.initialStop);
      const takeProfit = Number(config.initialTakeProfit);
      const exitPrice = Number(config.exitPrice);
      const exitDate = config.exitDate;

      const formatPrice = (value) => `$${Number(value).toFixed(2)}`;

      if (Number.isFinite(entryPrice)) {
        overlays.push({
          type: 'horizontal-line',
          value: entryPrice,
          color: '#1d4ed8',
          strokeDasharray: '4,4',
          label: `Entry: ${formatPrice(entryPrice)}`,
        });
        if (entryDate) {
          overlays.push({
            type: 'marker',
            key: 'manual-entry-marker',
            points: [{
              date: entryDate,
              price: entryPrice,
              text: 'ENTRY',
            }],
            shape: isShort ? 'arrow-down' : 'arrow-up',
            color: '#1d4ed8',
            size: 8,
            offsetY: isShort ? 12 : -12,
            fontSize: 11,
            textColor: '#1d4ed8',
            textOffsetY: isShort ? 18 : -18,
          });
        }
      }

      if (Number.isFinite(stopPrice)) {
        overlays.push({
          type: 'horizontal-line',
          value: stopPrice,
          color: '#ef4444',
          strokeDasharray: '6,6',
          label: `Stop: ${formatPrice(stopPrice)}`,
        });
      }

      if (Number.isFinite(takeProfit) && takeProfit !== 0) {
        overlays.push({
          type: 'horizontal-line',
          value: takeProfit,
          color: '#22c55e',
          strokeDasharray: '6,6',
          label: `Target: ${formatPrice(takeProfit)}`,
        });
      }

      if (Number.isFinite(exitPrice)) {
        overlays.push({
          type: 'horizontal-line',
          value: exitPrice,
          color: '#22c55e',
          strokeDasharray: '4,4',
          label: `Exit: ${formatPrice(exitPrice)}`,
        });
        if (exitDate) {
          overlays.push({
            type: 'marker',
            key: 'manual-exit-marker',
            points: [{
              date: exitDate,
              price: exitPrice,
              text: 'EXIT',
            }],
            shape: isShort ? 'arrow-up' : 'arrow-down',
            color: '#22c55e',
            size: 8,
            offsetY: isShort ? -12 : -12,
            fontSize: 11,
            textColor: '#22c55e',
            textOffsetY: -18,
          });
        }
      }

      const addEventMarkers = (items = [], keyPrefix, defaults = {}) => {
        items.forEach((item, index) => {
          const date = item?.date
            || item?.executedAt
            || item?.happenedAt
            || item?.eventDate
            || item?.createdAt
            || item?.timestamp
            || item?.time;
          const price = Number(
            item?.price
              ?? item?.level
              ?? item?.entryPrice
              ?? item?.triggerPrice
              ?? item?.priceAtExecution
              ?? entryPrice,
          );
          const label =
            item?.label
            || item?.name
            || item?.type
            || item?.side
            || (defaults.defaultLabelPrefix
              ? `${defaults.defaultLabelPrefix}${index + 1}`
              : `${keyPrefix.toUpperCase()} ${index + 1}`);
          if (!Number.isFinite(price) || !date) {
            return;
          }
          overlays.push({
            type: 'marker',
            key: `${keyPrefix}-${index}`,
            points: [{
              date,
              price,
              text: label.toUpperCase(),
            }],
            shape: defaults.shape ?? 'circle',
            color: defaults.color ?? '#fbbf24',
            size: defaults.size ?? 6,
            offsetY: defaults.offsetY ?? 10,
            fontSize: defaults.fontSize ?? 10,
            textColor: defaults.textColor ?? defaults.color ?? '#fbbf24',
            textOffsetY: defaults.textOffsetY ?? 16,
          });
        });
      };

      addEventMarkers(config.linkedEvents, 'linked-event', { color: '#fbbf24', offsetY: 12 });
      addEventMarkers(config.recentActions, 'recent-action', { color: '#f97316', offsetY: 12 });
      addEventMarkers(
        config.executions,
        'execution',
        { color: '#facc15', offsetY: -10, textOffsetY: -18, defaultLabelPrefix: 'E' },
      );

      if (Array.isArray(config.eventMarkers)) {
        addEventMarkers(config.eventMarkers, 'manual-event', { color: '#facc15', offsetY: 14 });
      }

      return overlays;
    };

    const manualOverlayItems = [];
    const overlayConfig = tradeOverlays;
    if (Array.isArray(overlayConfig)) {
      manualOverlayItems.push(...overlayConfig);
    } else if (overlayConfig && typeof overlayConfig === 'object') {
      manualOverlayItems.push(...normalizeOverlayConfig(overlayConfig));
    }

    manualOverlayItems.forEach((overlay, index) => {
      if (!overlay || typeof overlay !== 'object') {
        return;
      }

      if (overlay.type === 'horizontal-line') {
        const numeric = Number(overlay.value);
        if (Number.isFinite(numeric)) {
          manualGuides.push({
            y: numeric,
            dashed: !!overlay.strokeDasharray,
            label: overlay.label,
            color: overlay.color ?? 'rgba(148, 163, 184, 0.45)',
          });
        }
        return;
      }

      if (overlay.type === 'marker') {
        const points = Array.isArray(overlay.points) ? overlay.points : [];
        const data = points
          .map((point) => {
            const timestamp = toTimestamp(point?.date ?? point?.t ?? point?.time);
            const price = Number(point?.price ?? point?.v);
            if (!Number.isFinite(timestamp) || !Number.isFinite(price)) {
              return null;
            }
            const payload = { t: timestamp, v: price };
            if (point?.text) {
              payload.label = point.text;
            }
            return payload;
          })
          .filter(Boolean);
        if (!data.length) {
          return;
        }

        manualMarkerSeries.push({
          key: overlay.key ?? `manual-marker-${index}`,
          type: 'marker',
          data,
          style: {
            color: overlay.color ?? '#fbbf24',
            markerShape: overlay.shape ?? 'circle',
            markerSize: overlay.size ?? 6,
            markerOffsetY: overlay.offsetY ?? 0,
            markerFontSize: overlay.fontSize,
            markerTextColor: overlay.textColor ?? overlay.color ?? '#fbbf24',
            markerTextOffsetY: overlay.textOffsetY ?? 0,
          },
        });
        return;
      }

      if (overlay.type === 'position' || overlay.type === 'trade') {
        const start = toTimestamp(overlay.startDate ?? overlay.start);
        const end = toTimestamp(overlay.endDate ?? overlay.end);
        const entry = Number(overlay.entryPrice ?? overlay.entry);
        const stop = Number(overlay.stopPrice ?? overlay.stop);
        const target = Number(overlay.targetPrice ?? overlay.target ?? overlay.exitPrice);
        const messages = Array.isArray(overlay.messages)
          ? overlay.messages
              .map((message) => {
                const timestamp = toTimestamp(message?.date ?? message?.time ?? message?.t);
                const price = Number(message?.price ?? message?.v ?? entry);
                if (!Number.isFinite(timestamp) || !Number.isFinite(price)) {
                  return null;
                }
                return {
                  t: timestamp,
                  v: price,
                  text: message?.text ?? '',
                };
              })
              .filter(Boolean)
          : [];

        if (!Number.isFinite(entry)) {
          return;
        }

        manualPositionSeries.push({
          key: overlay.key ?? `manual-position-${index}`,
          type: 'position',
          data: [{
            start,
            end,
            entry,
            stop: Number.isFinite(stop) ? stop : entry,
            target: Number.isFinite(target) ? target : entry,
            messages,
            riskColor: overlay.riskColor ?? 'rgba(255, 107, 107, 0.2)',
            rewardColor: overlay.rewardColor ?? 'rgba(76, 175, 80, 0.2)',
            bubbleColor: overlay.bubbleColor ?? 'rgba(0,0,0,0.85)',
            textColor: overlay.textColor ?? 'white',
            fontSize: overlay.fontSize ?? 11,
            pointer: overlay.pointer ?? true,
            padding: overlay.padding ?? 6,
          }],
        });
      }
    });

    const markerSeries = [];
    if (systemMarkerPoints.length) {
      markerSeries.push({
        key: 'System Trade Markers',
        type: 'marker',
        data: systemMarkerPoints,
        style: {
          color: '#2962FF',
          markerShape: 'circle',
          markerSize: 7,
          markerOffsetY: -12,
          markerTextColor: '#2962FF',
          markerTextOffsetY: -6,
        },
      });
    }
    // User trade markers are split into two series (one per direction)
    // because the chart library treats series-level markerShape /
    // markerOffsetY as authoritative, overriding per-point values.
    // This mirrors the swing points pattern (Swing Highs and Swing
    // Lows are separate series).
    if (userTradeLongMarkerPoints.length) {
      markerSeries.push({
        key: 'User Trade Longs',
        type: 'marker',
        data: userTradeLongMarkerPoints,
        style: {
          color: TRADE_MARKER_COLOUR,
          markerShape: 'arrow-up',
          markerSize: 7,
          markerOffsetY: 14,
          markerVariant: 'outline',
          markerStrokeWidth: 1.5,
          markerHaloSize: 4,
          markerHaloOpacity: 0.25,
        },
      });
    }
    if (userTradeShortMarkerPoints.length) {
      markerSeries.push({
        key: 'User Trade Shorts',
        type: 'marker',
        data: userTradeShortMarkerPoints,
        style: {
          color: TRADE_MARKER_COLOUR,
          markerShape: 'arrow-down',
          markerSize: 7,
          markerOffsetY: -14,
          markerVariant: 'outline',
          markerStrokeWidth: 1.5,
          markerHaloSize: 4,
          markerHaloOpacity: 0.25,
        },
      });
    }
    if (manualMarkerSeries.length) {
      // All manual marker series render. (Previously only manual-entry /
      // manual-exit keys were merged, which silently dropped custom marker
      // overlays like the ghost-trade peak/verdict markers.)
      markerSeries.push(...manualMarkerSeries);
    }
    const positionSeries = [];
    if (manualPositionSeries.length) {
      // Manual position overlays (e.g. the ghost-trade risk/reward zone) were
      // previously collected but never merged — dead code since the user
      // trade zones were removed in favour of the hover tooltip.
      positionSeries.push(...manualPositionSeries);
    }
    if (systemPositionData.length) {
      positionSeries.push({
        key: 'System Trade Zones',
        type: 'position',
        data: systemPositionData,
        style: {
          riskColor: 'rgba(255, 107, 107, 0.25)',
          rewardColor: 'rgba(76, 175, 80, 0.25)',
          bubbleColor: 'rgba(0,0,0,0.85)',
          textColor: 'white',
          fontSize: 11,
          pointer: true,
          padding: 6,
        },
      });
    }
    // User trade rectangles (the previous "User Trade Zones" position
    // series) are intentionally gone. Risk/reward setup now lives in
    // the cursor-following hover tooltip via userTradeGroups.
    return { guides: manualGuides, markerSeries, positionSeries, userTradeGroups };
  }, [
    legacyTradesAvailable,
    tradeSignals,
    showSystemTrades,
    showUserTrades,
    systemTradeSignals,
    userTradeSignals,
    userStrategySelections,
    candleByDate,
    tradeOverlays,
  ]);

  // SMC all-families detection (BOS/ChoCH, FVG, order blocks). Hoisted above
  // the indicator-event memo so the top-of-chart event markers (below) and the
  // overlay lines (smcAnnotations, further down) share ONE detection pass over
  // the full history — they can never disagree. Runs only when an overlay is on.
  const anySmcOverlayActive = smcOverlays.size > 0;
  // DWLF-447: the SMC library's own events the zone overlays don't draw (range entries, pools),
  // one full-history pass, re-run only when its inputs change.
  const smcExtraDetected = useMemo(() => {
    if (!priceCandlesFull || priceCandlesFull.length < 50) {
      return [];
    }
    try {
      return smcExtraEvents(SMC, {
        fullCandles: priceCandlesFull, options: smcOptionsFor(normalizedTimeframeKey), overlays: smcOverlays,
      });
    } catch (error) {
      console.error('DWLFMarketChart: Failed to compute SMC events', error);
      return [];
    }
  }, [priceCandlesFull, normalizedTimeframeKey, smcOverlays]);

  const smcDetected = useMemo(() => {
    if (!anySmcOverlayActive || !priceCandlesFull || priceCandlesFull.length < 50) {
      return null;
    }
    try {
      return SMC.detectSMC(priceCandlesFull, smcOptionsFor(normalizedTimeframeKey));
    } catch (err) {
      return null;
    }
  }, [anySmcOverlayActive, priceCandlesFull, normalizedTimeframeKey]);

  // The swing lookback the chart's swing and v1 trendline detectors use on this
  // timeframe, and whether this timeframe's trendlines (lines, break markers
  // and break lines) are on. One copy, read by every series that must agree.
  const timeframeSwingLookback = resolvedSwingLookbacks[normalizedTimeframeKey]
    ?? DEFAULT_SWING_LOOKBACKS[normalizedTimeframeKey]
    ?? DEFAULT_SWING_LOOKBACKS.daily;
  const timeframeTrendlinesShown = (() => {
    const lower = (normalizedTimeframeKey || '').toLowerCase();
    if (lower === 'hourly') {
      return localShowHourlyTrendlines;
    }
    if (lower === 'weekly') {
      return localShowWeeklyTrendlines;
    }
    return localShowDailyTrendlines;
  })();

  // One v1 trendline detection per input, shared by the break markers and the
  // break lines drawn under them (DWLF-330). Trendline v1 is the only path;
  // v2 is still in @dwlf/indicators but isn't surfaced here.
  const trendlineEvents = useMemo(() => {
    if (!eventsOn('trendlines') || !timeframeTrendlinesShown || priceCandles.length === 0) {
      return [];
    }
    try {
      return TrendlineV1.detectEvents(priceCandles, { swingLookback: timeframeSwingLookback });
    } catch (error) {
      console.error('DWLFMarketChart: Failed to compute trendline events', error);
      return [];
    }
  }, [eventsOn, timeframeTrendlinesShown, priceCandles, timeframeSwingLookback]);

  const indicatorEventLayers = useMemo(() => {
    if (!showIndicatorEvents || priceCandles.length === 0) {
      return { markerSeries: [], groups: [] };
    }

    const candles = priceCandles;
    const candleTimes = candles.map((candle) => candle.t);

    const findCandleByIndex = (index) => {
      if (!Number.isFinite(index)) {
        return null;
      }
      const safeIndex = Math.max(0, Math.min(candles.length - 1, Math.round(index)));
      return candles[safeIndex] || null;
    };

    const findNearestCandle = (timestamp) => {
      if (!Number.isFinite(timestamp) || candles.length === 0) {
        return null;
      }
      let left = 0;
      let right = candleTimes.length - 1;
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const midTime = candleTimes[mid];
        if (midTime === timestamp) {
          return candles[mid];
        }
        if (midTime < timestamp) {
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }
      const candidates = [];
      if (left >= 0 && left < candles.length) {
        candidates.push(candles[left]);
      }
      if (right >= 0 && right < candles.length) {
        candidates.push(candles[right]);
      }
      if (candidates.length === 0) {
        return null;
      }
      candidates.sort((a, b) => Math.abs(a.t - timestamp) - Math.abs(b.t - timestamp));
      return candidates[0];
    };

    const fallbackPriceFromCandle = (candle) => {
      if (!candle) {
        return null;
      }
      const candidates = [
        candle.c,
        candle.close,
        candle.Close,
        candle.h,
        candle.high,
        candle.l,
        candle.low,
      ];
      for (const candidate of candidates) {
        const numeric = Number(candidate);
        if (Number.isFinite(numeric)) {
          return numeric;
        }
      }
      return null;
    };

    const resolveEventTimestamp = (event) => {
      if (!event) {
        return null;
      }
      if (Number.isFinite(event.t)) {
        return Number(event.t);
      }
      if (Number.isFinite(event.index)) {
        const candle = findCandleByIndex(event.index);
        return candle?.t ?? null;
      }
      const detailIndex = event.payload?.detail?.index;
      if (Number.isFinite(detailIndex)) {
        const candle = findCandleByIndex(detailIndex);
        return candle?.t ?? null;
      }
      if (event.candle) {
        if (Number.isFinite(event.candle.t)) {
          return Number(event.candle.t);
        }
        if (event.candle.date) {
          const parsed = new Date(event.candle.date).getTime();
          if (Number.isFinite(parsed)) {
            return parsed;
          }
        }
      }
      return null;
    };

    const resolveEventPrice = (event, candle) => {
      const payload = event?.payload || {};
      const candidates = [
        payload.price,
        payload.breakPrice,
        payload.swingPrice,
        payload.currentPrice,
        payload.previousPrice,
        payload.level,
        payload.bandValue,
        payload.breakoutPrice,
        payload.closingPrice,
        payload.detail?.close,
        payload.detail?.linePrice,
        payload.detail?.breach?.close,
        event?.candle?.c,
        event?.candle?.close,
        candle?.c,
      ];
      for (const candidate of candidates) {
        const numeric = Number(candidate);
        if (Number.isFinite(numeric)) {
          return numeric;
        }
      }
      return fallbackPriceFromCandle(candle);
    };

    const eventGroups = new Map();
    const addEventToGroup = (event, meta = {}) => {
      if (!event) {
        return;
      }
      const timestamp = resolveEventTimestamp(event);
      if (!Number.isFinite(timestamp)) {
        return;
      }
      const anchorCandle = findNearestCandle(timestamp);
      const price = resolveEventPrice(event, anchorCandle);
      if (!Number.isFinite(price)) {
        return;
      }
      const anchorTimestamp = Number(anchorCandle?.t);
      const key = Number.isFinite(anchorTimestamp) ? anchorTimestamp : Math.round(timestamp);
      const payload = {
        event,
        meta,
        price,
      };
      if (eventGroups.has(key)) {
        eventGroups.get(key).events.push(payload);
        return;
      }
      eventGroups.set(key, {
        timestamp,
        price,
        events: [payload],
      });
    };

    const registerEvents = (events, meta) => {
      if (!Array.isArray(events) || events.length === 0) {
        return;
      }
      events.forEach((event) => addEventToGroup(event, meta));
    };

    if (showSwings && eventsOn('swings')) {
      const lookback = timeframeSwingLookback;
      try {
        const events = Swing.detectEvents(candles, { lookback, sweepBars: resolvedSwingSweepBars });
        registerEvents(events, { ...INDICATOR_EVENT_META.swing, displayName: 'Swing' });
      } catch (error) {
        console.error('DWLFMarketChart: Failed to compute swing events', error);
      }
    }

    if (showDSS && eventsOn('dss')) {
      try {
        const events = DSS.detectEvents(candles, resolvedDssParams);
        registerEvents(events, { ...INDICATOR_EVENT_META.dss, displayName: 'DSS' });
      } catch (error) {
        console.error('DWLFMarketChart: Failed to compute DSS events', error);
      }
    }

    if (showADX && eventsOn('adx')) {
      try {
        registerEvents(adxEvents(ADX, {
          fullCandles: priceCandlesFull, from: candles[0].t, to: candles[candles.length - 1].t,
        }), { ...INDICATOR_EVENT_META.adx, displayName: 'ADX' });
      } catch (error) {
        console.error('DWLFMarketChart: Failed to compute ADX events', error);
      }
    }

    if (showRSI && eventsOn('rsi')) {
      try {
        registerEvents(rsiEvents(RSI, {
          fullCandles: priceCandlesFull, from: candles[0].t, to: candles[candles.length - 1].t,
        }), { ...INDICATOR_EVENT_META.rsi, displayName: 'RSI' });
      } catch (error) {
        console.error('DWLFMarketChart: Failed to compute RSI events', error);
      }
    }

    // DWLF-338: stored divergences at confirmed cycle pivots, on the bar each was confirmed,
    // coloured by direction like their lines.
    const shownDivergences = (divergenceEvents || []).filter((event) => (
      shownDivergenceOscillators.has(divergenceOscillator(event.id))
      && event.t >= candles[0].t && event.t <= candles[candles.length - 1].t
    ));
    ['bullish', 'bearish'].forEach((direction) => {
      registerEvents(shownDivergences.filter((event) => divergenceDirection(event.id) === direction), {
        color: DIVERGENCE_COLORS[direction], label: 'Divergence', displayName: 'Divergence',
      });
    });

    if (showATR && eventsOn('atr')) {
      try {
        registerEvents(atrRegimeEvents(ATR, {
          fullCandles: priceCandlesFull, from: candles[0].t, to: candles[candles.length - 1].t,
        }), { ...INDICATOR_EVENT_META.atr, displayName: 'ATR' });
      } catch (error) {
        console.error('DWLFMarketChart: Failed to compute ATR events', error);
      }
    }

    if (showBollinger && eventsOn('bollinger')) {
      try {
        // Over the full series and clipped, as the squeeze shading is, so markers and shading agree.
        const events = Bollinger.detectEvents(priceCandlesFull, resolvedBollingerParams)
          .filter((event) => event.t >= candles[0].t && event.t <= candles[candles.length - 1].t);
        registerEvents(events, { ...INDICATOR_EVENT_META.bollinger, displayName: 'Bollinger Bands' });
      } catch (error) {
        console.error('DWLFMarketChart: Failed to compute Bollinger events', error);
      }
    }

    if (vwapAnchors.size > 0 && eventsOn('vwap')) {
      // Detect over the full candle history so every period anchors
      // from its true open, then keep only the events inside the
      // visible window — mirrors the VWAP overlay memo. One pass per
      // active anchor.
      const visibleStart = candles.length ? candles[0].t : null;
      const visibleEnd = candles.length ? candles[candles.length - 1].t : null;
      for (const option of VWAP_ANCHOR_OPTIONS) {
        if (!vwapAnchors.has(option.key)) {
          continue;
        }
        try {
          const events = VWAP.detectEvents(priceCandlesFull, { anchor: option.key });
          const visibleEvents = (visibleStart !== null && visibleEnd !== null)
            ? events.filter((event) => event.t >= visibleStart && event.t <= visibleEnd)
            : [];
          registerEvents(visibleEvents, { ...INDICATOR_EVENT_META.vwap, displayName: option.label });
        } catch (error) {
          console.error(`DWLFMarketChart: Failed to compute VWAP events (${option.key})`, error);
        }
      }
    }

    SMA_OPTIONS.forEach(({ period, color, label }) => {
      if (!activeSMAs.has(period) || !eventsOn('movingAverages')) {
        return;
      }
      try {
        const events = SMA.detectEvents(candles, { length: period, source: 'close' });
        registerEvents(events, { color, displayName: label });
      } catch (error) {
        console.error(`DWLFMarketChart: Failed to compute SMA ${period} events`, error);
      }
    });

    if (eventsOn('movingAverages')) {
      try {
        registerEvents(goldenDeathCrossEvents(SMA, {
          fullCandles: priceCandlesFull, activeSMAs, from: candles[0].t, to: candles[candles.length - 1].t,
        }), CROSSOVER_META);
      } catch (error) {
        console.error('DWLFMarketChart: Failed to compute golden/death crosses', error);
      }
      // DWLF-448: the close-confirmed reclaim / loss of each key average, in its colour.
      try {
        smaReclaimEvents(SMA, {
          fullCandles: priceCandlesFull, activeSMAs, from: candles[0].t, to: candles[candles.length - 1].t,
        }).forEach(({ period, events }) => {
          const option = SMA_OPTIONS.find((entry) => entry.period === period);
          registerEvents(events, { color: option?.color, displayName: `${option?.label ?? `SMA ${period}`} reclaim / loss` });
        });
      } catch (error) {
        console.error('DWLFMarketChart: Failed to compute SMA reclaim / loss', error);
      }
    }

    EMA_OPTIONS.forEach(({ period, color, label }) => {
      if (!activeEMAs.has(period) || !eventsOn('movingAverages')) {
        return;
      }
      try {
        const events = EMA.detectEvents(candles, { length: period, source: 'close' });
        registerEvents(events, { color, displayName: label });
      } catch (error) {
        console.error(`DWLFMarketChart: Failed to compute EMA ${period} events`, error);
      }
    });

    if (showEMACloud && eventsOn('emaCloud')) {
      const emaCloudLengths = activeEMAs.size >= 2
        ? Array.from(activeEMAs).sort((a, b) => a - b)
        : DEFAULT_EMA_CLOUD_LENGTHS;
      try {
        const events = EMACloud.detectEvents(candles, { lengths: emaCloudLengths });
        registerEvents(events, { color: '#6C63FF', displayName: 'EMA Cloud' });
      } catch (error) {
        console.error('DWLFMarketChart: Failed to compute EMA Cloud events', error);
      }
    }

    registerEvents(trendlineEvents, {
      ...INDICATOR_EVENT_META.trendline,
      displayName: 'Trendline',
    });

    // Key level breaks, flips, reclaims and rejections (DWLF-333), stored nightly
    // against the same levels drawn as guides.
    if (showSR && eventsOn('sr')) {
      registerEvents(keyLevelEventsInRange(keyLevelEvents, candles), { ...INDICATOR_EVENT_META.keyLevels, displayName: 'Key Levels' });
    }

    // DWLF-447: what the platform already stores or detects, drawn with its indicator.
    const visibleFrom = candles[0].t;
    const visibleTo = candles[candles.length - 1].t;
    // Cycle-fib retraces, golden pocket, held / rejected, invalidated and extensions (stored nightly).
    if (showFib && eventsOn('fib')) {
      registerEvents(keyLevelEventsInRange(cycleFibEvents, candles), { ...INDICATOR_EVENT_META.cycleFib, displayName: 'Cycle Fib' });
    }
    // SMC premium / discount / equilibrium entries and liquidity pools (smcExtraDetected).
    if (eventsOn('smc')) {
      registerEvents(latestPerId(smcExtraDetected, { from: visibleFrom, to: visibleTo, maxPerId: SMC_MAX_PER_FAMILY }), {
        ...INDICATOR_EVENT_META.smc, displayName: 'SMC',
      });
    }
    // Where each Weinstein stage became knowable (the page's stage read), with the stage band.
    if (showStages && eventsOn('stages')) {
      registerEvents(weinsteinStageEvents(weinsteinStage, { from: visibleFrom, to: visibleTo }), {
        ...INDICATOR_EVENT_META.weinstein, displayName: 'Weinstein Stage',
      });
    }

    // Cycle markers render for whatever timeframe the user is on. Detection
    // is done per-timeframe upstream in marketDetailPage using chartCandles;
    // the prop names keep "weekly" for now purely as legacy — renaming them
    // is a Phase 2 concern once the settings schema is refactored.
    if (showLiveWeeklyCycleLows && eventsOn('liveCycleLows') && Array.isArray(liveCycleLowEvents) && liveCycleLowEvents.length > 0) {
      registerEvents(liveCycleLowEvents, {
        color: '#0ea5e9',
        displayName: 'Cycle Low (Live)',
      });
    }
    if (showLiveWeeklyCycleHighs && eventsOn('liveCycleHighs') && Array.isArray(liveCycleHighEvents) && liveCycleHighEvents.length > 0) {
      registerEvents(liveCycleHighEvents, {
        color: '#f97316',
        displayName: 'Cycle High (Live)',
      });
    }
    if (showWeeklyCycleLows && eventsOn('cycleLows') && Array.isArray(weeklyCycleLows) && weeklyCycleLows.length > 0) {
      const events = weeklyCycleLows.map((pivot) => ({
        id: 'cycle.low.confirmed',
        name: 'Confirmed Cycle Low',
        description: 'Cycle low confirmed.',
        // Position the top-row event marker at the bar where the
        // signal was actually emitted (`emittedAt` = source/anchor
        // timestamp the cron ran at), not the pivot bar — placing
        // it at the pivot would imply we knew at the time, which is
        // never true. The "CL N" triangle below the candle remains
        // anchored to the pivot (where the low actually printed),
        // so the spatial story stays clear: triangle = where the low
        // was, top dot = when we said so.
        t: Number.isFinite(pivot.emittedAt) ? pivot.emittedAt
          : Number.isFinite(pivot.confirmTime) ? pivot.confirmTime
          : pivot.pivotTime,
        index: Number.isFinite(pivot.confirmIndex) ? pivot.confirmIndex : pivot.pivotIndex,
        payload: {
          price: pivot.low,
          pivotIndex: pivot.pivotIndex,
          pivotTime: pivot.pivotTime,
          confirmIndex: pivot.confirmIndex,
          confirmTime: pivot.confirmTime,
          emittedAt: pivot.emittedAt,
          totalScore: pivot.totalScore,
        },
      }));
      registerEvents(events, { color: '#22c55e', displayName: 'Cycle Low (Confirmed)' });
    }
    if (showWeeklyCycleHighs && eventsOn('cycleHighs') && Array.isArray(weeklyCycleHighs) && weeklyCycleHighs.length > 0) {
      const events = weeklyCycleHighs.map((pivot) => ({
        id: 'cycle.high.confirmed',
        name: 'Confirmed Cycle High',
        description: 'Cycle high confirmed.',
        // See cycle-low equivalent above for the pivot-vs-emitted rationale.
        t: Number.isFinite(pivot.emittedAt) ? pivot.emittedAt
          : Number.isFinite(pivot.confirmTime) ? pivot.confirmTime
          : pivot.pivotTime,
        index: Number.isFinite(pivot.confirmIndex) ? pivot.confirmIndex : pivot.pivotIndex,
        payload: {
          price: pivot.high,
          pivotIndex: pivot.pivotIndex,
          pivotTime: pivot.pivotTime,
          confirmIndex: pivot.confirmIndex,
          confirmTime: pivot.confirmTime,
          emittedAt: pivot.emittedAt,
          totalScore: pivot.totalScore,
        },
      }));
      registerEvents(events, { color: '#ef4444', displayName: 'Cycle High (Confirmed)' });
    }

    // SMC structure breaks (BOS / ChoCH). Same detection pass as the overlay
    // lines (smcDetected, full history), so a top-row marker always matches a
    // line on the chart. Only the last N breaks per family are surfaced, mirroring
    // the overlay's slice.
    if (smcOverlays.has('structure') && eventsOn('smc') && smcDetected?.structureBreaks?.all?.length) {
      const events = smcDetected.structureBreaks.all
        .slice(-SMC_MAX_PER_FAMILY)
        .map((b) => {
          const kind = b.type === 'choch' ? 'ChoCH' : 'BOS';
          const dir = b.direction === 'bullish' ? 'Bullish' : 'Bearish';
          const level = Number.isFinite(b.swingLevel) ? b.swingLevel.toFixed(2) : b.swingLevel;
          const side = b.direction === 'bullish' ? 'high' : 'low';
          const cross = b.direction === 'bullish' ? 'above' : 'below';
          return {
            id: `smc.${b.type}.${b.direction}`,
            name: `${dir} ${kind}`,
            description: `${dir} ${kind}: close ${cross} the protected ${side} at ${level}.`,
            t: b.breakTime,
            index: b.breakIndex,
            payload: {
              price: b.breakPrice,
              level: b.swingLevel,
              swingTime: b.swingTime,
            },
          };
        });
      registerEvents(events, { color: '#FF9800', displayName: 'BOS / ChoCH' });
    }

    // SMC FVG lifecycle markers — fills + tests (NOT formations: gaps are far
    // more frequent than structure breaks, so a marker per formation would flood
    // the band, and the box already shows formation). Live from the same
    // smcDetected pass; each bounded to the most recent SMC_MAX_PER_FAMILY, like
    // the structure block above.
    if (smcOverlays.has('fvg') && eventsOn('smc') && smcDetected?.fairValueGaps?.all?.length) {
      const gaps = smcDetected.fairValueGaps.all;
      const zoneStr = (f) => `${f.zone.bottom.toFixed(2)}–${f.zone.top.toFixed(2)}`;
      // Only surface a marker if the gap's BOX is currently drawn — active
      // (extends to the live edge) or a ghost still inside the ghost window.
      // Stops a marker outliving its zone. (The event itself stays in the events
      // table for backtests/strategies; this is purely the chart view.)
      const smcLastIdx = priceCandlesFull.length - 1;
      const fvgRendered = (f) => fvgIsLive(f)
        || (fvgFill(f) !== null && smcLastIdx - fvgFill(f).index <= FVG_FILLED_GHOST_BARS);
      const tested = gaps
        .filter((f) => Number.isInteger(f.testedIndex) && Number.isFinite(f.testedTime))
        .filter(fvgRendered)
        .sort((a, b) => a.testedTime - b.testedTime)
        .slice(-SMC_MAX_PER_FAMILY)
        .map((f) => ({
          id: 'smc.fvg.tested',
          name: 'FVG Tested',
          description: `Price tested the ${f.type} FVG (${zoneStr(f)}).`,
          t: f.testedTime,
          index: f.testedIndex,
          payload: { price: f.zone.center },
        }));
      registerEvents(tested, { color: '#26A69A', displayName: 'FVG Tested' });

      // Filled = a close through the far edge (indicators 16, DWLF-336), the
      // bar the stored `smc.fvg.filled` event falls on.
      const filled = gaps
        .filter((f) => fvgFill(f) !== null)
        .filter(fvgRendered)
        .sort((a, b) => fvgFill(a).time - fvgFill(b).time)
        .slice(-SMC_MAX_PER_FAMILY)
        .map((f) => ({
          id: 'smc.fvg.filled',
          name: 'FVG Filled',
          description: `Price filled the ${f.type} FVG (${zoneStr(f)}).`,
          t: fvgFill(f).time,
          index: fvgFill(f).index,
          payload: { price: f.zone.center },
        }));
      registerEvents(filled, { color: '#90A4AE', displayName: 'FVG Filled' });
    }

    // SMC order-block lifecycle markers — tested (price tapped the zone),
    // mitigated (closed through), breaker (flipped). Not "formed" (the box shows
    // that). Each bounded to recent SMC_MAX_PER_FAMILY, gated by the OB overlay +
    // Indicator Events, mirroring the FVG markers above.
    if (smcOverlays.has('orderBlocks') && eventsOn('smc') && smcDetected?.orderBlocks?.all?.length) {
      const blocks = smcDetected.orderBlocks.all;
      const obZone = (o) => `${o.zone.bottom.toFixed(2)}–${o.zone.top.toFixed(2)}`;
      // Only surface a marker if the block's BOX is currently drawn — live
      // (active/tested, extends) or a ghost still inside the ghost window. Stops
      // a marker outliving its zone. (The event stays in the DB; chart view only.)
      const obLastIdx = priceCandlesFull.length - 1;
      const obRendered = (o) => (o.state === 'active' || o.state === 'tested')
        || ((o.state === 'mitigated' || o.state === 'breaker')
          && Number.isInteger(o.mitigationIndex) && obLastIdx - o.mitigationIndex <= FVG_FILLED_GHOST_BARS);
      const obTested = blocks
        .filter((o) => Number.isInteger(o.testedIndex) && Number.isFinite(o.testedTime))
        .filter(obRendered)
        .sort((a, b) => a.testedTime - b.testedTime)
        .slice(-SMC_MAX_PER_FAMILY)
        .map((o) => ({
          id: 'smc.orderBlock.tested',
          name: 'Order Block Tested',
          description: `Price tested the ${o.type} order block (${obZone(o)}).`,
          t: o.testedTime,
          index: o.testedIndex,
          payload: { price: o.zone.center },
        }));
      registerEvents(obTested, { color: '#42A5F5', displayName: 'OB Tested' });

      const obMitigated = blocks
        .filter((o) => o.state === 'mitigated' && Number.isInteger(o.mitigationIndex) && Number.isFinite(o.mitigationTime))
        .filter(obRendered)
        .sort((a, b) => a.mitigationTime - b.mitigationTime)
        .slice(-SMC_MAX_PER_FAMILY)
        .map((o) => ({
          id: 'smc.orderBlock.mitigated',
          name: 'Order Block Mitigated',
          description: `Price closed through the ${o.type} order block (${obZone(o)}).`,
          t: o.mitigationTime,
          index: o.mitigationIndex,
          payload: { price: o.zone.center },
        }));
      registerEvents(obMitigated, { color: '#90A4AE', displayName: 'OB Mitigated' });

      const obBreaker = blocks
        .filter((o) => o.state === 'breaker' && Number.isInteger(o.breakerIndex) && Number.isFinite(o.breakerTime))
        .filter(obRendered)
        .sort((a, b) => a.breakerTime - b.breakerTime)
        .slice(-SMC_MAX_PER_FAMILY)
        .map((o) => ({
          id: 'smc.orderBlock.breaker',
          name: 'Breaker Block',
          description: `The ${o.type} order block (${obZone(o)}) flipped into a breaker.`,
          t: o.breakerTime,
          index: o.breakerIndex,
          payload: { price: o.zone.center },
        }));
      registerEvents(obBreaker, { color: '#AB47BC', displayName: 'Breaker Block' });
    }

    // SMC liquidity sweep markers — a pool was raided (wick through the equal
    // highs/lows) then rejected. Bounded to recent SMC_MAX_PER_FAMILY, gated by
    // the Liquidity overlay + Indicator Events. The pools themselves render as
    // lines (see smcAnnotations); these mark the actionable raid-and-reject.
    if (smcOverlays.has('liquidity') && eventsOn('smc') && smcDetected?.liquidity?.all?.length) {
      const liqLastIdx = priceCandlesFull.length - 1;
      const sweeps = smcDetected.liquidity.all
        .filter((p) => p.state === 'swept' && Number.isInteger(p.sweptIndex) && Number.isFinite(p.sweptTime))
        // only while the pool line is still on the chart (recent ghost window)
        .filter((p) => liqLastIdx - p.sweptIndex <= FVG_FILLED_GHOST_BARS)
        .sort((a, b) => a.sweptTime - b.sweptTime)
        .slice(-SMC_MAX_PER_FAMILY)
        .map((p) => {
          const bsl = p.type === 'buy_side';
          return {
            // BSL swept = bearish reversal signal; SSL swept = bullish.
            id: bsl ? 'smc.liquidity.sweep.bearish' : 'smc.liquidity.sweep.bullish',
            name: bsl ? 'Buy-Side Liquidity Swept' : 'Sell-Side Liquidity Swept',
            description: `Price raided the ${bsl ? 'buy' : 'sell'}-side pool @ ${p.level.toFixed(2)} (wick ${p.sweptExtreme?.toFixed(2) ?? '?'}) and rejected.`,
            t: p.sweptTime,
            index: p.sweptIndex,
            payload: { price: p.sweptExtreme ?? p.level, level: p.level },
          };
        });
      registerEvents(sweeps, { color: '#9C27B0', displayName: 'Liquidity Sweep' });
    }

    if (!eventGroups.size) {
      return { markerSeries: [], groups: [] };
    }

    const sortedGroups = Array.from(eventGroups.values()).sort((a, b) => a.timestamp - b.timestamp);

    // Markers render in the lower row of the shared "events" pane
    // (v: 0.3 — see the customEventLayers comment for the full row
    // layout). Custom events occupy v: 0.7 in the upper row. Each dot
    // gets the muted variant of its first event's colour, with a +N
    // cluster badge if multiple indicator events fired on the same
    // candle.
    const INDICATOR_EVENT_ROW_Y = 0.3;
    const markerData = [];

    sortedGroups.forEach((group) => {
      // Cluster groups still pick the first event's colour as the dot
      // tint — the breakdown is visible in the hover tooltip. The old
      // "always yellow when clustered" behaviour was lossy.
      const baseColor = (group.events.length > 0 && group.events[0].meta?.color)
        || '#FBC02D';
      const markerColor = MUTED_EVENT_COLORS[baseColor] || baseColor;
      const isCluster = group.events.length > 1;

      markerData.push({
        t: group.timestamp,
        v: INDICATOR_EVENT_ROW_Y,
        color: markerColor,
        label: isCluster ? `+${group.events.length - 1}` : '',
        size: 5,
        shape: 'circle',
        offsetY: 0,
        fontSize: 9,
        textOffsetY: -8,
      });
    });

    const markerSeries = markerData.length
      ? [{
          key: 'Indicator Events',
          type: 'marker',
          data: markerData.map((item) => ({
            t: item.t,
            v: item.v,
            color: item.color,
            label: item.label,
            size: item.size,
            shape: item.shape,
            offsetY: item.offsetY,
            fontSize: item.fontSize,
            textOffsetY: item.textOffsetY,
          })),
          // Premium marker styling — same hollow ring + halo treatment
          // as custom events. See customEventLayers for the rationale.
          style: {
            markerShape: 'circle',
            markerSize: 5,
            markerOffsetY: 0,
            markerVariant: 'outline',
            markerStrokeWidth: 1,
            markerHaloSize: 3,
            markerHaloOpacity: 0.2,
          },
        }]
      : [];

    return {
      markerSeries,
      groups: sortedGroups.map((group) => ({
        ...group,
        summaries: buildIndicatorEventSummaries(group),
      })),
    };
  }, [
    showIndicatorEvents,
    eventsOn,
    priceCandles,
    showSwings,
    resolvedSwingSweepBars,
    showDSS,
    resolvedDssParams,
    showATR,
    showRSI,
    showADX,
    divergenceEvents,
    shownDivergenceOscillators,
    showBollinger,
    resolvedBollingerParams,
    vwapAnchors,
    priceCandlesFull,
    showEMACloud,
    activeSMAs,
    activeEMAs,
    timeframeSwingLookback,
    trendlineEvents,
    showSR,
    keyLevelEvents,
    cycleFibEvents,
    weinsteinStage,
    showStages,
    smcExtraDetected,
    showFib,
    liveCycleLowEvents,
    liveCycleHighEvents,
    weeklyCycleLows,
    weeklyCycleHighs,
    showWeeklyCycleLows,
    showWeeklyCycleHighs,
    showLiveWeeklyCycleLows,
    showLiveWeeklyCycleHighs,
    smcOverlays,
    smcDetected,
  ]);

  // Helper: determine color for a custom event based on tags/name/characteristics
  const getEventColor = useCallback((event) => {
    // Check if event has explicit color field
    if (event?.color) {
      return event.color;
    }

    // Find event definition and check for color there
    const eventId = event?.customEventId ?? event?.eventId ?? event?.id;
    const definition = customEventDefinitions.find((def) => {
      const defId = def?.eventId ?? def?.id ?? def?.customEventId;
      return defId === eventId;
    });

    if (definition?.color) {
      return definition.color;
    }

    // Infer color from tags or name
    const tags = event?.tags || definition?.tags || [];
    const name = (event?.eventName || event?.name || definition?.eventName || definition?.name || '').toLowerCase();

    // Bullish indicators: green palette
    if (
      tags.includes('bullish') ||
      name.includes('bull') ||
      name.includes('buy') ||
      name.includes('long') ||
      name.includes('breakout') ||
      name.includes('higher')
    ) {
      return '#22c55e'; // green
    }

    // Bearish indicators: red palette
    if (
      tags.includes('bearish') ||
      name.includes('bear') ||
      name.includes('sell') ||
      name.includes('short') ||
      name.includes('breakdown') ||
      name.includes('lower')
    ) {
      return '#ef4444'; // red
    }

    // Default: blue/purple for neutral events
    return '#3b82f6'; // blue
  }, [customEventDefinitions]);

  const customEventLayers = useMemo(() => {
    if (!showCustomEvents || priceCandles.length === 0) {
      return { markerSeries: [], groups: [] };
    }

    const filteredEvents = Array.isArray(customEvents)
      ? customEvents.filter((event) => {
        const eventId =
          event?.customEventId
          ?? event?.eventId
          ?? event?.id;
        return eventId && customEventSelections.has(eventId);
      })
      : [];

    if (filteredEvents.length === 0) {
      return { markerSeries: [], groups: [] };
    }

    const candles = priceCandles;
    const candleTimes = candles.map((candle) => candle.t);

    const findNearestCandle = (timestamp) => {
      if (!Number.isFinite(timestamp) || candles.length === 0) {
        return null;
      }
      let left = 0;
      let right = candleTimes.length - 1;
      while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const midTime = candleTimes[mid];
        if (midTime === timestamp) {
          return candles[mid];
        }
        if (midTime < timestamp) {
          left = mid + 1;
        } else {
          right = mid - 1;
        }
      }
      const candidates = [];
      if (left >= 0 && left < candles.length) {
        candidates.push(candles[left]);
      }
      if (right >= 0 && right < candles.length) {
        candidates.push(candles[right]);
      }
      if (candidates.length === 0) {
        return null;
      }
      candidates.sort((a, b) => Math.abs(a.t - timestamp) - Math.abs(b.t - timestamp));
      return candidates[0];
    };

    const resolveEventTimestamp = (event) => {
      if (!event) {
        return null;
      }
      const raw = event.eventTimestamp || event.date || event.timestamp || event.time;
      if (!raw && raw !== 0) {
        return null;
      }
      const parsed = typeof raw === 'number' ? raw : new Date(raw).getTime();
      return Number.isFinite(parsed) ? parsed : null;
    };

    const fallbackPriceFromCandle = (candle) => {
      if (!candle) {
        return null;
      }
      const candidates = [
        candle.c,
        candle.close,
        candle.Close,
        candle.h,
        candle.high,
        candle.l,
        candle.low,
      ];
      for (const candidate of candidates) {
        const numeric = Number(candidate);
        if (Number.isFinite(numeric)) {
          return numeric;
        }
      }
      return null;
    };

    const buildSummary = (event) => {
      const eventId =
        event?.customEventId
        ?? event?.eventId
        ?? event?.id;
      const name =
        event?.eventName
        || (eventId ? customEventNameMap.get(eventId) : null)
        || 'Custom Event';
      const timeframeLabel = event?.timeframe ? ` (${String(event.timeframe).toUpperCase()})` : '';
      return `${name}${timeframeLabel}`;
    };

    const eventGroups = new Map();
    filteredEvents.forEach((event) => {
      const timestamp = resolveEventTimestamp(event);
      if (!Number.isFinite(timestamp)) {
        return;
      }
      const anchorCandle = findNearestCandle(timestamp);
      const price = fallbackPriceFromCandle(anchorCandle);
      if (!Number.isFinite(price)) {
        return;
      }
      const anchorTimestamp = Number(anchorCandle?.t);
      const key = Number.isFinite(anchorTimestamp) ? anchorTimestamp : Math.round(timestamp);
      const color = getEventColor(event);
      const payload = {
        event,
        price,
        summary: buildSummary(event),
        color,
      };
      if (eventGroups.has(key)) {
        eventGroups.get(key).events.push(payload);
        return;
      }
      eventGroups.set(key, {
        timestamp,
        price,
        events: [payload],
        color, // Use first event's color for the group
      });
    });

    if (!eventGroups.size) {
      return { markerSeries: [], groups: [] };
    }

    const sortedGroups = Array.from(eventGroups.values()).sort((a, b) => a.timestamp - b.timestamp);

    // Markers are rendered in the shared "events" pane above the price
    // pane (hideYAxis: true) so the candles stay completely uncovered.
    // The pane has a 0..1 fixed scale and is split into two rows:
    //   - Custom events sit on the upper row at v: 0.7
    //   - Indicator events sit on the lower row at v: 0.3
    // This keeps both event types visually grouped as a single "events
    // strip" while preventing markers of different kinds from colliding
    // on the same timestamp. Cluster groups get a small "+N" count
    // label; single events render as just a dot.
    //
    // The previous design rendered markers above each candle's high
    // price PLUS a vertical line through the candle from high to low.
    // The vertical lines were the "covers candles" complaint and the
    // chunky filled circles with flag glyphs felt unrefined. Both are
    // gone now — see notes/dwlf_charting_divergence.md / git log for
    // the design discussion.
    const CUSTOM_EVENT_ROW_Y = 0.7;
    const markerData = [];

    sortedGroups.forEach((group) => {
      // Map the group's saturated event colour to its muted marker-band
      // variant (see MUTED_EVENT_COLORS at module scope for the why).
      const baseColor = group.color || '#3b82f6';
      const markerColor = MUTED_EVENT_COLORS[baseColor] || baseColor;

      // A "cluster" is multiple custom events firing on the same
      // candle. The cluster count is shown as a small "+N" label
      // next to the dot.
      const isCluster = group.events.length > 1;

      markerData.push({
        t: group.timestamp,
        v: CUSTOM_EVENT_ROW_Y,
        color: markerColor,
        label: isCluster ? `+${group.events.length - 1}` : '',
        size: 5,
        shape: 'circle',
        offsetY: 0,
        fontSize: 9,
        textOffsetY: -8,
      });
    });

    const markerSeries = markerData.length
      ? [{
          key: 'Custom Events',
          type: 'marker',
          data: markerData.map((item) => ({
            t: item.t,
            v: item.v,
            color: item.color,
            label: item.label,
            size: item.size,
            shape: item.shape,
            offsetY: item.offsetY,
            fontSize: item.fontSize,
            textOffsetY: item.textOffsetY,
          })),
          // Premium marker styling — hollow rings (markerVariant: 'outline')
          // with a subtle translucent halo behind each one. The hollow
          // ring catches the eye without filling the band visually, and
          // the halo gives a soft glow that makes the dots feel "lit"
          // rather than stamped on. Both options are independently
          // tweakable — `markerStrokeWidth`, `markerHaloSize`, and
          // `markerHaloOpacity` are the main knobs.
          //
          // These options require @dwlf/charting@>=1.4.0 (transitively
          // via @andywilliams/dwlf-charting@>=0.35.60). Earlier versions
          // silently ignore them and fall back to the previous
          // filled-circle render.
          style: {
            markerShape: 'circle',
            markerSize: 5,
            markerOffsetY: 0,
            markerVariant: 'outline',
            markerStrokeWidth: 1,
            markerHaloSize: 3,
            markerHaloOpacity: 0.2,
          },
        }]
      : [];

    return {
      markerSeries,
      groups: sortedGroups.map((group) => ({
        ...group,
        summaries: group.events.map((entry) => entry.summary),
      })),
    };
  }, [
    showCustomEvents,
    customEvents,
    customEventSelections,
    customEventNameMap,
    customEventDefinitions,
    priceCandles,
    getEventColor,
  ]);

  const tradeGuides = tradeLayers.guides;
  const tradeMarkerSeries = tradeLayers.markerSeries;
  const tradePositionSeries = tradeLayers.positionSeries;
  const userTradeGroups = tradeLayers.userTradeGroups || [];

  // Trade Journal Overlay - visualize closed trades from the trade journal
  const tradeJournalSeries = useMemo(() => {
    if (!Array.isArray(tradeJournalTrades) || tradeJournalTrades.length === 0) {
      return [];
    }

    // Parse date from linkedEventIds like "TSLA_higher_low_2025-10-10"
    const parseDateFromEventId = (eventId) => {
      if (!eventId) {return null;}
      const match = eventId.match(/(\d{4}-\d{2}-\d{2})$/);
      if (match) {
        const date = new Date(match[1]);
        return date.getTime();
      }
      return null;
    };

    // Find the candle closest to a given timestamp
    const findCandleAtTime = (timestamp) => {
      if (!priceCandles || priceCandles.length === 0) {return null;}
      let closest = null;
      let closestDiff = Infinity;
      for (const candle of priceCandles) {
        const candleTime = candle.t || candle.time || candle.date;
        if (!candleTime) {continue;}
        const diff = Math.abs(candleTime - timestamp);
        if (diff < closestDiff) {
          closestDiff = diff;
          closest = candle;
        }
      }
      // Only return if within 2 days
      return closestDiff < 2 * 86400000 ? closest : null;
    };

    const buildNoteMarkerData = (trades) => {
      const markers = [];
      trades.forEach((trade) => {
        const notesArray = trade?.notesArray;
        if (!Array.isArray(notesArray) || notesArray.length === 0) {
          return;
        }
        const entryPrice = Number(trade?.entryPrice);
        if (!Number.isFinite(entryPrice)) {
          return;
        }

        // Create a marker for each individual note
        notesArray.forEach((note) => {
          const text = note?.text || '';
          if (!text.trim()) {
            return;
          }

          // Try to get date from linkedEventIds, fall back to createdAt
          let noteTime = null;
          if (Array.isArray(note.linkedEventIds) && note.linkedEventIds.length > 0) {
            noteTime = parseDateFromEventId(note.linkedEventIds[0]);
          }
          if (!noteTime && note.createdAt) {
            noteTime = new Date(note.createdAt).getTime();
          }
          if (!Number.isFinite(noteTime)) {
            return;
          }

          // Find candle at note time and use its high price, fallback to entry price
          const candle = findCandleAtTime(noteTime);
          const notePrice = candle 
            ? (candle.h || candle.high || candle.c || candle.close || entryPrice)
            : entryPrice;

          // Truncate long notes for display
          const displayText = text.length > 200 ? text.substring(0, 200) + '...' : text;

          markers.push({
            t: noteTime,
            v: notePrice,
            tooltip: displayText,
          });
        });
      });
      return markers;
    };

    const noteMarkerData = buildNoteMarkerData(tradeJournalTrades);
    let series = [];

    // Use TradeJournalOverlay helper if available, otherwise build series manually
    if (typeof TradeJournalOverlay?.toSeries === 'function') {
      series = TradeJournalOverlay.toSeries(tradeJournalTrades) || [];
    }

    if (series.length === 0) {
      // Manual series generation for trade journal trades
      const positionData = [];
      const entryMarkerData = [];

      tradeJournalTrades.forEach((trade) => {
        const entryTime = new Date(trade.entryTime).getTime();
        const exitTime = new Date(trade.exitTime).getTime();
        const entryPrice = Number(trade.entryPrice);
        const exitPrice = Number(trade.exitPrice);
        const side = (trade.side || 'long').toLowerCase();
        const pnl = Number(trade.pnl) || 0;

        if (!Number.isFinite(entryTime) || !Number.isFinite(exitTime)) {return;}
        if (!Number.isFinite(entryPrice) || !Number.isFinite(exitPrice)) {return;}

        const isProfit = pnl >= 0;
        const isLong = side === 'long' || side === 'buy';

        // Build message text (notes are shown as separate markers now)
        const messageText = `${isLong ? 'Long' : 'Short'} | P&L: ${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}`;

        // Entry marker (▲ for long, ▼ for short)
        entryMarkerData.push({
          t: entryTime,
          v: entryPrice,
          label: isLong ? '▲' : '▼',
          color: isLong ? '#4CAF50' : '#F44336',
          tooltip: `${isLong ? 'Long' : 'Short'} entry @ ${entryPrice.toFixed(2)}`,
        });

        // Position line connecting entry to exit
        positionData.push({
          start: entryTime,
          end: exitTime,
          entry: entryPrice,
          target: exitPrice,
          stop: entryPrice, // Not used for closed trades, but required
          riskColor: 'transparent',
          rewardColor: isProfit
            ? 'rgba(76, 175, 80, 0.15)'
            : 'rgba(244, 67, 54, 0.15)',
          messages: [{
            t: exitTime,
            v: exitPrice,
            text: messageText,
          }],
          bubbleColor: 'rgba(0,0,0,0.85)',
          textColor: isProfit ? '#4CAF50' : '#F44336',
          fontSize: 10,
          padding: 6,
        });
      });

      // Add entry markers
      if (entryMarkerData.length > 0) {
        series.push({
          key: 'tradeJournalEntries',
          type: 'marker',
          data: entryMarkerData,
          style: {
            fontSize: 16,
            markerOffsetY: 0,
          },
        });
      }

      if (positionData.length > 0) {
        series.push({
          key: 'tradeJournalPositions',
          type: 'position',
          data: positionData,
        });
      }
    }

    // Add individual note markers (emoji only, no background shape, clickable tooltip)
    if (noteMarkerData.length > 0) {
      series.push({
        key: 'tradeJournalNotes',
        type: 'marker',
        data: noteMarkerData.map((n) => ({
          t: n.t,
          v: n.v,
          label: '📝',
          shape: 'none',
          size: 0,
          offsetY: -15,
          tooltip: n.tooltip, // Click to show tooltip
          fontSize: 24,
        })),
        style: {
          markerSize: 0,
          markerShape: 'none',
          fontSize: 24,
          markerOffsetY: -15,
        },
      });
    }

    return series;
  }, [tradeJournalTrades, priceCandles]);
  const indicatorEventMarkerSeries = indicatorEventLayers.markerSeries;
  const indicatorEventGroups = indicatorEventLayers.groups || [];
  const customEventMarkerSeries = customEventLayers.markerSeries;
  const customEventGroups = customEventLayers.groups || [];
  const indicatorEventSearchTolerance = useMemo(
    () => Math.max(Number(averageCandleInterval) * 0.6 || 0, 60 * 1000),
    [averageCandleInterval],
  );
  const customEventSearchTolerance = indicatorEventSearchTolerance;

  const findNearestIndicatorEventGroup = useCallback(
    (timestamp) => {
      if (!Number.isFinite(timestamp) || indicatorEventGroups.length === 0) {
        return null;
      }
      let best = null;
      indicatorEventGroups.forEach((group) => {
        const diff = Math.abs(group.timestamp - timestamp);
        if (diff <= indicatorEventSearchTolerance && (!best || diff < best.diff)) {
          best = { diff, group };
        }
      });
      return best?.group ?? null;
    },
    [indicatorEventGroups, indicatorEventSearchTolerance],
  );

  const handleIndicatorEventCrosshair = useCallback(
    (timestamp) => {
      if (!showIndicatorEvents) {
        setActiveIndicatorEventGroup(null);
        return;
      }
      const group = findNearestIndicatorEventGroup(timestamp);
      setActiveIndicatorEventGroup(group || null);
    },
    [showIndicatorEvents, findNearestIndicatorEventGroup],
  );

  const findNearestCustomEventGroup = useCallback(
    (timestamp) => {
      if (!Number.isFinite(timestamp) || customEventGroups.length === 0) {
        return null;
      }
      let best = null;
      customEventGroups.forEach((group) => {
        const diff = Math.abs(group.timestamp - timestamp);
        if (diff <= customEventSearchTolerance && (!best || diff < best.diff)) {
          best = { diff, group };
        }
      });
      return best?.group ?? null;
    },
    [customEventGroups, customEventSearchTolerance],
  );

  const handleCustomEventCrosshair = useCallback(
    (timestamp) => {
      if (!showCustomEvents) {
        setActiveCustomEventGroup(null);
        return;
      }
      const group = findNearestCustomEventGroup(timestamp);
      setActiveCustomEventGroup(group || null);
    },
    [showCustomEvents, findNearestCustomEventGroup],
  );

  const findNearestUserTradeGroup = useCallback(
    (timestamp) => {
      if (!Number.isFinite(timestamp) || userTradeGroups.length === 0) {
        return null;
      }
      let best = null;
      userTradeGroups.forEach((group) => {
        const diff = Math.abs(group.timestamp - timestamp);
        if (diff <= customEventSearchTolerance && (!best || diff < best.diff)) {
          best = { diff, group };
        }
      });
      return best?.group ?? null;
    },
    [userTradeGroups, customEventSearchTolerance],
  );

  const handleUserTradeCrosshair = useCallback(
    (timestamp) => {
      if (!showUserTrades) {
        setActiveUserTradeGroup(null);
        return;
      }
      const group = findNearestUserTradeGroup(timestamp);
      setActiveUserTradeGroup(group || null);
    },
    [showUserTrades, findNearestUserTradeGroup],
  );

  const handleCrosshairMove = useCallback(
    (timestamp) => {
      if (showIndicatorEvents) {
        handleIndicatorEventCrosshair(timestamp);
      } else {
        setActiveIndicatorEventGroup(null);
      }
      if (showCustomEvents) {
        handleCustomEventCrosshair(timestamp);
      } else {
        setActiveCustomEventGroup(null);
      }
      if (showUserTrades) {
        handleUserTradeCrosshair(timestamp);
      } else {
        setActiveUserTradeGroup(null);
      }
      // Look up the hovered candle for the OHLC info panel. Uses a
      // ref to avoid adding priceCandles to this callback's deps.
      if (Number.isFinite(timestamp)) {
        const candles = priceCandlesRef.current;
        if (candles && candles.length > 0) {
          let lo = 0;
          let hi = candles.length - 1;
          while (lo <= hi) {
            const mid = (lo + hi) >>> 1;
            if (candles[mid].t < timestamp) {
              lo = mid + 1;
            } else if (candles[mid].t > timestamp) {
              hi = mid - 1;
            } else {
              lo = mid;
              break;
            }
          }
          const idx = Math.min(lo, candles.length - 1);
          setHoveredCandle(candles[idx] || null);
        } else {
          setHoveredCandle(null);
        }
      } else {
        setHoveredCandle(null);
      }
    },
    [
      showIndicatorEvents,
      showCustomEvents,
      showUserTrades,
      handleIndicatorEventCrosshair,
      handleCustomEventCrosshair,
      handleUserTradeCrosshair,
    ],
  );

  useEffect(() => {
    if (!showIndicatorEvents) {
      setActiveIndicatorEventGroup(null);
    }
  }, [showIndicatorEvents]);

  useEffect(() => {
    if (!showCustomEvents) {
      setActiveCustomEventGroup(null);
    }
  }, [showCustomEvents]);

  useEffect(() => {
    if (!showUserTrades) {
      setActiveUserTradeGroup(null);
    }
  }, [showUserTrades]);

  // Track pointer position over the chart container so the event
  // hover tooltips (custom events and indicator events) can follow the
  // cursor (or finger) instead of sitting in a fixed top-left card. We
  // only attach listeners while at least one of those event overlays
  // is enabled to avoid extra work elsewhere.
  //
  // Performance: setCursorPos triggers a re-render of this large
  // component, so we throttle to one update per animation frame via
  // requestAnimationFrame. This caps re-renders at ~60Hz which is well
  // above the tooltip's 80ms CSS transition.
  //
  // Touch devices: touchstart/touchmove make the tooltip work on
  // tablets/phones. The render path also falls back to a fixed
  // position when cursorPos is null, so the tooltip is always visible
  // when there's an active group regardless of input modality.
  useEffect(() => {
    if (!showCustomEvents && !showIndicatorEvents && !showUserTrades) {
      setCursorPos(null);
      return undefined;
    }
    const node = chartContainerRef.current;
    if (!node) {
      return undefined;
    }

    let pendingPos = null;
    let rafId = null;
    const flush = () => {
      rafId = null;
      if (pendingPos) {
        setCursorPos(pendingPos);
      }
    };
    const schedule = (clientX, clientY) => {
      const rect = node.getBoundingClientRect();
      pendingPos = {
        x: clientX - rect.left,
        y: clientY - rect.top,
        containerWidth: rect.width,
        containerHeight: rect.height,
      };
      if (rafId === null) {
        rafId = requestAnimationFrame(flush);
      }
    };
    const handleMove = (event) => schedule(event.clientX, event.clientY);
    const handleTouchMove = (event) => {
      const touch = event.touches && event.touches[0];
      if (!touch) {
        return;
      }
      schedule(touch.clientX, touch.clientY);
    };
    const handleLeave = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      pendingPos = null;
      setCursorPos(null);
    };

    node.addEventListener('mousemove', handleMove);
    node.addEventListener('mouseleave', handleLeave);
    node.addEventListener('touchstart', handleTouchMove, { passive: true });
    node.addEventListener('touchmove', handleTouchMove, { passive: true });
    node.addEventListener('touchend', handleLeave);
    node.addEventListener('touchcancel', handleLeave);
    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      node.removeEventListener('mousemove', handleMove);
      node.removeEventListener('mouseleave', handleLeave);
      node.removeEventListener('touchstart', handleTouchMove);
      node.removeEventListener('touchmove', handleTouchMove);
      node.removeEventListener('touchend', handleLeave);
      node.removeEventListener('touchcancel', handleLeave);
    };
  }, [showCustomEvents, showIndicatorEvents, showUserTrades]);

  useEffect(() => {
    if (showWeeklyCycleLows) {
      // eslint-disable-next-line no-console
      console.log('DWLF WCL overlay props', {
        symbol,
        timeframe,
        normalizedTimeframeKey,
        weeklyCycleLowsCount: Array.isArray(weeklyCycleLows) ? weeklyCycleLows.length : 0,
        liveCycleLow,
      });
    }
  }, [normalizedTimeframeKey, showWeeklyCycleLows, weeklyCycleLows, liveCycleLow, symbol, timeframe]);

  // === Weekly Cycle Low overlays ===

  const weeklyCycleLowMarkerSeries = useMemo(() => {
    if (!showWeeklyCycleLows) {
      return [];
    }
    if (!Array.isArray(weeklyCycleLows) || weeklyCycleLows.length === 0) {
      return [];
    }

    const markers = weeklyCycleLows
      .filter((event) => Number.isFinite(event?.pivotTime) && Number.isFinite(event?.low))
      .map((event, index) => ({
        t: event.pivotTime,
        v: event.low,
        label: `CL ${index + 1}`,
        __cycleLowEvent: event,
      }));

    if (!markers.length) {
      return [];
    }

    return [{
      key: 'Cycle Lows',
      type: 'marker',
      data: markers,
      style: {
        color: '#22d3ee',
        markerShape: 'arrow-up',
        markerSize: 7,
        markerOffsetY: 14,
        markerTextColor: '#22d3ee',
        markerTextOffsetY: 20,
      },
      tooltipFormatter: (raw) => {
        const event = raw?.__cycleLowEvent;
        if (!event) {
          return raw?.label || 'Cycle Low';
        }
        const { totalScore } = event;
        const date = Number.isFinite(event.pivotTime)
          ? new Date(event.pivotTime).toLocaleDateString('en-GB')
          : '—';
        const scoreStr = Number.isFinite(totalScore) ? ` · Score: ${totalScore}` : '';
        return `Cycle Low · ${date}${scoreStr}`;
      },
      onClick: (raw) => {
        const event = raw?.__cycleLowEvent;
        // eslint-disable-next-line no-console
        console.log('DWLF WCL marker clicked', {
          raw,
          pivotIndex: event?.pivotIndex,
          pivotTime: event?.pivotTime,
          totalScore: event?.totalScore,
        });
      },
    }];
  }, [showWeeklyCycleLows, weeklyCycleLows, normalizedTimeframeKey]);

  /**
   * Live WCL marker visibility conditions (must all hold):
   * - timeframe is weekly
   * - corresponding live overlay toggle enabled
   * - live state exists
   * - live pivot is strictly after the last confirmed pivot (enforced upstream)
   * - live pivot age ≤ MAX_LIVE_CYCLE_AGE_BARS (enforced upstream)
   * - status === 'provisional'
   * - pivotType === 'provisional'
   * - 0 < pivotConfidencePct < 100
   */
  const liveCycleLowSeries = useMemo(() => {
    if (!showLiveWeeklyCycleLows) {
      return [];
    }
    if (!liveCycleLow || liveCycleLow.status === 'none') {
      return [];
    }

    const pivotTime = liveCycleLow.pivotTime;
    const low = liveCycleLow.low;
    if (!Number.isFinite(pivotTime) || !Number.isFinite(low)) {
      return [];
    }

    if (
      !Number.isFinite(liveCycleLow.pivotConfidencePct)
      || liveCycleLow.pivotConfidencePct <= 0
    ) {
      return [];
    }

    const status = liveCycleLow.status || 'provisional';
    const displayStatus = formatLiveCycleStatusLabel(status);
    const baseColor = '#22d3ee';
    const confidenceRaw = Number.isFinite(liveCycleLow.pivotConfidencePct)
      ? liveCycleLow.pivotConfidencePct
      : 0;
    const confidence = Math.max(0, Math.min(100, confidenceRaw));
    const isConfirmed = status === 'confirmed';
    const alpha = isConfirmed
      ? 1
      : 0.5 + 0.25 * (confidence / 100);
    const size = isConfirmed
      ? 8
      : 4 + (confidence / 100) * 3;

    return [{
      key: 'Live Weekly Cycle Low',
      type: 'marker',
      data: [{
        t: pivotTime,
        v: low,
        label: displayStatus.toUpperCase(),
        __liveCycleMeta: liveCycleLow,
      }],
      style: {
        color: `rgba(34, 211, 238, ${alpha.toFixed(2)})`,
        markerShape: 'arrow-up',
        markerSize: size,
        markerOffsetY: 18,
        markerTextColor: baseColor,
        markerTextOffsetY: 24,
        markerShadow: '0 3px 10px rgba(0, 0, 0, 0.55)',
      },
      tooltipFormatter: (raw) => {
        const live = raw?.__liveCycleMeta || liveCycleLow;
        const date = Number.isFinite(live?.pivotTime)
          ? new Date(live.pivotTime).toLocaleDateString('en-GB')
          : '—';
        const statusLabel = formatLiveCycleStatusLabel(live?.status);
        const confidenceStr = Number.isFinite(live?.pivotConfidencePct)
          ? ` · ${Math.round(live.pivotConfidencePct)}%`
          : '';
        const scoreStr = Number.isFinite(live?.currentScore) && Number.isFinite(live?.maxPossibleScore)
          ? ` · ${live.currentScore}/${live.maxPossibleScore}`
          : '';
        return `Live CL · ${date} · ${statusLabel}${confidenceStr}${scoreStr}`;
      },
    }];
  }, [showLiveWeeklyCycleLows, liveCycleLow, normalizedTimeframeKey]);

  /**
   * Live WCH marker visibility conditions (must all hold):
   * - timeframe is weekly
   * - corresponding live overlay toggle enabled
   * - live state exists
   * - live pivot is strictly after the last confirmed pivot (enforced upstream)
   * - live pivot age ≤ MAX_LIVE_CYCLE_AGE_BARS (enforced upstream)
   * - status === 'provisional'
   * - pivotType === 'provisional'
   * - 0 < pivotConfidencePct < 100
   */
  const liveCycleHighSeries = useMemo(() => {
    if (!showLiveWeeklyCycleHighs) {
      return [];
    }
    if (!liveCycleHigh || liveCycleHigh.status === 'none') {
      return [];
    }

    const pivotTime = liveCycleHigh.pivotTime;
    const high = liveCycleHigh.high;
    if (!Number.isFinite(pivotTime) || !Number.isFinite(high)) {
      return [];
    }

    if (
      !Number.isFinite(liveCycleHigh.pivotConfidencePct)
      || liveCycleHigh.pivotConfidencePct <= 0
    ) {
      return [];
    }

    const status = liveCycleHigh.status || 'provisional';
    const displayStatus = formatLiveCycleStatusLabel(status);
    const baseColor = '#B967FF';
    const confidenceRaw = Number.isFinite(liveCycleHigh.pivotConfidencePct)
      ? liveCycleHigh.pivotConfidencePct
      : 0;
    const confidence = Math.max(0, Math.min(100, confidenceRaw));
    const isConfirmed = status === 'confirmed';
    const alpha = isConfirmed
      ? 1
      : 0.5 + 0.25 * (confidence / 100);
    const size = isConfirmed
      ? 8
      : 4 + (confidence / 100) * 3;

    return [{
      key: 'Live Weekly Cycle High',
      type: 'marker',
      data: [{
        t: pivotTime,
        v: high,
        label: displayStatus.toUpperCase(),
        __liveCycleMeta: liveCycleHigh,
      }],
      style: {
        color: `rgba(185, 103, 255, ${alpha.toFixed(2)})`,
        markerShape: 'arrow-down',
        markerSize: size,
        markerOffsetY: -18,
        markerTextColor: baseColor,
        markerTextOffsetY: -24,
        markerShadow: '0 3px 10px rgba(0, 0, 0, 0.55)',
      },
      tooltipFormatter: (raw) => {
        const live = raw?.__liveCycleMeta || liveCycleHigh;
        const date = Number.isFinite(live?.pivotTime)
          ? new Date(live.pivotTime).toLocaleDateString('en-GB')
          : '—';
        const statusLabel = formatLiveCycleStatusLabel(live?.status);
        const confidenceStr = Number.isFinite(live?.pivotConfidencePct)
          ? ` · ${Math.round(live.pivotConfidencePct)}%`
          : '';
        const scoreStr = Number.isFinite(live?.currentScore) && Number.isFinite(live?.maxPossibleScore)
          ? ` · ${live.currentScore}/${live.maxPossibleScore}`
          : '';
        return `Live CH · ${date} · ${statusLabel}${confidenceStr}${scoreStr}`;
      },
    }];
  }, [showLiveWeeklyCycleHighs, liveCycleHigh, normalizedTimeframeKey]);

  // === Weekly Cycle High overlays ===
  const weeklyCycleHighMarkerSeries = useMemo(() => {
    if (!showWeeklyCycleHighs) {
      return [];
    }
    if (!Array.isArray(weeklyCycleHighs) || weeklyCycleHighs.length === 0) {
      return [];
    }

    const markers = weeklyCycleHighs
      .filter((event) => Number.isFinite(event?.pivotTime) && Number.isFinite(event?.high))
      .map((event, index) => ({
        t: event.pivotTime,
        v: event.high,
        label: `CH ${index + 1}`,
        __cycleHighEvent: event,
      }));

    if (!markers.length) {
      return [];
    }

    return [{
      key: 'Cycle Highs',
      type: 'marker',
      data: markers,
      style: {
        color: '#B967FF',
        markerShape: 'arrow-down',
        markerSize: 7,
        markerOffsetY: -14,
        markerTextColor: '#B967FF',
        markerTextOffsetY: -20,
      },
      tooltipFormatter: (raw) => {
        const event = raw?.__cycleHighEvent;
        if (!event) {
          return raw?.label || 'Cycle High';
        }
        const { totalScore } = event;
        const date = Number.isFinite(event.pivotTime)
          ? new Date(event.pivotTime).toLocaleDateString('en-GB')
          : '—';
        const scoreStr = Number.isFinite(totalScore) ? ` · Score: ${totalScore}` : '';
        return `Cycle High · ${date}${scoreStr}`;
      },
      onClick: (raw) => {
        const event = raw?.__cycleHighEvent;
        // eslint-disable-next-line no-console
        console.log('DWLF WCH marker clicked', {
          raw,
          pivotIndex: event?.pivotIndex,
          pivotTime: event?.pivotTime,
          totalScore: event?.totalScore,
        });
      },
    }];
  }, [showWeeklyCycleHighs, weeklyCycleHighs, normalizedTimeframeKey]);

  const weeklyCycleLengthSeries = useMemo(() => {
    if (!showWeeklyCycleLows) {
      return [];
    }
    if (!Array.isArray(weeklyCycleLows) || weeklyCycleLows.length < 2) {
      return [];
    }

    const annotations = [];
    const { unitMs, unitLabel } = getCycleLengthUnit(normalizedTimeframeKey);

    for (let i = 0; i < weeklyCycleLows.length - 1; i += 1) {
      const a = weeklyCycleLows[i];
      const b = weeklyCycleLows[i + 1];
      const tA = a?.pivotTime;
      const tB = b?.pivotTime;
      if (!Number.isFinite(tA) || !Number.isFinite(tB) || tB <= tA) {
        // eslint-disable-next-line no-continue
        continue;
      }
      const dt = tB - tA;
      const units = Math.round(dt / unitMs);
      const mid = tA + dt / 2;
      const lowA = Number.isFinite(a?.low) ? a.low : null;
      const lowB = Number.isFinite(b?.low) ? b.low : null;
      if (lowA === null || lowB === null) {
        // eslint-disable-next-line no-continue
        continue;
      }
      const midPrice = (lowA + lowB) / 2;
      annotations.push({
        t: mid,
        v: midPrice,
        label: `${units}${unitLabel}`,
        units,
      });
    }

    if (!annotations.length) {
      return [];
    }

    const totalUnits = annotations.reduce((sum, ann) => {
      const value = Number(ann.units);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    const avgUnits = totalUnits / annotations.length;
    const avgUnitsRounded = Number.isFinite(avgUnits)
      ? Math.round(avgUnits)
      : null;

    return [{
      key: 'Cycle Low Lengths',
      type: 'marker',
      data: annotations,
      style: {
        color: '#9ca3af',
        markerShape: 'circle',
        markerSize: 0,
        markerOffsetY: 32,
        markerFontSize: 10,
        markerTextColor: '#9ca3af',
        markerTextOffsetY: 0,
      },
      tooltipFormatter: (raw) => {
        const base = raw?.label || '';
        if (!Number.isFinite(avgUnitsRounded)) {
          return base;
        }
        return `${base} (avg ~${avgUnitsRounded}${unitLabel})`;
      },
    }];
  }, [showWeeklyCycleLows, weeklyCycleLows, normalizedTimeframeKey]);

  const weeklyCycleHighLengthSeries = useMemo(() => {
    if (!showWeeklyCycleHighs) {
      return [];
    }
    if (!Array.isArray(weeklyCycleHighs) || weeklyCycleHighs.length < 2) {
      return [];
    }

    const annotations = [];
    const { unitMs, unitLabel } = getCycleLengthUnit(normalizedTimeframeKey);

    for (let i = 0; i < weeklyCycleHighs.length - 1; i += 1) {
      const a = weeklyCycleHighs[i];
      const b = weeklyCycleHighs[i + 1];
      const tA = a?.pivotTime;
      const tB = b?.pivotTime;
      if (!Number.isFinite(tA) || !Number.isFinite(tB) || tB <= tA) {
        // eslint-disable-next-line no-continue
        continue;
      }
      const dt = tB - tA;
      const units = Math.round(dt / unitMs);
      const mid = tA + dt / 2;
      const highA = Number.isFinite(a?.high) ? a.high : null;
      const highB = Number.isFinite(b?.high) ? b.high : null;
      if (highA === null || highB === null) {
        // eslint-disable-next-line no-continue
        continue;
      }
      const midPrice = (highA + highB) / 2;
      annotations.push({
        t: mid,
        v: midPrice,
        label: `${units}${unitLabel}`,
        units,
      });
    }

    if (!annotations.length) {
      return [];
    }

    const totalUnits = annotations.reduce((sum, ann) => {
      const value = Number(ann.units);
      return Number.isFinite(value) ? sum + value : sum;
    }, 0);
    const avgUnits = totalUnits / annotations.length;
    const avgUnitsRounded = Number.isFinite(avgUnits)
      ? Math.round(avgUnits)
      : null;

    return [{
      key: 'Cycle High Lengths',
      type: 'marker',
      data: annotations,
      style: {
        color: '#6b7280',
        markerShape: 'circle',
        markerSize: 0,
        markerOffsetY: 0,
        markerFontSize: 10,
        markerTextColor: '#9ca3af',
        markerTextOffsetY: 0,
      },
      tooltipFormatter: (raw) => {
        const base = raw?.label || '';
        if (!Number.isFinite(avgUnitsRounded)) {
          return base;
        }
        return `${base} (avg ~${avgUnitsRounded}${unitLabel})`;
      },
    }];
  }, [showWeeklyCycleHighs, weeklyCycleHighs, normalizedTimeframeKey]);

  // Price extent of the candles: where the window labels sit and what the faint range box is clipped to. The
  // window boxes and background bands themselves are full-height (DWLF-459), so a zone or band beyond the bars
  // cannot leave a gap above or below them.
  const windowPriceExtent = useMemo(() => {
    if (!Array.isArray(priceCandles) || priceCandles.length === 0) return null;
    const lows = priceCandles.map((candle) => Number(candle?.l)).filter((v) => Number.isFinite(v));
    const highs = priceCandles.map((candle) => Number(candle?.h)).filter((v) => Number.isFinite(v));
    if (!lows.length || !highs.length) return null;
    const minLow = Math.min(...lows);
    const maxHigh = Math.max(...highs);
    if (!Number.isFinite(minLow) || !Number.isFinite(maxHigh) || minLow >= maxHigh) return null;
    return { minLow, maxHigh };
  }, [priceCandles]);

  // "Now" for the cycle-window phases. Re-read when the candle array changes
  // (a fresh candle is the natural moment), never on every render — so the
  // two window series keep their memo identity between renders.
  const [windowNow, setWindowNow] = useState(() => Date.now());
  useEffect(() => { setWindowNow(Date.now()); }, [priceCandles]);

  // WEEKLY: the next cycle window is the STORED one (DWLF-113 ①) — the
  // writer's fold of the symbol's own gaps against the real bar calendar.
  // Nothing is computed from visible pivots; a symbol with no stored window
  // gets no band, and the page says so. The same holds for DAILY (DWLF-271).
  // HOURLY: the store has no rows, so the pre-existing band stays
  // (buildLegacyStateBand — the state row's quantiles, else mean of visible
  // pivots). Coverage is decided once, in isStoreTimeframe.
  const expectedWindowSeries = useMemo(() => {
    if (!showCycleLowWindow || !windowPriceExtent) {
      return [];
    }
    const { unitMs, unitLabel } = getCycleLengthUnit(normalizedTimeframeKey);
    const livePivotTime = liveCycleLow && liveCycleLow.status !== 'none' && Number.isFinite(liveCycleLow.pivotTime)
      ? liveCycleLow.pivotTime
      : null;
    if (!isStoreTimeframe(normalizedTimeframeKey)) {
      return buildLegacyStateBand({
        pivots: weeklyCycleLows, stateSide: cycleWindowState?.low, side: 'low', unitMs, unitLabel, now: windowNow, livePivotTime, ...windowPriceExtent,
      });
    }
    return [
      ...buildCycleWindowBand({
        cycleWindow: cycleWindows?.low ?? null,
        side: 'low',
        now: windowNow,
        livePivotTime,
        unitLabel,
        ...windowPriceExtent,
      }),
      // DWLF-416: the window's price zone, from the API's derived band.
      ...buildCycleDepthZone({ cycleWindow: cycleWindows?.low ?? null, side: 'low', ...windowPriceExtent }),
    ];
  }, [showCycleLowWindow, windowPriceExtent, windowNow, normalizedTimeframeKey, liveCycleLow, cycleWindows, cycleWindowState, weeklyCycleLows]);

  const expectedHighWindowSeries = useMemo(() => {
    if (!showCycleHighWindow || !windowPriceExtent) {
      return [];
    }
    const { unitMs, unitLabel } = getCycleLengthUnit(normalizedTimeframeKey);
    const livePivotTime = liveCycleHigh && liveCycleHigh.status !== 'none' && Number.isFinite(liveCycleHigh.pivotTime)
      ? liveCycleHigh.pivotTime
      : null;
    if (!isStoreTimeframe(normalizedTimeframeKey)) {
      return buildLegacyStateBand({
        pivots: weeklyCycleHighs, stateSide: cycleWindowState?.high, side: 'high', unitMs, unitLabel, now: windowNow, livePivotTime, ...windowPriceExtent,
      });
    }
    return [
      ...buildCycleWindowBand({
        cycleWindow: cycleWindows?.high ?? null,
        side: 'high',
        now: windowNow,
        livePivotTime,
        unitLabel,
        ...windowPriceExtent,
      }),
      // DWLF-416: the window's price zone, from the API's derived band.
      ...buildCycleDepthZone({ cycleWindow: cycleWindows?.high ?? null, side: 'high', ...windowPriceExtent }),
    ];
  }, [showCycleHighWindow, windowPriceExtent, windowNow, normalizedTimeframeKey, liveCycleHigh, cycleWindows, cycleWindowState, weeklyCycleHighs]);

  // Past windows (DWLF-444): the store's closed windows for the side(s) toggled on, with outcome markers. Store timeframes only.
  const pastWindowSeries = useMemo(() => {
    if (!(showPastLowWindows || showPastHighWindows) || !windowPriceExtent || !isStoreTimeframe(normalizedTimeframeKey)) {
      return [];
    }
    return [
      ...(showPastLowWindows ? buildPastCycleWindows({ history: pastCycleWindows?.low, side: 'low', ...windowPriceExtent }) : []),
      ...(showPastHighWindows ? buildPastCycleWindows({ history: pastCycleWindows?.high, side: 'high', ...windowPriceExtent }) : []),
    ];
  }, [showPastLowWindows, showPastHighWindows, windowPriceExtent, normalizedTimeframeKey, pastCycleWindows]);

  // Weinstein stage band (DWLF-382): the page's stage read (a prop, like cycleWindows), drawn behind everything;
  // context, not a signal. A chart mounted without the prop draws no band.
  const stageBandSeries = useMemo(() => {
    if (!showStages || !windowPriceExtent) {
      return [];
    }
    return buildStageBand({
      stage: weinsteinStage, now: windowNow, barTimes: priceCandles.map((candle) => candle?.t), ...windowPriceExtent,
    });
  }, [showStages, weinsteinStage, windowNow, windowPriceExtent, priceCandles]);

  // DWLF-447: Bollinger squeeze spans shaded behind the candles, from the library's own
  // squeeze / released events (over the full series, so the window keeps its warm-up).
  const squeezeBandSeries = useMemo(() => {
    // Drawn from squeeze events, so 'Hide event markers' hides it too.
    if (!showBollinger || !eventsOn('bollinger') || !windowPriceExtent || priceCandles.length === 0) {
      return [];
    }
    try {
      return buildSqueezeBand({
        spans: squeezeSpans(Bollinger, { fullCandles: priceCandlesFull, params: resolvedBollingerParams }),
        barTimes: priceCandles.map((candle) => candle?.t),
        color: INDICATOR_EVENT_META.bollinger.color,
        ...windowPriceExtent,
      });
    } catch (error) {
      console.error('DWLFMarketChart: Failed to compute Bollinger squeezes', error);
      return [];
    }
  }, [showBollinger, eventsOn, windowPriceExtent, priceCandles, priceCandlesFull, resolvedBollingerParams]);

  const indicatorEventTooltip = useMemo(() => {
    if (!showIndicatorEvents || !activeIndicatorEventGroup) {
      return null;
    }
    const group = activeIndicatorEventGroup;
    const dateLabel = new Date(group.timestamp).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    return {
      dateLabel,
      price: group.price,
      summaries: group.summaries || [],
    };
  }, [showIndicatorEvents, activeIndicatorEventGroup]);

  const customEventTooltip = useMemo(() => {
    if (!showCustomEvents || !activeCustomEventGroup) {
      return null;
    }
    const group = activeCustomEventGroup;
    const dateLabel = new Date(group.timestamp).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    return {
      dateLabel,
      price: group.price,
      summaries: group.summaries || [],
    };
  }, [showCustomEvents, activeCustomEventGroup]);

  const userTradeTooltip = useMemo(() => {
    if (!showUserTrades || !activeUserTradeGroup) {
      return null;
    }
    const group = activeUserTradeGroup;
    const dateLabel = new Date(group.timestamp).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    const exitDateLabel = group.endTime
      ? new Date(group.endTime).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      })
      : null;
    // Build a mini summary line per facet so the tooltip render can
    // reuse the same {dateLabel, price, summaries} shape as the
    // custom/indicator event tooltips. Each summary entry is a single
    // bullet line with optional second-line description.
    const directionLabel = group.direction === 'long' ? 'Long' : 'Short';
    const statusLabel = group.active ? 'Open' : 'Closed';
    const fmt = (price) => (Number.isFinite(price) ? formatPriceValue(price) : '—');
    const summaries = [];
    summaries.push(`• ${group.strategyName} · ${directionLabel} · ${statusLabel}`);
    summaries.push(`Entry: ${fmt(group.entryPrice)}    Stop: ${fmt(group.stopPrice)}    Target: ${fmt(group.takePrice)}`);
    if (group.exitPrice !== null) {
      const pnl = group.direction === 'long'
        ? ((group.exitPrice - group.entryPrice) / group.entryPrice) * 100
        : ((group.entryPrice - group.exitPrice) / group.entryPrice) * 100;
      const pnlText = Number.isFinite(pnl) ? `${pnl >= 0 ? '+' : ''}${pnl.toFixed(2)}%` : '';
      const exitLabel = exitDateLabel ? `${exitDateLabel} @ ${fmt(group.exitPrice)}` : `@ ${fmt(group.exitPrice)}`;
      summaries.push(`Exit: ${exitLabel}${pnlText ? `   (${pnlText})` : ''}`);
    }
    if (group.reasonSummary) {
      summaries.push(group.reasonSummary);
    }
    return {
      dateLabel,
      price: group.entryPrice,
      summaries,
    };
  }, [showUserTrades, activeUserTradeGroup]);

  const movingAverageSeries = useMemo(() => {
    const hasAnyMA = showEMACloud || activeSMAs.size > 0 || activeEMAs.size > 0;
    if (priceCandles.length === 0 || !hasAnyMA) {
      return [];
    }

    const sanitizePoints = (points) => (
      Array.isArray(points)
        ? points
            .filter((point) => point && Number.isFinite(point.t) && Number.isFinite(point.v))
            .map((point) => ({ t: point.t, v: point.v }))
        : []
    );

    const series = [];

    SMA_OPTIONS.forEach(({ period, color, label }) => {
      if (!activeSMAs.has(period)) {
        return;
      }

      try {
        const result = SMA.computeSMA(priceCandles, { length: period, source: 'close' });
        const points = sanitizePoints(result?.sma);
        if (points.length > 0) {
          series.push({
            key: label,
            type: 'line',
            data: points,
            style: { color, lineWidth: 2 },
          });
        }
      } catch (error) {
        console.error(`DWLFMarketChart: Failed to compute SMA ${period}`, error);
      }
    });

    EMA_OPTIONS.forEach(({ period, color, label }) => {
      if (!activeEMAs.has(period)) {
        return;
      }

      try {
        const result = EMA.computeEMA(priceCandles, { length: period, source: 'close' });
        const points = sanitizePoints(result?.ema);
        if (points.length > 0) {
          series.push({
            key: label,
            type: 'line',
            data: points,
            style: { color, lineWidth: 2, dashed: true },
          });
        }
      } catch (error) {
        console.error(`DWLFMarketChart: Failed to compute EMA ${period}`, error);
      }
    });

    if (showEMACloud) {
      const emaCloudLengths = activeEMAs.size >= 2
        ? Array.from(activeEMAs).sort((a, b) => a - b)
        : DEFAULT_EMA_CLOUD_LENGTHS;
      try {
        const result = EMACloud.computeEmaCloud(priceCandles, { lengths: emaCloudLengths });
        result.lines.forEach((line, idx) => {
          const points = sanitizePoints(line?.points);
          if (!points.length) {
            return;
          }
          const palette = ['#6C63FF', '#4DD0E1', '#26A69A', '#FFB74D'];
          const color = palette[idx % palette.length];
          series.push({
            key: `EMA Cloud ${line.length}`,
            type: 'line',
            data: points,
            style: { color, lineWidth: 1.5, dashed: true, opacity: 0.9 },
          });
        });
      } catch (error) {
        console.error('DWLFMarketChart: Failed to compute EMA Cloud', error);
      }
    }

    return series;
  }, [priceCandles, activeSMAs, activeEMAs, showEMACloud]);

  // DWLF-330: each v1 break marker is judged against the line a live run had
  // on that bar (indicators 14), which can differ from the hindsight lines
  // drawn below. Draw each break's own line, thin and dotted, so every marker
  // sits on one: drawn from the markers' own events (trendlineEvents).
  const trendlineBreakSeries = useMemo(() => {
    try {
      return computeTrendlineBreakSegments(priceCandles, trendlineEvents).map((segment, index) => ({
        key: `trendline-break-line-${index}`,
        type: 'line',
        data: segment.points.map((point) => ({ t: point.t, v: point.value })),
        style: {
          color: segment.slope < 0 ? 'rgba(255, 115, 115, 0.6)' : 'rgba(64, 196, 255, 0.6)',
          lineWidth: 1.2,
          dashed: true,
          dashArray: '2,3',
        },
        showInTooltip: false,
      }));
    } catch (error) {
      console.error('DWLFMarketChart: Failed to compute trendline break lines', error);
      return [];
    }
  }, [priceCandles, trendlineEvents]);

  const trendlineSeries = useMemo(() => {
    // When compressGaps is active (hourly), the chart uses an index-based x-scale.
    // Trendline endpoints outside the candle data range won't render, so
    // we re-anchor them (preserving slope) to the visible candle range.
    const isCompressed = (timeframe || '').toLowerCase() === 'hourly';
    const trendlineFirstT = isCompressed && priceCandles.length > 1 ? priceCandles[0]?.t : null;
    const trendlineLastT = isCompressed && priceCandles.length > 1 ? priceCandles[priceCandles.length - 1]?.t : null;

    const convert = (line, key, style) => {
      // Use the timestamps embedded on the line itself; these already reflect the
      // candles the indicator library used when computing start/end.
      let startTime =
        Number.isFinite(line.start?.t) && line.start?.t > 0
          ? line.start.t
          : new Date(line.start?.date || line.startDate || 0).getTime();
      let endTime =
        Number.isFinite(line.end?.t) && line.end?.t > 0
          ? line.end.t
          : new Date(line.end?.date || line.endDate || 0).getTime();

      if (!Number.isFinite(startTime) || !Number.isFinite(endTime)) {
        return null;
      }

      let startValue = Number(
        line.start?.value ?? line.startPrice ?? (Number.isFinite(line.start?.price) ? line.start.price : undefined),
      );
      let endValue = Number(
        line.end?.value ?? line.endPrice ?? (Number.isFinite(line.end?.price) ? line.end.price : undefined),
      );

      if (!Number.isFinite(startValue) || !Number.isFinite(endValue)) {
        return null;
      }

      // Clip endpoints that fall outside the hourly visible range, preserving
      // slope. In-range endpoints stay put so the rendered line length matches
      // what's shown on the source timeframe. Trendlines entirely outside the
      // visible range are dropped so old lines don't extrapolate across the
      // viewport.
      if (trendlineFirstT && trendlineLastT) {
        if ((startTime < trendlineFirstT && endTime < trendlineFirstT)
          || (startTime > trendlineLastT && endTime > trendlineLastT)) {
          return null;
        }
        const dt = endTime - startTime;
        if (Number.isFinite(dt) && dt !== 0) {
          const slope = (endValue - startValue) / dt;
          if (startTime < trendlineFirstT) {
            startValue = startValue + slope * (trendlineFirstT - startTime);
            startTime = trendlineFirstT;
          } else if (startTime > trendlineLastT) {
            startValue = startValue + slope * (trendlineLastT - startTime);
            startTime = trendlineLastT;
          }
          if (endTime > trendlineLastT) {
            endValue = endValue + slope * (trendlineLastT - endTime);
            endTime = trendlineLastT;
          } else if (endTime < trendlineFirstT) {
            endValue = endValue + slope * (trendlineFirstT - endTime);
            endTime = trendlineFirstT;
          }
        }
      }

      // Project the line forward to the live edge of the visible candles so
      // weekly (and daily) trendlines reach the current bar instead of stopping
      // at their last swing-pivot anchor — which on the daily chart is always
      // the prior completed week. Preserves slope; only ever extends, never
      // retracts. Skipped for the compressed hourly x-scale, which does its own
      // range clipping above.
      //
      // ONLY extend ACTIVE lines: inactive (historical / already-broken)
      // trendlines must keep their original short segments, otherwise every
      // dead line in history fires a ray at the live edge and buries the chart.
      if (!isCompressed && line.isActive !== false && priceCandles.length > 0) {
        const lastCandleT = priceCandles[priceCandles.length - 1]?.t;
        if (Number.isFinite(lastCandleT) && lastCandleT > endTime) {
          const dt = endTime - startTime;
          if (Number.isFinite(dt) && dt !== 0) {
            const slope = (endValue - startValue) / dt;
            endValue = endValue + slope * (lastCandleT - endTime);
            endTime = lastCandleT;
          }
        }
      }

      return {
        key,
        type: 'line',
        data: [
          { t: startTime, v: startValue },
          { t: endTime, v: endValue },
        ],
        style,
        showInTooltip: false,
      };
    };

    const series = [];

    if (localShowDailyTrendlines) {
      // Prefer locally computed trendlines (from @andywilliams/indicators) for the
      // current timeframe. Fall back to any server-provided dailyTrendlines only
      // if local computation isn't available.
      const resolved =
        timeframe === 'daily' && Array.isArray(trendlines) && trendlines.length > 0
          ? trendlines
          : Array.isArray(dailyTrendlines)
            ? dailyTrendlines
            : [];

      resolved.forEach((line, index) => {
        // On hourly, drop daily trendlines whose end is before the visible
        // range — otherwise ancient lines extrapolate across the viewport.
        // Inactive (dimmed) lines are still shown, matching the daily view.
        if (isCompressed && trendlineFirstT) {
          const endTime = Number.isFinite(line.end?.t) && line.end.t > 0
            ? line.end.t
            : new Date(line.end?.date || line.endDate || 0).getTime();
          if (Number.isFinite(endTime) && endTime < trendlineFirstT) {return;}
        }

        const isProvisional = line.status === 'provisional' || line.meta?.provisional;
        const isDown = line.slope < 0;
        const isActive = line.isActive !== false;

        const color = (() => {
          if (isProvisional) {
            return isDown ? 'rgba(255, 196, 38, 0.85)' : 'rgba(56, 229, 178, 0.85)';
          }
          if (isDown) {
            return isActive ? 'rgba(255, 115, 115, 0.92)' : 'rgba(255, 115, 115, 0.55)';
          }
          return isActive ? 'rgba(64, 196, 255, 0.92)' : 'rgba(64, 196, 255, 0.55)';
        })();

        const strokeWidth = isActive ? 2.6 : 1.8;
        const dash = isProvisional ? '4,3' : undefined;

        const key = `daily-trendline-${index}`;
        const spec = convert(line, key, { color, lineWidth: strokeWidth, dashed: dash !== undefined, dashArray: dash });
        if (spec) {
          series.push(spec);
        }
      });
    }

    if (localShowHourlyTrendlines) {
      const resolved = Array.isArray(hourlyTrendlines) && hourlyTrendlines.length > 0
        ? hourlyTrendlines
        : (timeframe === 'hourly' ? trendlines : []);

      resolved.forEach((line, index) => {
        const isDown = line.slope < 0;
        const style = {
          color: scaleColorIntensity(isDown ? '#FF7043' : '#8E24AA', 1, 1),
          lineWidth: 1.6,
          dashed: true,
        };
        const spec = convert(line, `hourly-trendline-${index}`, style);
        if (spec) {
          series.push(spec);
        }
      });
    }

    if (localShowWeeklyTrendlines && Array.isArray(weeklyTrendlines)) {
      weeklyTrendlines.forEach((line, index) => {
        // Skip inactive weekly trendlines — on daily they're just dimmed,
        // but on hourly the projection would make old lines fully visible.
        if (isCompressed && line.isActive === false) {return;}

        // When projecting, skip trendlines whose end date is before the
        // hourly data starts — they're too old to be relevant.
        if (isCompressed && trendlineFirstT) {
          const endTime = Number.isFinite(line.end?.t) && line.end.t > 0
            ? line.end.t
            : new Date(line.end?.date || line.endDate || 0).getTime();
          if (Number.isFinite(endTime) && endTime < trendlineFirstT) {return;}
        }

        const isActive = line.isActive !== false;
        const style = {
          color: line.slope < 0
            ? (isActive ? '#FF9F33' : 'rgba(255, 159, 51, 0.45)')
            : (isActive ? '#33A8FF' : 'rgba(51, 168, 255, 0.45)'),
          lineWidth: isActive ? 2 : 1.4,
          dashed: true,
        };
        const spec = convert(line, `weekly-trendline-${index}`, style);
        if (spec) {
          series.push(spec);
        }
      });
    }

    return series;
  }, [localShowDailyTrendlines, dailyTrendlines, timeframe, trendlines, localShowHourlyTrendlines, hourlyTrendlines, localShowWeeklyTrendlines, weeklyTrendlines, priceCandles]);

  const pricePaneSeries = useMemo(() => {
    const series = [];
    // First, so the stage boxes sit behind the candles and every other overlay.
    if (stageBandSeries.length > 0) {
      series.push(...stageBandSeries);
    }
    if (squeezeBandSeries.length > 0) {
      series.push(...squeezeBandSeries);
    }
    if (divergencePriceSeries.length > 0) {
      series.push(...divergencePriceSeries);
    }
    if (priceCandles.length > 0) {
      series.push({ key: 'OHLC', type: 'ohlc', data: priceCandles, style: candleStyle(scheme) });
    }
    if (movingAverageSeries.length > 0) {
      series.push(...movingAverageSeries);
    }
    if (bollingerSeries.length > 0) {
      series.push(...bollingerSeries);
    }
    if (vwapSeries.length > 0) {
      series.push(...vwapSeries);
    }
    if (anchoredVwapSeries.length > 0) {
      series.push(...anchoredVwapSeries);
    }
    if (trendlineSeries.length > 0) {
      series.push(...trendlineSeries);
    }
    if (cycleTrendlinePriceSeries.length > 0) {
      series.push(...cycleTrendlinePriceSeries);
    }
    if (trendlineBreakSeries.length > 0) {
      series.push(...trendlineBreakSeries);
    }
    if (swingMarkerSeries.length > 0) {
      series.push(...swingMarkerSeries);
    }
    if (tradeMarkerSeries.length > 0) {
      series.push(...tradeMarkerSeries);
    }
    if (tradePositionSeries.length > 0) {
      series.push(...tradePositionSeries);
    }
    if (showTradeJournal && tradeJournalSeries.length > 0) {
      series.push(...tradeJournalSeries);
    }
    // Both custom and indicator event markers live in their own
    // dedicated "events" pane above the price pane (see chartSpec
    // below) — neither is pushed into the price pane any more. The
    // chart-rendered callout bubbles that used to anchor to candle
    // prices have been removed for both event types; the cursor-
    // following React tooltips now serve the same role without
    // overlapping the candles.
    if (weeklyCycleLowMarkerSeries.length > 0) {
      series.push(...weeklyCycleLowMarkerSeries);
    }
    if (weeklyCycleHighMarkerSeries.length > 0) {
      series.push(...weeklyCycleHighMarkerSeries);
    }
    if (liveCycleLowSeries.length > 0) {
      series.push(...liveCycleLowSeries);
    }
    if (liveCycleHighSeries.length > 0) {
      series.push(...liveCycleHighSeries);
    }
    if (weeklyCycleLengthSeries.length > 0) {
      series.push(...weeklyCycleLengthSeries);
    }
    series.push(...pastWindowSeries);
    if (expectedWindowSeries.length > 0) {
      series.push(...expectedWindowSeries);
    }
    if (expectedHighWindowSeries.length > 0) {
      series.push(...expectedHighWindowSeries);
    }
    if (weeklyCycleHighLengthSeries.length > 0) {
      series.push(...weeklyCycleHighLengthSeries);
    }
    // When the user is hovering near a user trade marker, show that
    // one trade's risk/reward zone as a very subtle position series.
    // The zone fades in when the crosshair snaps to a trade and
    // disappears when it moves away — giving detail-on-demand without
    // the permanent visual clutter of rendering all zones at once.
    if (activeUserTradeGroup) {
      const g = activeUserTradeGroup;
      const THIRTY_CANDLES_MS = 29 * 86_400_000;
      series.push({
        key: 'Hovered Trade Zone',
        type: 'position',
        data: [{
          start: g.timestamp,
          end: g.endTime || (g.timestamp + THIRTY_CANDLES_MS),
          entry: g.entryPrice,
          stop: g.stopPrice,
          target: g.takePrice,
          riskColor: 'rgba(239, 68, 68, 0.12)',
          rewardColor: 'rgba(34, 197, 94, 0.12)',
          bubbleColor: 'transparent',
          textColor: 'transparent',
          messages: [],
        }],
      });
    }
    return series;
  }, [
    priceCandles,
    movingAverageSeries,
    bollingerSeries,
    vwapSeries,
    anchoredVwapSeries,
    trendlineSeries,
    trendlineBreakSeries,
    swingMarkerSeries,
    tradeMarkerSeries,
    tradePositionSeries,
    showTradeJournal,
    tradeJournalSeries,
    weeklyCycleLowMarkerSeries,
    weeklyCycleHighMarkerSeries,
    liveCycleLowSeries,
    liveCycleHighSeries,
    weeklyCycleLengthSeries,
    pastWindowSeries,
    expectedWindowSeries,
    expectedHighWindowSeries,
    weeklyCycleHighLengthSeries,
    activeUserTradeGroup,
    scheme,
    stageBandSeries,
    squeezeBandSeries,
    divergencePriceSeries,
    cycleTrendlinePriceSeries,
  ]);

  const pricePaneGuides = useMemo(() => (
    [...basePricePaneGuides, ...tradeGuides]
  ), [basePricePaneGuides, tradeGuides]);

  const timeFormatter = useMemo(() => {
    const lowerTf = (timeframe || '').toLowerCase();
    if (lowerTf === 'hourly') {
      return (timestamp) => {
        const date = new Date(timestamp);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      };
    }
    return (timestamp) => new Date(timestamp).toLocaleDateString();
  }, [timeframe]);

  const chartSpec = useMemo(() => {
    const panes = [];

    // Shared event marker band — a thin pane *above* the price pane,
    // shared by both custom events and indicator events. The pane has
    // a 0..1 fixed scale and adapts to which event types are active:
    //
    //   - Both on  → heightRatio 0.8, two rows. Custom events on the
    //                upper row (v: 0.7), indicator events on the lower
    //                row (v: 0.3). The two rows share the same vertical
    //                real estate, keeping the chrome compact.
    //   - One on   → heightRatio 0.5, single row centred at v: 0.5.
    //                The other type's row would just be empty, so we
    //                give the price pane back the space.
    //   - None     → pane omitted entirely.
    //
    // hideYAxis: true suppresses the right-edge axis tick labels
    // (otherwise the chart would render "0.00", "0.25", "0.50", etc.
    // which would look broken on a marker band). hideYAxis was added
    // in @dwlf/charting@1.3.0.
    //
    // Pane order matters: this must be pushed BEFORE the price pane so
    // it renders on top.
    const customMarkersPresent = customEventMarkerSeries.length > 0;
    const indicatorMarkersPresent = indicatorEventMarkerSeries.length > 0;
    const bothMarkersPresent = customMarkersPresent && indicatorMarkersPresent;
    // Helper: re-position a series' marker dots onto a new v row. Used
    // to centre a single-type band when only one event kind is active.
    const repositionMarkers = (series, newV) => series.map((s) => ({
      ...s,
      data: s.data.map((d) => ({ ...d, v: newV })),
    }));
    let eventsPaneSeries = [];
    let eventsHeightRatio = 0;
    if (bothMarkersPresent) {
      eventsPaneSeries = [
        ...customEventMarkerSeries,
        ...indicatorEventMarkerSeries,
      ];
      eventsHeightRatio = 0.8;
    } else if (customMarkersPresent) {
      eventsPaneSeries = repositionMarkers(customEventMarkerSeries, 0.5);
      eventsHeightRatio = 0.5;
    } else if (indicatorMarkersPresent) {
      eventsPaneSeries = repositionMarkers(indicatorEventMarkerSeries, 0.5);
      eventsHeightRatio = 0.5;
    }
    if (eventsPaneSeries.length > 0) {
      panes.push({
        id: 'events',
        heightRatio: eventsHeightRatio,
        yScale: { mode: 'fixed', min: 0, max: 1 },
        hideYAxis: true,
        series: eventsPaneSeries,
      });
    }

    if (pricePaneSeries.length > 0) {
      panes.push({
        id: 'price',
        // No pane title — the symbol/timeframe header in the toolbar
        // already says "Bitcoin (BTC-USD)" + "Daily/Weekly", so a
        // chart-rendered "BTC-USD (DAILY)" overlay just duplicates
        // the same information in a less-styled font.
        heightRatio: 6 - (showDSS && dssSeries ? 1 : 0) - (showATR && atrSeries ? 1 : 0) - (showRSI && rsiSeries ? 1 : 0) - (showADX && adxSeries ? 1 : 0),
        yScale: { mode: 'auto' },
        series: pricePaneSeries,
        guides: pricePaneGuides,
      });
    }

    if (showDSS && dssSeries) {
      panes.push({
        id: 'dss',
        title: 'DSS Oscillator',
        heightRatio: 2,
        yScale: { mode: 'fixed', min: 0, max: 100 },
        guides: [
          { y: 80, dashed: true, color: 'rgba(148, 163, 184, 0.4)', label: '80' },
          { y: 50, dashed: true, color: 'rgba(148, 163, 184, 0.3)', label: '50' },
          { y: 20, dashed: true, color: 'rgba(148, 163, 184, 0.4)', label: '20' },
        ],
        series: [
          { key: 'DSS', type: 'line', data: dssSeries.dss, style: { color: '#42a5f5', lineWidth: 1.8 } },
          { key: 'Signal', type: 'line', data: dssSeries.signal, style: { color: '#ef5350', lineWidth: 1.2, dashed: true } },
          // DWLF-338: the stored divergences' pivot-to-pivot DSS readings.
          // Only those read off a DSS with the pane's settings, so the line sits on the curve.
          ...(shownDivergenceOscillators.has('dss') ? divergenceSegments(
            divergenceEvents.filter((event) => dssSourceMatches(event, resolvedDssParams)),
            { oscillator: 'dss', on: 'oscillator', from: priceCandles[0]?.t, to: priceCandles[priceCandles.length - 1]?.t },
          ) : []),
        ],
      });
    }

    if (showRSI && rsiSeries) {
      panes.push({
        id: 'rsi',
        title: rsiPaneTitle(),
        heightRatio: 2,
        yScale: { mode: 'fixed', min: 0, max: 100 },
        guides: [
          { y: RSI_GUIDE_LEVELS.overbought, dashed: true, color: 'rgba(148, 163, 184, 0.4)', label: String(RSI_GUIDE_LEVELS.overbought) },
          { y: RSI_GUIDE_LEVELS.midline, dashed: true, color: 'rgba(148, 163, 184, 0.3)', label: String(RSI_GUIDE_LEVELS.midline) },
          { y: RSI_GUIDE_LEVELS.oversold, dashed: true, color: 'rgba(148, 163, 184, 0.4)', label: String(RSI_GUIDE_LEVELS.oversold) },
        ],
        series: [
          { key: 'RSI', type: 'line', data: rsiSeries, style: { color: INDICATOR_EVENT_META.rsi.color, lineWidth: 1.6 } },
          ...(shownDivergenceOscillators.has('rsi') ? divergenceSegments(divergenceEvents, {
            oscillator: 'rsi', on: 'oscillator', from: priceCandles[0]?.t, to: priceCandles[priceCandles.length - 1]?.t,
          }) : []),
        ],
      });
    }

    if (showADX && adxSeries) {
      panes.push({
        id: 'adx',
        title: adxPaneTitle(),
        heightRatio: 2,
        yScale: { mode: 'auto' },
        guides: [
          { y: ADX_GUIDE_LEVELS.trendLevel, dashed: true, color: 'rgba(148, 163, 184, 0.4)', label: String(ADX_GUIDE_LEVELS.trendLevel) },
          { y: ADX_GUIDE_LEVELS.fadeLevel, dashed: true, color: 'rgba(148, 163, 184, 0.3)', label: String(ADX_GUIDE_LEVELS.fadeLevel) },
        ],
        series: [
          { key: 'ADX', type: 'line', data: adxSeries.adx, style: { color: ADX_COLORS.adx, lineWidth: 1.8 } },
          { key: '+DI', type: 'line', data: adxSeries.plusDI, style: { color: ADX_COLORS.plusDI, lineWidth: 1 } },
          { key: '-DI', type: 'line', data: adxSeries.minusDI, style: { color: ADX_COLORS.minusDI, lineWidth: 1 } },
        ],
      });
    }

    if (showATR && atrSeries) {
      panes.push({
        id: 'atr',
        title: atrPaneTitle(),
        heightRatio: 2,
        yScale: { mode: 'auto' },
        series: [
          { key: 'ATR %', type: 'line', data: atrSeries, style: { color: INDICATOR_EVENT_META.atr.color, lineWidth: 1.6 } },
        ],
      });
    }

    return {
      panes,
      timeFormatter,
      // Always wire up the crosshair callback so the OHLC info panel
      // updates on hover. Event/trade tooltip handlers run inside the
      // same callback and short-circuit when their respective overlays
      // are disabled, so there's negligible cost when only the OHLC
      // panel is needed.
      onCrosshairMove: handleCrosshairMove,
    };
  }, [
    pricePaneSeries,
    pricePaneGuides,
    customEventMarkerSeries,
    indicatorEventMarkerSeries,
    showDSS,
    dssSeries,
    showATR,
    showRSI,
    rsiSeries,
    showADX,
    adxSeries,
    shownDivergenceOscillators,
    divergenceEvents,
    priceCandles,
    resolvedDssParams,
    atrSeries,
    timeFormatter,
    handleCrosshairMove,
  ]);

  // The hovered time's series values (moving averages, VWAP, DSS…) for the header readout.
  const hoveredSeriesValues = useMemo(
    () => seriesValuesAt((chartSpec?.panes || []).flatMap((p) => p.series || []), hoveredCandle?.t),
    [chartSpec, hoveredCandle],
  );

  // Toolbar dropdown contents are split into three buckets so the
  // user can mentally find what they want without scanning a kitchen-
  // sink list. The split is by *kind*, not alphabetical:
  //
  //   Indicators → analytical layers (Fib, swings, DSS, Bollinger,
  //                trendlines, S/R, indicator-events firing). Moving
  //                averages live as a sub-section inside this bucket.
  //   Cycles     → DWLF-specific cycle pivots (weekly + live).
  //   My Data    → user-owned overlays (trades, journal, custom
  //                events). Custom events are a sub-section inside.
  const indicatorMenuItems = [
    { label: 'Fib Levels', eventsKey: 'fib', active: showFib, toggle: handleToggleFib },
    { label: 'Swings', eventsKey: 'swings', active: showSwings, toggle: () => setShowSwings((v) => !v) },
    { label: 'DSS Oscillator', eventsKey: 'dss', active: showDSS, toggle: () => setShowDSS((v) => !v) },
    { label: 'ATR (volatility)', eventsKey: 'atr', active: showATR, toggle: () => setShowATR((v) => !v) },
    { label: 'RSI', eventsKey: 'rsi', active: showRSI, toggle: () => setShowRSI((v) => !v) },
    { label: 'ADX / DMI (trend strength)', eventsKey: 'adx', active: showADX, toggle: () => setShowADX((v) => !v) },
    { label: 'Bollinger Bands', eventsKey: 'bollinger', active: showBollinger, toggle: () => setShowBollinger((v) => !v) },
    // VWAP anchors render in their own collapsible sub-section (below) —
    // not as flat rows here.
    { label: 'EMA Cloud', eventsKey: 'emaCloud', active: showEMACloud, toggle: () => setShowEMACloud((v) => !v) },
    {
      label: 'Daily Trendlines',
      eventsKey: 'trendlines',
      active: localShowDailyTrendlines,
      toggle: () => {
        const next = !localShowDailyTrendlines;
        setLocalShowDailyTrendlines(next);
        onToggleDailyTrendlines?.(next);
      },
    },
    {
      label: 'Hourly Trendlines',
      eventsKey: 'trendlines',
      active: localShowHourlyTrendlines,
      toggle: () => {
        const next = !localShowHourlyTrendlines;
        setLocalShowHourlyTrendlines(next);
        onToggleHourlyTrendlines?.(next);
      },
    },
    {
      label: 'Weekly Trendlines',
      eventsKey: 'trendlines',
      active: localShowWeeklyTrendlines,
      toggle: () => {
        const next = !localShowWeeklyTrendlines;
        setLocalShowWeeklyTrendlines(next);
        onToggleWeeklyTrendlines?.(next);
      },
    },
    ...(cycleTrendlinesBeta ? [{
      label: 'Cycle trendlines (beta)',
      active: showCycleTrendlines,
      toggle: () => setShowCycleTrendlines((v) => !v),
    }] : []),
    { label: 'Support / Resistance', eventsKey: 'sr', active: showSR, toggle: () => setShowSR((v) => !v) },
  ];
  const cycleMenuItems = [
    {
      label: 'Cycle Lows',
      eventsKey: 'cycleLows',
      active: showWeeklyCycleLows,
      toggle: () => setShowWeeklyCycleLows((v) => !v),
    },
    {
      label: 'Cycle Highs',
      eventsKey: 'cycleHighs',
      active: showWeeklyCycleHighs,
      toggle: () => setShowWeeklyCycleHighs((v) => !v),
    },
    {
      label: 'Live Cycle Lows',
      eventsKey: 'liveCycleLows',
      active: showLiveWeeklyCycleLows,
      toggle: () => setShowLiveWeeklyCycleLows((v) => !v),
    },
    {
      label: 'Live Cycle Highs',
      eventsKey: 'liveCycleHighs',
      active: showLiveWeeklyCycleHighs,
      toggle: () => setShowLiveWeeklyCycleHighs((v) => !v),
    },
    {
      label: 'Low Window',
      active: showCycleLowWindow,
      toggle: () => setShowCycleLowWindow((v) => !v),
    },
    {
      label: 'High Window',
      active: showCycleHighWindow,
      toggle: () => setShowCycleHighWindow((v) => !v),
    },
    {
      label: 'Past Low Windows',
      active: showPastLowWindows,
      toggle: () => setShowPastLowWindows((v) => !v),
    },
    {
      label: 'Past High Windows',
      active: showPastHighWindows,
      toggle: () => setShowPastHighWindows((v) => !v),
    },
    {
      label: 'Weinstein Stages',
      eventsKey: 'stages',
      active: showStages,
      toggle: () => setShowStages((v) => !v),
    },
  ];
  // User Trades has no top-level toggle row any more — visibility is
  // driven by the per-strategy selections in the Custom Strategies
  // sub-section, exactly like Custom Events.
  const myDataMenuItems = [
    { label: 'System Trades', active: showSystemTrades, toggle: () => setShowSystemTrades((v) => !v) },
    { label: 'Trade Journal', active: showTradeJournal, toggle: () => setShowTradeJournal((v) => !v) },
  ];
  const totalActiveMovingAverages = activeSMAs.size + activeEMAs.size;
  const hasActiveMovingAverages = totalActiveMovingAverages > 0;
  // Events keys of every layer currently shown (the mobile Events chip switches these on together).
  const activeEventKeys = () => [
    ...[...indicatorMenuItems, ...cycleMenuItems].filter((item) => item.active && item.eventsKey).map((item) => item.eventsKey),
    ...(vwapAnchors.size > 0 ? ['vwap'] : []),
    ...(smcOverlays.size > 0 ? ['smc'] : []),
    ...(totalActiveMovingAverages > 0 ? ['movingAverages'] : []),
  ];
  const indicatorActiveCount = indicatorMenuItems
    .reduce((count, item) => (item.active ? count + 1 : count), 0)
    + totalActiveMovingAverages
    + vwapAnchors.size;
  const cycleActiveCount = cycleMenuItems.reduce((count, item) => (item.active ? count + 1 : count), 0);
  const myDataActiveCount = myDataMenuItems.reduce((count, item) => (item.active ? count + 1 : count), 0)
    + customEventSelections.size
    + userStrategySelections.size;

  // Per-dropdown clear handlers. Each dropdown owns its own clear so
  // the affordance lives where the user expects to find it (the
  // dropdown that has active items also has the button to turn them
  // off). The earlier "global clear all" sat inside one dropdown but
  // cleared everything, which was discoverability-hostile when only a
  // different dropdown had active items.
  const handleClearIndicators = () => {
    setShowFib(false);
    setShowSwings(false);
    setShowDSS(false);
    setShowATR(false);
    setShowRSI(false);
    setShowADX(false);
    setEventLayers(new Set());
    setShowBollinger(false);
    setShowEMACloud(false);
    setShowSR(false);
    setLocalShowDailyTrendlines(false);
    onToggleDailyTrendlines?.(false);
    setLocalShowHourlyTrendlines(false);
    onToggleHourlyTrendlines?.(false);
    setLocalShowWeeklyTrendlines(false);
    onToggleWeeklyTrendlines?.(false);
    setShowCycleTrendlines(false);
    setActiveSMAs(new Set());
    setActiveEMAs(new Set());
    setVwapAnchors(new Set());
  };

  const handleClearCycles = () => {
    setShowWeeklyCycleLows(false);
    setShowWeeklyCycleHighs(false);
    setShowLiveWeeklyCycleLows(false);
    setShowLiveWeeklyCycleHighs(false);
    setShowCycleLowWindow(false);
    setShowCycleHighWindow(false);
    setShowPastLowWindows(false);
    setShowPastHighWindows(false);
    setShowStages(false);
  };

  const handleClearMyData = () => {
    setShowSystemTrades(false);
    setShowTradeJournal(false);
    setCustomEventSelections(new Set());
    setUserStrategySelections(new Set());
  };

  const sectionHeaderStyle = {
    padding: '8px 12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: 'var(--dwlf-ink-80)',
    backgroundColor: 'var(--dwlf-ink-03)',
  };

  // The Desk's lenses (DWLF-300; canvas board AppDesk): one-click views over the layers above, for
  // reading a chart by question — where is it in its cycle, where are its levels, what fired, what's
  // the trend. A lens is lit when any of its layers is on; clicking a lit lens turns all of them off,
  // clicking a dark one turns on its default set. The dropdowns stay for fine control.
  // Trend lights for any timeframe's trendlines (weekly lines draw on every timeframe) and turns on
  // the current timeframe's; off clears all three.
  const trendlineFlags = [
    ['daily', localShowDailyTrendlines, setLocalShowDailyTrendlines, onToggleDailyTrendlines],
    ['hourly', localShowHourlyTrendlines, setLocalShowHourlyTrendlines, onToggleHourlyTrendlines],
    ['weekly', localShowWeeklyTrendlines, setLocalShowWeeklyTrendlines, onToggleWeeklyTrendlines],
  ];
  const setTrendlines = (on) => trendlineFlags.forEach(([tf, , setLocal, notify]) => {
    if (on && tf !== effectiveSelectedTimeframe) return;
    setLocal(on);
    notify?.(on);
  });
  const lenses = [
    {
      key: 'cycle', label: 'Cycle', color: 'accent',
      active: showWeeklyCycleLows || showLiveWeeklyCycleLows || showWeeklyCycleHighs || showLiveWeeklyCycleHighs
        || showCycleLowWindow || showCycleHighWindow,
      set: (on) => {
        setShowWeeklyCycleLows(on);
        setShowLiveWeeklyCycleLows(on);
        setShowCycleLowWindow(on);
        if (!on) {
          setShowWeeklyCycleHighs(false); setShowLiveWeeklyCycleHighs(false); setShowCycleHighWindow(false);
        }
      },
    },
    {
      key: 'levels', label: 'Levels', color: 'warn',
      active: showSR || showFib,
      set: (on) => {
        setShowSR(on);
        if (!on) { didAutoShowFib.current = true; setShowFib(false); } // and keep auto-Fib from re-lighting it
      },
    },
    {
      key: 'events', label: 'Events', color: 'up',
      active: showIndicatorEvents,
      // On: events for every layer currently shown; off: none.
      set: (on) => setEventLayers(on ? new Set(activeEventKeys()) : new Set()),
    },
    {
      key: 'trend', label: 'Trend', color: 'ink-60',
      active: trendlineFlags.some(([, shown]) => shown) || showSwings || (cycleTrendlinesBeta && showCycleTrendlines),
      // On leaves the V3 beta as it is (it is opt-in); off clears it with the rest.
      set: (on) => {
        setTrendlines(on);
        setShowSwings(on);
        if (!on) {setShowCycleTrendlines(false);}
      },
    },
  ];

  // Renders a single label + check toggle row used in all three of the
  // chart toolbar dropdowns. Defined here (not extracted to a
  // component) because it closes over the same hover handlers and
  // colour palette used elsewhere in the toolbar.
  // The small "events" switch beside a layer (DWLF-447): its markers draw only while the layer is on
  // and this is on. Clicking it never toggles the layer itself.
  const renderEventsSwitch = (eventsKey, layerActive) => {
    const on = eventLayers.has(eventsKey);
    return (
      <span
        role="switch"
        aria-checked={on}
        aria-label="Show this layer's events"
        title={layerActive ? 'Show this layer\'s event markers' : 'Turn the layer on to show its events'}
        onClick={(e) => { e.stopPropagation(); toggleEventLayer(eventsKey); }}
        style={{
          marginLeft: 'auto',
          marginRight: 8,
          padding: '1px 6px',
          borderRadius: 8,
          fontSize: 10,
          cursor: 'pointer',
          border: on ? '1px solid #FFB300' : '1px solid var(--dwlf-ink-40)',
          color: on ? '#FFB300' : 'var(--dwlf-ink-50)',
          opacity: layerActive ? 1 : 0.45,
        }}
      >
        {on ? '◆ events' : '◇ events'}
      </span>
    );
  };

  const renderToggleRow = ({ label, active, toggle, eventsKey }) => (
    <div key={label}>
      <div
        onClick={toggle}
        style={{
          padding: '8px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: 12,
          color: 'var(--dwlf-ink-88)',
          backgroundColor: active ? 'rgba(76, 175, 80, 0.2)' : 'transparent',
          borderBottom: '1px solid var(--dwlf-ink-20)',
          transition: 'background-color 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = active
            ? 'rgba(76, 175, 80, 0.3)'
            : 'var(--dwlf-ink-10)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = active ? 'rgba(76, 175, 80, 0.2)' : 'transparent';
        }}
      >
        <span>{label}</span>
        {eventsKey ? renderEventsSwitch(eventsKey, active) : null}
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          height: 16,
          borderRadius: '50%',
          border: active ? '1px solid #4CAF50' : '1px solid var(--dwlf-ink-40)',
          backgroundColor: active ? '#4CAF50' : 'transparent',
          fontSize: 10,
          color: active ? '#000' : 'var(--dwlf-ink-62)',
        }}>
          {active ? '✓' : ''}
        </span>
      </div>
    </div>
  );

  // Renders the "Clear X" footer row inside a dropdown. Each
  // top-level toolbar dropdown owns its own clear handler so the
  // affordance lives where the user expects to find it (in the
  // dropdown that has active items).
  const renderClearRow = (onClear, label) => (
    <>
      <div style={{ borderTop: '1px solid var(--dwlf-ink-30)', margin: '4px 0' }} />
      <div
        onClick={onClear}
        style={{
          padding: '6px 12px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          fontSize: 11,
          color: 'var(--dwlf-ink-55)',
          transition: 'color 0.2s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dwlf-ink-88)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dwlf-ink-55)'; }}
      >
        {label}
      </div>
    </>
  );

  // Renders a single SMA/EMA row inside the Moving Averages
  // sub-section. The colour swatch on the left previews the line
  // colour the chart will use.
  const renderMovingAverageRow = ({ key, label, color, isActive, onToggle }) => (
    <div
      key={key}
      onClick={onToggle}
      style={{
        padding: '8px 12px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: 12,
        color: 'var(--dwlf-ink-88)',
        backgroundColor: isActive ? 'rgba(76, 175, 80, 0.2)' : 'transparent',
        borderBottom: '1px solid var(--dwlf-ink-20)',
        transition: 'background-color 0.2s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = isActive
          ? 'rgba(76, 175, 80, 0.3)'
          : 'var(--dwlf-ink-10)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = isActive
          ? 'rgba(76, 175, 80, 0.2)'
          : 'transparent';
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            width: 12,
            height: 2,
            backgroundColor: color,
            borderRadius: 1,
          }}
        />
        <span>{label}</span>
      </div>
      {isActive && <span style={{ color: '#4CAF50', fontSize: 10 }}>✓</span>}
    </div>
  );

  const lowerTf = (timeframe || '').toLowerCase();
  const compressGaps = false;


  // Clip cross-timeframe trendlines/rays to the visible time range.
  //
  // A higher-tf trendline (e.g. daily on hourly) can have endpoints well outside
  // the lower-tf viewport. The chart library would draw a line from the far
  // off-screen pixel to the on-screen pixel, visually spanning the whole chart
  // and making old lines look like they extend "crazily". We clip each out-of-range
  // endpoint to the visible edge while preserving the original slope. In-range
  // endpoints are left untouched, so line lengths match what's shown on the source
  // timeframe.
  // SMC overlays computed live from candle history (recompute-on-read). These
  // are ephemeral chart-only annotations — never persisted, never sent to the
  // annotations API. dwlf-charting renders order_block / fair_value_gap /
  // bos_line natively. `locked: true` keeps them non-draggable.
  const smcAnnotations = useMemo(() => {
    if (!smcDetected || smcOverlays.size === 0 || !priceCandlesFull) {
      return [];
    }
    const out = [];
    const lastT = priceCandlesFull[priceCandlesFull.length - 1].t;
    const base = (id) => ({
      id,
      symbol,
      timeframe,
      createdAt: 0,
      updatedAt: 0,
      locked: true,
    });

    if (smcOverlays.has('fvg')) {
      const allGaps = smcDetected.fairValueGaps?.all || [];
      const lastIdx = priceCandlesFull.length - 1;
      // Left edge ~2 bars before the formation candle (start of the 3-candle
      // pattern). Index path only when formationIndex is a real integer;
      // otherwise fall back to the formation timestamp rather than silently
      // drawing from the first candle in history.
      const fvgLeftEdge = (f) => {
        const fi = Number.isInteger(f.formationIndex) ? f.formationIndex : null;
        return fi !== null
          ? (priceCandlesFull[Math.max(0, fi - 2)]?.t ?? f.formationTime)
          : f.formationTime;
      };

      // Live gaps (open or partial: not filled, not expired) — the forward
      // draws. Solid, full opacity, extend to the live edge. Capped to the most
      // recent SMC_MAX_PER_FAMILY.
      const active = allGaps.filter(fvgIsLive).slice(-SMC_MAX_PER_FAMILY);
      for (const f of active) {
        out.push({
          ...base(`smc-fvg-${f.id}`),
          type: 'fair_value_gap',
          time1: fvgLeftEdge(f),
          time2: lastT,
          top: f.zone.top,
          bottom: f.zone.bottom,
          direction: f.type,
          filled: false,
          fillOpacity: 0.18,
          lineStyle: 'solid',
          lineWidth: 1,
          label: 'FVG',
        });
      }

      // Recently-filled gaps — spent context, not targets. Ghosted and faded by
      // age, drawn ONLY to the candle whose close filled them (fvgFill), and
      // dropped once older than the lookback so the candle field stays clean.
      for (const f of allGaps) {
        const fill = fvgFill(f);
        if (fill === null) { continue; }
        const age = lastIdx - fill.index;
        if (age < 0 || age > FVG_FILLED_GHOST_BARS) { continue; }
        const freshness = 1 - age / FVG_FILLED_GHOST_BARS; // 1 = just filled → 0 = window edge
        out.push({
          ...base(`smc-fvg-${f.id}`),
          type: 'fair_value_gap',
          time1: fvgLeftEdge(f),
          time2: fill.time, // stop at the fill, not the live edge
          top: f.zone.top,
          bottom: f.zone.bottom,
          direction: f.type,
          filled: false, // drive the faded styling directly (lib forces 0.1/0.3 when filled)
          fillOpacity: Math.max(0.03, 0.11 * freshness),
          strokeOpacity: Math.max(0.12, 0.5 * freshness),
          lineStyle: 'dashed',
          lineWidth: 1,
          label: '', // suppress the tag on ghosts (lib honours '' since 1.7.0)
        });
      }
    }

    if (smcOverlays.has('orderBlocks')) {
      const allObs = smcDetected.orderBlocks?.all || [];
      const lastObIdx = priceCandlesFull.length - 1;
      const obBase = (o) => ({
        ...base(`smc-ob-${o.id}`),
        type: 'order_block',
        time: o.formationTime,
        high: o.zone.top,
        low: o.zone.bottom,
        direction: o.type,
        lineWidth: 1,
      });

      // Active / tested OBs — live forward S/R zones, extended to the live edge.
      const live = allObs
        .filter((o) => o.state === 'active' || o.state === 'tested')
        .slice(-SMC_MAX_PER_FAMILY);
      for (const o of live) {
        out.push({
          ...obBase(o),
          endTime: lastT,
          state: o.state,
          fillOpacity: o.state === 'tested' ? 0.11 : 0.16,
          lineStyle: o.state === 'tested' ? 'dashed' : 'solid',
          label: 'OB',
        });
      }

      // Spent OBs — mitigated (closed through) OR breaker (closed through +
      // flipped). Both render as faded ghosts, terminated at the candle that
      // spent them (mitigationTime), recent only. Breakers are NOT singled out
      // with a label: the detector now classifies them correctly, but over a
      // long window they're still common, so a distinct treatment just read as a
      // wall of "Breaker" labels. Quiet ghosts instead (Andy's call).
      for (const o of allObs) {
        if (o.state !== 'mitigated' && o.state !== 'breaker') { continue; }
        if (!Number.isInteger(o.mitigationIndex)) { continue; }
        const age = lastObIdx - o.mitigationIndex;
        if (age < 0 || age > FVG_FILLED_GHOST_BARS) { continue; }
        const freshness = 1 - age / FVG_FILLED_GHOST_BARS;
        out.push({
          ...obBase(o),
          endTime: o.mitigationTime ?? lastT,
          state: 'mitigated',
          fillOpacity: Math.max(0.03, 0.11 * freshness),
          strokeOpacity: Math.max(0.12, 0.5 * freshness),
          lineStyle: 'dashed',
          label: '',
        });
      }
    }

    if (smcOverlays.has('structure')) {
      const breaks = (smcDetected.structureBreaks?.all || []).slice(-SMC_MAX_PER_FAMILY);
      for (const b of breaks) {
        out.push({
          ...base(`smc-${b.type}-${b.id}`),
          type: 'bos_line',
          // Bounded segment: the broken swing (startTime) → the break candle
          // (time), drawn at the broken level (price). dwlf-charting stops the
          // line at the break instead of extending it to the right edge.
          startTime: b.swingTime,
          time: b.breakTime,
          price: b.swingLevel,
          bosType: b.type === 'choch' ? 'ChoCH' : 'BOS',
          direction: b.direction,
          confirmed: true,
          lineStyle: b.type === 'choch' ? 'dashed' : 'solid',
          lineWidth: 1,
          showPrice: false,
          showLabel: true,
        });
      }
    }

    if (smcOverlays.has('liquidity')) {
      const liqLastIdx = priceCandlesFull.length - 1;
      const pools = [
        ...(smcDetected.liquidity?.buySide || []),
        ...(smcDetected.liquidity?.sellSide || []),
      ]
        // Resting liquidity (active) or a recent raid (swept, ghost window) — skip
        // broken pools (spent on a clean break, not actionable resting liquidity).
        .filter((p) => p.state === 'active'
          || (p.state === 'swept' && Number.isInteger(p.sweptIndex)
            && liqLastIdx - p.sweptIndex <= FVG_FILLED_GHOST_BARS))
        .sort((a, b) => a.formationTime - b.formationTime)
        .slice(-SMC_MAX_PER_FAMILY);
      for (const p of pools) {
        const bsl = p.type === 'buy_side';
        out.push({
          ...base(`smc-liq-${p.id}`),
          type: 'bos_line',
          // Pool level as a bounded segment spanning the cluster: anchored at the
          // FIRST equal high/low (so the line visibly tags the equal swings that
          // formed it, not just a level off the last point) → through to the
          // sweep, or the live edge if still resting. Reuses the bos_line primitive.
          startTime: (p.touches && p.touches.length ? p.touches[0].time : p.formationTime),
          time: p.state === 'swept' && Number.isFinite(p.sweptTime) ? p.sweptTime : lastT,
          price: p.level,
          bosType: 'BOS', // unused for label (we pass an explicit `label`); kept non-ChoCH so the line isn't force-dashed
          color: '#9C27B0', // purple — matches the Liquidity overlay legend (bos_line honors color)
          label: bsl ? 'BSL' : 'SSL', // buy-side / sell-side liquidity (needs @dwlf/charting >=1.9.0)
          direction: bsl ? 'bearish' : 'bullish',
          confirmed: true,
          // Swept pools fade (dashed + reduced opacity) and only linger for the
          // ghost window before dropping off; resting pools are solid + full.
          lineStyle: p.state === 'swept' ? 'dashed' : 'solid',
          strokeOpacity: p.state === 'swept' ? 0.4 : 1,
          lineWidth: 1,
          showPrice: false,
          showLabel: true,
        });
      }
    }

    // Premium / Discount — the current dealing range split at equilibrium.
    // Premium (upper half) shaded red = expensive/sell; discount (lower half)
    // green = cheap/buy; a dashed equilibrium line marks the 50% fair-value
    // boundary. One range (the active structural leg), drawn from its anchor to
    // the live edge.
    if (smcOverlays.has('premiumDiscount')) {
      const pd = smcDetected.premiumDiscount;
      if (pd?.range && pd.range.high > pd.range.low) {
        const { high, low, equilibrium, startTime } = pd.range;
        out.push({
          ...base('smc-pd-premium'),
          type: 'rectangle',
          time1: startTime,
          time2: lastT,
          price1: high,
          price2: equilibrium,
          color: '#EF5350', // premium = expensive / sell zone
          fillOpacity: 0.08,
          lineStyle: 'solid',
          lineWidth: 1,
          label: 'Premium',
        });
        out.push({
          ...base('smc-pd-discount'),
          type: 'rectangle',
          time1: startTime,
          time2: lastT,
          price1: equilibrium,
          price2: low,
          color: '#66BB6A', // discount = cheap / buy zone
          fillOpacity: 0.08,
          lineStyle: 'solid',
          lineWidth: 1,
          label: 'Discount',
        });
        out.push({
          ...base('smc-pd-eq'),
          type: 'bos_line',
          startTime,
          time: lastT,
          price: equilibrium,
          bosType: 'BOS', // placeholder; label overrides the text
          color: '#B0BEC5',
          label: 'EQ',
          direction: 'bearish',
          confirmed: true,
          lineStyle: 'dashed',
          strokeOpacity: 0.7,
          lineWidth: 1,
          showPrice: false,
          showLabel: true,
        });
      }
    }

    // Strong / Weak highs & lows — the trend-relevant protected level + the
    // liquidity it sits against. In a downtrend the strong HIGH (protected
    // reversal level) is solid above + the taken weak LOW dashed below; in an
    // uptrend the strong LOW is solid below + the weak HIGH dashed above. Drawn
    // from each level's swing to the live edge.
    if (smcOverlays.has('strongWeak')) {
      const sw = smcDetected.strongWeak;
      // Trend-exclusive: exactly one strong side is active. Pair it with the
      // most-recently-taken weak level on the opposite side.
      const strong = sw?.strongHigh || sw?.strongLow || null;
      const weak = sw?.strongHigh ? sw?.weakLow : sw?.strongLow ? sw?.weakHigh : null;
      if (strong) {
        out.push({
          ...base('smc-sw-strong'),
          type: 'bos_line',
          startTime: strong.time,
          time: lastT,
          price: strong.price,
          bosType: 'BOS',
          color: '#00897B',
          label: strong.kind === 'high' ? 'Strong High' : 'Strong Low',
          direction: strong.kind === 'high' ? 'bearish' : 'bullish',
          confirmed: true,
          lineStyle: 'solid',
          strokeOpacity: 1,
          lineWidth: 2,
          showPrice: false,
          showLabel: true,
        });
      }
      if (weak) {
        out.push({
          ...base('smc-sw-weak'),
          type: 'bos_line',
          startTime: weak.time,
          time: lastT,
          price: weak.price,
          bosType: 'BOS',
          color: '#80CBC4',
          label: weak.kind === 'high' ? 'Weak High' : 'Weak Low',
          direction: weak.kind === 'high' ? 'bearish' : 'bullish',
          confirmed: true,
          lineStyle: 'dashed',
          strokeOpacity: 0.6,
          lineWidth: 1,
          showPrice: false,
          showLabel: true,
        });
      }
    }

    return out;
  }, [smcDetected, smcOverlays, priceCandlesFull, symbol, timeframe]);

  const projectedAnnotations = useMemo(() => {
    // anchored_vwap is rendered as a computed chart series (anchoredVwapSeries),
    // not by the generic annotation layer — strip it here so the chart doesn't
    // try to draw an annotation type it has no geometry for.
    const drawable = (annotations || []).filter((a) => a.type !== 'anchored_vwap');
    if (!drawable.length || !priceCandles?.length) {return drawable;}
    const firstT = priceCandles[0]?.t;
    const lastT = priceCandles[priceCandles.length - 1]?.t;
    if (!Number.isFinite(firstT) || !Number.isFinite(lastT) || firstT === lastT) {
      return drawable;
    }
    const PROJECTABLE_TYPES = new Set(['trendline', 'ray']);

    return drawable.map((ann) => {
      if (!ann._sourceTimeframe || ann._sourceTimeframe === timeframe) {return ann;}
      if (ann.time1 === undefined || ann.time2 === undefined) {return ann;}
      if (ann.price1 === undefined || ann.price2 === undefined) {return ann;}

      if (!PROJECTABLE_TYPES.has(ann.type)) {
        return { ...ann, visible: false };
      }

      const t1In = ann.time1 >= firstT && ann.time1 <= lastT;
      const t2In = ann.time2 >= firstT && ann.time2 <= lastT;
      if (t1In && t2In) {return ann;} // fully in range — render natively

      // Both endpoints on the same side of the visible range: line never crosses it
      if ((ann.time1 < firstT && ann.time2 < firstT)
        || (ann.time1 > lastT && ann.time2 > lastT)) {
        return { ...ann, visible: false };
      }

      const dt = ann.time2 - ann.time1;
      if (!Number.isFinite(dt) || dt === 0) {return ann;}
      const slope = (ann.price2 - ann.price1) / dt; // price per ms

      const clip = (t, p) => {
        if (t < firstT) {return { t: firstT, p: p + slope * (firstT - t) };}
        if (t > lastT) {return { t: lastT, p: p + slope * (lastT - t) };}
        return { t, p };
      };
      const c1 = clip(ann.time1, ann.price1);
      const c2 = clip(ann.time2, ann.price2);

      return {
        ...ann,
        time1: c1.t,
        price1: c1.p,
        time2: c2.t,
        price2: c2.p,
        _projected: true,
        locked: true,
      };
    });
  }, [annotations, priceCandles, timeframe]);

  // Merge the ephemeral SMC overlays onto the persisted/projected annotations
  // handed to the chart. SMC ones are recomputed per render-input, never stored.
  const chartAnnotations = useMemo(
    () => (smcAnnotations.length ? [...projectedAnnotations, ...smcAnnotations] : projectedAnnotations),
    [projectedAnnotations, smcAnnotations],
  );

  return (
    <div
      // The whole chart shell opts out of text selection (see .dwlf-no-select
      // in App.css) — a measure/trendline drag would otherwise sweep a
      // selection across the title bar, toolbar and axis labels.
      className="dwlf-no-select"
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      {/* Title & Action bar */}
      <div
        style={{
          background: 'var(--dwlf-elevated)',
          color: 'var(--dwlf-ink-88)',
          padding: '6px 12px',
          borderRadius: 0,
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          boxShadow: '0 12px 28px rgba(0, 0, 0, 0.35)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            onClick={() => {
              if (isInWatchlist) {
                dispatch(removeFromWatchlist(symbol));
              } else {
                dispatch(addToWatchlist(symbol));
              }
            }}
            title={isInWatchlist ? 'Remove from Watchlist' : 'Add to Watchlist'}
            style={{
              backgroundColor: 'transparent',
              color: isInWatchlist ? '#FFD700' : 'var(--dwlf-ink-50)',
              border: 'none',
              padding: 0,
              fontSize: 18,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            {isInWatchlist ? '★' : '☆'}
          </button>
          <span>{title}</span>
        </div>
        {Array.isArray(timeframeOptions) && timeframeOptions.length > 0 && typeof onTimeframeChange === 'function' && (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {timeframeOptions.map((option) => {
              const isActive = option === effectiveSelectedTimeframe;
              const label = option === 'hourly'
                ? '1H'
                : `${option.charAt(0).toUpperCase()}${option.slice(1)}`;
              return (
                <button
                  key={option}
                  onClick={() => onTimeframeChange(option)}
                  style={{
                    backgroundColor: isActive ? 'var(--dwlf-accent)' : 'var(--dwlf-ink-20)',
                    color: isActive ? 'var(--dwlf-on-accent)' : 'var(--dwlf-text)',
                    border: 'none',
                    padding: '4px 10px',
                    fontSize: 11,
                    borderRadius: 16,
                    cursor: 'pointer',
                    opacity: isActive ? 1 : 0.8,
                    transition: 'background-color 0.2s ease, opacity 0.2s ease',
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
        <div role="group" aria-label="Lenses" style={{ display: 'flex', gap: 0, border: '1px solid var(--dwlf-ink-15)' }}>
          {lenses.map((lens, i) => (
            <button
              key={lens.key}
              type="button"
              aria-pressed={lens.active}
              onClick={() => lens.set(!lens.active)}
              title={`${lens.active ? 'Hide' : 'Show'} the ${lens.label.toLowerCase()} lens`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, height: 26, padding: '0 10px',
                border: 'none', borderLeft: i ? '1px solid var(--dwlf-ink-15)' : 'none', borderRadius: 0, cursor: 'pointer',
                backgroundColor: lens.active ? 'var(--dwlf-ink-08)' : 'transparent',
                color: lens.active ? 'var(--dwlf-text)' : 'var(--dwlf-ink-50)',
                fontFamily: '"Space Mono", monospace', fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
                transition: 'background-color 150ms ease, color 150ms ease',
              }}
            >
              <span aria-hidden="true" style={{
                width: 6, height: 6, flexShrink: 0, transition: 'background-color 150ms ease',
                backgroundColor: lens.active ? `var(--dwlf-${lens.color})` : 'var(--dwlf-ink-20)',
              }} />
              {lens.label}
            </button>
          ))}
        </div>
        {/* Indicators dropdown — analytical layers + Moving Averages sub-section */}
        <ChartToolbarDropdown
          label="Indicators"
          activeCount={indicatorActiveCount}
          isOpen={showIndicatorsDropdown}
          onToggle={setShowIndicatorsDropdown}
        >
          {indicatorMenuItems.map((item) => renderToggleRow(item))}

          {/* VWAP sub-section — collapses the anchor-period overlays
              (Monthly / Quarterly / Yearly / Full History) into one
              expandable group so they don't crowd the flat menu. */}
          <div
            onClick={() => toggleSection('vwap')}
            style={{
              ...sectionHeaderStyle,
              borderTop: '1px solid var(--dwlf-ink-30)',
              marginTop: 4,
            }}
          >
            <span>
              {`VWAP${vwapAnchors.size ? ` (${vwapAnchors.size})` : ''}`}
            </span>
            {renderEventsSwitch('vwap', vwapAnchors.size > 0)}
            <span style={{ fontSize: 12, color: 'var(--dwlf-ink-50)' }}>
              {expandedSections.vwap ? '-' : '+'}
            </span>
          </div>
          {expandedSections.vwap && (
            <>
              {VWAP_ANCHOR_OPTIONS.map((option) => renderMovingAverageRow({
                key: `vwap-${option.key}`,
                label: option.shortLabel,
                color: option.line,
                isActive: vwapAnchors.has(option.key),
                onToggle: () => toggleVwapAnchor(option.key),
              }))}
            </>
          )}

          {/* SMC sub-section — Smart Money Concepts overlays (Fair Value Gaps /
              Order Blocks / BOS-ChoCH) computed client-side from candle history
              and toggled independently, collapsed into one expandable group. */}
          <div
            onClick={() => toggleSection('smc')}
            style={{
              ...sectionHeaderStyle,
              borderTop: '1px solid var(--dwlf-ink-30)',
              marginTop: 4,
            }}
          >
            <span>
              {`SMC${smcOverlays.size ? ` (${smcOverlays.size})` : ''}`}
            </span>
            {renderEventsSwitch('smc', smcOverlays.size > 0)}
            <span style={{ fontSize: 12, color: 'var(--dwlf-ink-50)' }}>
              {expandedSections.smc ? '-' : '+'}
            </span>
          </div>
          {expandedSections.smc && (
            <>
              {SMC_OVERLAY_OPTIONS.map((option) => renderMovingAverageRow({
                key: `smc-${option.key}`,
                label: option.shortLabel,
                color: option.color,
                isActive: smcOverlays.has(option.key),
                onToggle: () => toggleSmcOverlay(option.key),
              }))}
            </>
          )}

          {/* Moving Averages sub-section — collapses SMA + EMA into one
              expandable group instead of two separate top-level sections. */}
          <div
            onClick={() => toggleSection('movingAverages')}
            style={{
              ...sectionHeaderStyle,
              borderTop: '1px solid var(--dwlf-ink-30)',
              marginTop: 4,
            }}
          >
            <span>
              {`Moving Averages${totalActiveMovingAverages ? ` (${totalActiveMovingAverages})` : ''}`}
            </span>
            {renderEventsSwitch('movingAverages', totalActiveMovingAverages > 0)}
            <span style={{ fontSize: 12, color: 'var(--dwlf-ink-50)' }}>
              {expandedSections.movingAverages ? '-' : '+'}
            </span>
          </div>
          {expandedSections.movingAverages && (
            <>
              {SMA_OPTIONS.map(({ period, color, label }) => renderMovingAverageRow({
                key: `sma-${period}`,
                label,
                color,
                isActive: activeSMAs.has(period),
                onToggle: () => toggleSMA(period),
              }))}
              {EMA_OPTIONS.map(({ period, color, label }) => renderMovingAverageRow({
                key: `ema-${period}`,
                label,
                color,
                isActive: activeEMAs.has(period),
                onToggle: () => toggleEMA(period),
              }))}
              {hasActiveMovingAverages && (
                <>
                  <div style={{ borderTop: '1px solid var(--dwlf-ink-30)', margin: '4px 0' }} />
                  <div
                    onClick={() => {
                      setActiveSMAs(new Set());
                      setActiveEMAs(new Set());
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontSize: 12,
                      color: '#FF6B6B',
                      textAlign: 'center',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 107, 107, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    Clear All Moving Averages
                  </div>
                </>
              )}
            </>
          )}
          {indicatorActiveCount > 0 && renderClearRow(handleClearIndicators, 'Clear indicators')}
        </ChartToolbarDropdown>

        {/* Cycles dropdown — DWLF-specific weekly cycle pivots */}
        <ChartToolbarDropdown
          label="Cycles"
          activeCount={cycleActiveCount}
          isOpen={showCyclesDropdown}
          onToggle={setShowCyclesDropdown}
        >
          {cycleMenuItems.map((item) => renderToggleRow(item))}
          {cycleActiveCount > 0 && renderClearRow(handleClearCycles, 'Clear cycles')}
        </ChartToolbarDropdown>

        {/* My Data dropdown — user-owned overlays + Custom Events sub-section */}
        <ChartToolbarDropdown
          label="My Data"
          activeCount={myDataActiveCount}
          isOpen={showMyDataDropdown}
          onToggle={setShowMyDataDropdown}
        >
          {myDataMenuItems.map((item) => renderToggleRow(item))}

          <div
            onClick={() => toggleSection('customEvents')}
            style={{
              ...sectionHeaderStyle,
              borderTop: '1px solid var(--dwlf-ink-30)',
              marginTop: 4,
            }}
          >
            <span>
              {`Custom Events${customEventSelections.size ? ` (${customEventSelections.size})` : ''}`}
            </span>
            <span style={{ fontSize: 12, color: 'var(--dwlf-ink-50)' }}>
              {expandedSections.customEvents ? '-' : '+'}
            </span>
          </div>
          {expandedSections.customEvents && (
            customEventDefinitionList.length ? (
              customEventDefinitionList.map(({ id, name }) => {
                const isActive = customEventSelections.has(id);
                const definition = customEventDefinitions.find((def) => {
                  const defId = def?.eventId ?? def?.id ?? def?.customEventId;
                  return defId === id;
                });
                const eventColor = getEventColor(definition || { id });

                return (
                  <div
                    key={`custom-event-${id}`}
                    onClick={() => {
                      setCustomEventSelections((prev) => {
                        const next = new Set(prev);
                        if (next.has(id)) {
                          next.delete(id);
                        } else {
                          next.add(id);
                        }
                        return next;
                      });
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: 12,
                      color: 'var(--dwlf-ink-88)',
                      backgroundColor: isActive ? 'rgba(76, 175, 80, 0.2)' : 'transparent',
                      borderBottom: '1px solid var(--dwlf-ink-20)',
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = isActive
                        ? 'rgba(76, 175, 80, 0.3)'
                        : 'var(--dwlf-ink-10)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isActive
                        ? 'rgba(76, 175, 80, 0.2)'
                        : 'transparent';
                    }}
                  >
                    <span style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      borderRadius: '50%',
                      backgroundColor: eventColor,
                      border: '1px solid var(--dwlf-ink-30)',
                      flexShrink: 0,
                    }} />
                    <span style={{ flex: 1 }}>{name}</span>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border: isActive ? '1px solid #4CAF50' : '1px solid var(--dwlf-ink-40)',
                      backgroundColor: isActive ? '#4CAF50' : 'transparent',
                      fontSize: 10,
                      color: isActive ? '#000' : 'var(--dwlf-ink-62)',
                      flexShrink: 0,
                    }}>
                      {isActive ? '✓' : ''}
                    </span>
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  padding: '10px 12px',
                  fontSize: 12,
                  color: '#9CA3AF',
                  borderBottom: '1px solid var(--dwlf-ink-20)',
                }}
              >
                No custom events available.
              </div>
            )
          )}

          {/* Custom Strategies sub-section — mirrors Custom Events:
              the user picks which of their authored strategies should
              render their trade markers on the chart. With nothing
              selected, no user trades show. */}
          <div
            onClick={() => toggleSection('userStrategies')}
            style={{
              ...sectionHeaderStyle,
              borderTop: '1px solid var(--dwlf-ink-30)',
              marginTop: 4,
            }}
          >
            <span>
              {`Custom Strategies${userStrategySelections.size ? ` (${userStrategySelections.size})` : ''}`}
            </span>
            <span style={{ fontSize: 12, color: 'var(--dwlf-ink-50)' }}>
              {expandedSections.userStrategies ? '-' : '+'}
            </span>
          </div>
          {expandedSections.userStrategies && (
            userStrategyDefinitionList.length ? (
              userStrategyDefinitionList.map(({ id, name, count }) => {
                const isActive = userStrategySelections.has(id);
                return (
                  <div
                    key={`user-strategy-${id}`}
                    onClick={() => {
                      setUserStrategySelections((prev) => {
                        const next = new Set(prev);
                        if (next.has(id)) {
                          next.delete(id);
                        } else {
                          next.add(id);
                        }
                        return next;
                      });
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: 12,
                      color: 'var(--dwlf-ink-88)',
                      backgroundColor: isActive ? 'rgba(76, 175, 80, 0.2)' : 'transparent',
                      borderBottom: '1px solid var(--dwlf-ink-20)',
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = isActive
                        ? 'rgba(76, 175, 80, 0.3)'
                        : 'var(--dwlf-ink-10)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = isActive
                        ? 'rgba(76, 175, 80, 0.2)'
                        : 'transparent';
                    }}
                  >
                    <span style={{ flex: 1 }}>{name}</span>
                    <span style={{
                      fontSize: 10,
                      color: 'var(--dwlf-ink-45)',
                      flexShrink: 0,
                    }}>
                      {count}
                    </span>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      border: isActive ? '1px solid #4CAF50' : '1px solid var(--dwlf-ink-40)',
                      backgroundColor: isActive ? '#4CAF50' : 'transparent',
                      fontSize: 10,
                      color: isActive ? '#000' : 'var(--dwlf-ink-62)',
                      flexShrink: 0,
                    }}>
                      {isActive ? '✓' : ''}
                    </span>
                  </div>
                );
              })
            ) : (
              <div
                style={{
                  padding: '10px 12px',
                  fontSize: 12,
                  color: '#9CA3AF',
                  borderBottom: '1px solid var(--dwlf-ink-20)',
                }}
              >
                No custom strategies available.
              </div>
            )
          )}
          {myDataActiveCount > 0 && renderClearRow(handleClearMyData, 'Clear my data')}
        </ChartToolbarDropdown>

        {/* Annotation Drawing Tools */}
        <AnnotationToolbar
          activeTool={annotationTool}
          onToolChange={setAnnotationTool}
          annotationCount={annotations.length}
          onClearAll={clearAnnotations}
        />


      </div>

      {/* Chart - Temporarily disabled due to DWLF charting React conflicts */}
      {/*<div style={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: 'var(--dwlf-elevated)',
        color: 'var(--dwlf-ink-50)',
        fontSize: '14px'
      }}>
        Market chart temporarily unavailable due to React conflicts
      </div>*/}
      <div style={{ flex: 1, minHeight: 0, width: '100%', position: 'relative' }}>
        {((showIndicatorEvents && indicatorEventTooltip) || (showCustomEvents && customEventTooltip) || (showUserTrades && userTradeTooltip)) && (() => {
          // Single combined event tooltip. When indicator events,
          // custom events, and user trade markers fire on the same
          // candle they share one card with clearly-labelled sections —
          // this avoids the earlier bug where two separate tooltips
          // computed identical top/left and the later one obscured
          // the first.
          //
          // The tooltip follows the cursor with a small offset, edge-
          // clamps inside the chart container, and falls back to a
          // fixed top-left position when cursorPos is null (touch
          // devices that haven't fired a pointer event yet, or
          // chart-fired crosshair before any mousemove).
          const sections = [];
          if (showIndicatorEvents && indicatorEventTooltip) {
            sections.push({ kind: 'indicator', title: 'Indicator Events', tooltip: indicatorEventTooltip });
          }
          if (showCustomEvents && customEventTooltip) {
            sections.push({ kind: 'custom', title: 'Custom Events', tooltip: customEventTooltip });
          }
          if (showUserTrades && userTradeTooltip) {
            sections.push({ kind: 'user-trade', title: 'User Trade', tooltip: userTradeTooltip });
          }
          const totalSummaryCount = sections.reduce(
            (acc, section) => acc + section.tooltip.summaries.length,
            0,
          );
          const TOOLTIP_WIDTH = 280;
          // Rough height estimate: per section header+meta (~46) + each
          // summary entry (~34) + a divider gap (~12) between sections.
          // Good enough for edge-flipping.
          const estimatedHeight = (sections.length * 46)
            + (totalSummaryCount * 34)
            + (Math.max(0, sections.length - 1) * 12);
          const OFFSET = 16;
          let top;
          let left;
          if (cursorPos) {
            const containerW = cursorPos.containerWidth || 0;
            const containerH = cursorPos.containerHeight || 0;
            left = cursorPos.x + OFFSET;
            top = cursorPos.y + OFFSET;
            if (left + TOOLTIP_WIDTH > containerW) {
              left = Math.max(8, cursorPos.x - TOOLTIP_WIDTH - OFFSET);
            }
            if (top + estimatedHeight > containerH) {
              top = Math.max(8, cursorPos.y - estimatedHeight - OFFSET);
            }
          } else {
            top = 12;
            left = 12;
          }
          return (
            <div
              style={{
                position: 'absolute',
                top,
                left,
                zIndex: 5,
                background: 'var(--dwlf-bg-95)',
                color: 'var(--dwlf-ink-88)',
                padding: '10px 12px',
                borderRadius: 8,
                boxShadow: '0 8px 24px rgba(0,0,0,0.45)',
                border: '1px solid rgba(148, 163, 184, 0.18)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                width: TOOLTIP_WIDTH,
                maxWidth: TOOLTIP_WIDTH,
                pointerEvents: 'none',
                fontSize: 12,
                lineHeight: 1.45,
                transition: 'top 80ms linear, left 80ms linear',
              }}
            >
              {sections.map((section, sectionIdx) => (
                <div
                  key={section.kind}
                  style={{
                    marginTop: sectionIdx === 0 ? 0 : 10,
                    paddingTop: sectionIdx === 0 ? 0 : 10,
                    borderTop: sectionIdx === 0 ? 'none' : '1px solid rgba(148, 163, 184, 0.18)',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 13 }}>{section.title}</div>
                  <div style={{ fontSize: 11, opacity: 0.8, marginBottom: 6 }}>
                    {section.tooltip.dateLabel} · Price {formatPriceValue(section.tooltip.price)}
                  </div>
                  <div>
                    {section.tooltip.summaries.map((entry, index) => (
                      <p key={`${section.kind}-summary-${index}`} style={{ margin: '0 0 6px' }}>
                        {entry.split('\n').map((line, idx, arr) => (
                          <React.Fragment key={`${section.kind}-line-${index}-${idx}`}>
                            {line}
                            {idx < arr.length - 1 && <br />}
                          </React.Fragment>
                        ))}
                      </p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
        {/* OHLC info panel — always-visible overlay in the top-left
            corner that shows the hovered candle's Open/High/Low/Close
            and datetime. Driven by the crosshair callback, so it
            updates in real time as the user moves the cursor across
            the chart. */}
        {hoveredCandle && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 4,
              display: 'flex',
              flexWrap: 'wrap',
              columnGap: 12,
              rowGap: 2,
              // Wraps under itself rather than running into the price axis (VWAP bands add many entries).
              maxWidth: 'calc(100% - 96px)',
              alignItems: 'center',
              fontSize: 11,
              color: 'var(--dwlf-ink-70)',
              pointerEvents: 'none',
              fontFamily: 'monospace',
            }}
          >
            <span style={{ color: 'var(--dwlf-ink-50)' }}>
              {(() => {
                const d = new Date(hoveredCandle.t);
                const datePart = d.toLocaleDateString(undefined, {
                  year: 'numeric',
                  month: 'short',
                  day: '2-digit',
                });
                const isSubDaily = !['daily', 'weekly', 'monthly'].includes(lowerTf);
                if (!isSubDaily) {return datePart;}
                const timePart = d.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: false,
                });
                return `${datePart} ${timePart}`;
              })()}
            </span>
            <span>O <span style={{ color: 'var(--dwlf-ink-88)' }}>{formatPriceValue(hoveredCandle.o)}</span></span>
            <span>H <span style={{ color: 'var(--dwlf-ink-88)' }}>{formatPriceValue(hoveredCandle.h)}</span></span>
            <span>L <span style={{ color: 'var(--dwlf-ink-88)' }}>{formatPriceValue(hoveredCandle.l)}</span></span>
            <span>C <span style={{ color: 'var(--dwlf-ink-88)' }}>{formatPriceValue(hoveredCandle.c)}</span></span>
            {hoveredSeriesValues.map((sv) => (
              <span key={sv.key}>
                <span style={{ color: sv.color || 'var(--dwlf-ink-50)' }}>{sv.key}</span>{' '}
                <span style={{ color: 'var(--dwlf-ink-88)' }}>{formatPriceValue(sv.value)}</span>
              </span>
            ))}
          </div>
        )}
        {/* Two-point tool status indicator */}
        {annotationTool === 'rectangle' && (
          <div style={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(59, 130, 246, 0.9)',
            color: 'white',
            padding: '4px 12px',
            borderRadius: 4,
            fontSize: 11,
            fontWeight: 500,
            zIndex: 100,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}>
            <span>📐</span>
            {pendingFirstPoint ? 'Click to set second corner • ESC to cancel' : 'Click to set first corner'}
          </div>
        )}
        <div ref={chartContainerRef} style={{ width: '100%', height: '100%', position: 'relative' }}>
          {/* Custom Event Legend */}
          {showCustomEvents && (
            <CustomEventLegend
              events={customEventDefinitionList}
              activeSelections={customEventSelections}
              onToggleEvent={(eventId) => {
                setCustomEventSelections((prev) => {
                  const next = new Set(prev);
                  if (next.has(eventId)) {
                    next.delete(eventId);
                  } else {
                    next.add(eventId);
                  }
                  return next;
                });
              }}
              getEventColor={getEventColor}
            />
          )}
          {/* Animation Loader Overlay */}
          {overlayVisible && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                transition: 'opacity 0.3s ease-out',
                opacity: phase === 'idle' || phase === 'background' ? 1 : 0,
                pointerEvents: phase === 'idle' || phase === 'background' ? 'auto' : 'none',
                cursor: phase === 'idle' || phase === 'background' ? 'pointer' : 'default',
              }}
              onClick={() => {
                // Allow click-to-skip animation during active phases
                if (phase !== 'complete' && skipToEnd) {
                  skipToEnd();
                }
              }}
            >
              <DWLFLoader size="medium" showText={true} />
            </div>
          )}
        <DWLFChart
          key={chartAppearanceKey}
          ref={chartRef}
          spec={chartSpec}
          darkMode={isDark}
          palette={chartPalette}
          showGrid={showAdaptiveGrid}
          enablePanZoom
          // Make existing annotations transparent to clicks whenever a drawing
          // tool is active — not merely once a placement is under way. An
          // annotation under the cursor captures mousedown via stopPropagation,
          // so with `isPlacingTwoPoint` alone the FIRST click of a measure (or
          // any tool) was swallowed whenever it happened to land on an existing
          // box, and the annotation showed its move cursor as if it were the
          // target. With a tool active you are drawing, not selecting, so the
          // tool wins.
          // `isBrushDrawing` is not redundant here: setActiveTool clears the
          // pending-placement state but not an in-flight brush stroke, so a
          // stroke can outlive the tool selection.
          interactiveAnnotations={!isPlacingAnnotation && !isBrushDrawing}
          timeframe={timeframe}
          initialVisibleCount={panInitialVisible}
          onViewportChange={handleViewportChange}
          visibleRange={switchRange}
          visibleRangeInterpolation="zoom"
          onVisibleRangeSettled={handleSwitchSettled}
          extraRightSlots={panExtraSlots}
          compressGaps={compressGaps}
          className="dwlf-market-chart__chart"
          style={{ width: '100%', height: '100%', cursor: isPlacingAnnotation ? 'crosshair' : undefined }}
          axisColors={axisColors}
          crosshairSnapMode={crosshairSnapMode}
          showCrosshairPriceLabel={showCrosshairPriceLabel}
          // The hovered candle and series values show in the header; its date on the time axis.
          showPaneTooltips={false}
          // Animation props
          animationState={animationState}
          // Annotation props
          annotations={chartAnnotations}
          selectedAnnotationId={selectedAnnotationId}
          onAnnotationSelect={(id, info) => {
            if (id) {
              selectAnnotation(id);
              // Issue #3 fix: Update popover position - use provided coords or estimate from chart
              if (info?.screenX !== undefined && info?.screenY !== undefined) {
                setAnnotationPopoverPosition({ x: info.screenX, y: info.screenY });
              } else if (chartRef.current) {
                // Fallback: position popover near chart center if no coords provided
                const rect = chartRef.current.getBoundingClientRect?.() || 
                             chartRef.current.container?.getBoundingClientRect?.();
                if (rect) {
                  setAnnotationPopoverPosition({ 
                    x: rect.left + rect.width / 2, 
                    y: rect.top + 100 
                  });
                }
              }
            } else {
              deselectAnnotation();
            }
          }}
          onAnnotationMove={(id, update) => {
            // Don't persist moves for annotations that aren't real stored ones:
            // timeframe-projected (_projected) or ephemeral SMC overlays (locked).
            // Search the full set the chart renders, not just projectedAnnotations,
            // so ephemeral SMC ids (smc-*) don't fall through to updateAnnotation.
            const ann = chartAnnotations?.find(a => a.id === id);
            if (ann?._projected || ann?.locked) {return;}
            updateAnnotation(id, update);
          }}
          onAnnotationDoubleClick={(id, info) => {
            openAnnotationPopoverAtPosition(id, info);
          }}
          onAnnotationTextEdit={(id, info) => {
            openAnnotationPopoverAtPosition(id, info);
          }}
          onCanvasMouseMove={(isPlacingTwoPoint || isBrushDrawing) ? (info) => {
            // Record the last canvas position so the window-level mouseup
            // listener can commit even when the release lands off-pane.
            lastCanvasInfoRef.current = info;
            if (isPlacingTwoPoint) {
              handleAnnotationMouseMove(info);
            }
            if (isBrushDrawing) {
              handleBrushMouseMove(info);
            }
          } : undefined}
          onChartCanvasClick={(info) => {
            // Handle animation skip first
            if (handleChartAreaClick(info)) {
              return; // Animation skip handled
            }

            // If mouseup just handled a placement commit, skip this click so
            // we don't double-fire createXyzAnnotation.
            if (justPlacedOnMouseUpRef.current) {
              justPlacedOnMouseUpRef.current = false;
              return;
            }

            // Clicking empty chart space deselects any selected annotation
            if (!annotationTool && selectedAnnotationId) {
              deselectAnnotation();
              return;
            }

            // Handle annotation placement when a tool is active
            if (annotationTool) {
              const result = handleAnnotationChartClick({
                time: info.time,
                price: info.price,
                screenX: info.screenX,
                screenY: info.screenY,
              });
              if (result && result.type === 'text') {
                // Show text input at click position
                setTextInputPosition({ x: info.screenX, y: info.screenY });
              }
              if (result && (
                result.type === 'hline'
                || result.type === 'vline'
                || result.type === 'crossline'
                || result.type === 'trendline'
                || result.type === 'ray'
                || result.type === 'long_position'
                || result.type === 'short_position'
              )) {
                // Set popover position for single-click and two-click completions
                if (!result.pending) {
                  setAnnotationPopoverPosition({ x: info.screenX, y: info.screenY });
                }
              }
              if (result && result.type === 'rectangle' && result.step === 'complete') {
                // Rectangle created — show edit popover
                setAnnotationPopoverPosition({ x: info.screenX, y: info.screenY });
              }
              if (result && result.type === 'arrow' && result.step === 'complete') {
                // Issue #5 fix: For arrows, anchor popover to the completion click
                if (info.screenX !== undefined && info.screenY !== undefined) {
                  setAnnotationPopoverPosition({ x: info.screenX, y: info.screenY });
                }
              }
              if (result && result.type === 'fibRetracement' && !result.pending) {
                setAnnotationPopoverPosition({ x: info.screenX, y: info.screenY });
              }
            }
          }}
          onChartCanvasHover={handleAnnotationChartHover}
        />
        </div>
      </div>

      {/* Annotation Edit Popover */}
      {editPopoverAnnotation && selectedAnnotationId && (
        <AnnotationEditPopover
          annotation={editPopoverAnnotation}
          position={annotationPopoverPosition}
          onUpdate={updateAnnotation}
          onDelete={deleteAnnotation}
          onRearmAlert={rearmAlertLine}
          onClose={deselectAnnotation}
          currentTimeframe={effectiveSelectedTimeframe}
          availableTimeframes={timeframeOptions}
        />
      )}

      {/* Text Annotation Input */}
      {pendingTextAnnotation && (
        <TextAnnotationInput
          position={textInputPosition}
          onSubmit={(text) => {
            // Issue #6 fix: Set popover position after text annotation creation
            const result = completePendingTextAnnotation(text);
            if (result?.position) {
              setAnnotationPopoverPosition(result.position);
            } else if (textInputPosition) {
              // Fallback to text input position
              setAnnotationPopoverPosition(textInputPosition);
            }
          }}
          onCancel={cancelPendingTextAnnotation}
        />
      )}
    </div>
  );
};

DWLFMarketChart.propTypes = {
  isMobile: PropTypes.bool,
  onReachOldestCandles: PropTypes.func,
  title: PropTypes.string,
  symbol: PropTypes.string.isRequired,
  marketData: PropTypes.array,
  timeframe: PropTypes.string,
  timeframeOptions: PropTypes.arrayOf(PropTypes.string),
  selectedTimeframe: PropTypes.string,
  onTimeframeChange: PropTypes.func,
  supportResistance: PropTypes.object,
  // Stored keyLevel.* events in library shape (useBackendKeyLevelEvents).
  keyLevelEvents: PropTypes.arrayOf(PropTypes.object),
  cycleFibEvents: PropTypes.arrayOf(PropTypes.object),
  divergenceEvents: PropTypes.arrayOf(PropTypes.object),
  trendlines: PropTypes.array,
  dailyTrendlines: PropTypes.array,
  hourlyTrendlines: PropTypes.array,
  weeklyTrendlines: PropTypes.array,
  chartPreferences: PropTypes.object,
  axisColors: PropTypes.shape({
    light: PropTypes.string,
    dark: PropTypes.string,
  }),
  crosshairSnapMode: PropTypes.oneOf(['series', 'pointer']),
  showCrosshairPriceLabel: PropTypes.bool,
  showAdaptiveGrid: PropTypes.bool,
  onChartPreferencesChange: PropTypes.func,
  onSelectedCustomEventIdsChange: PropTypes.func,
  alerts: PropTypes.array,
  fibLevels: PropTypes.array,
  autoShowFib: PropTypes.bool,
  swingPoints: PropTypes.array,
  weeklySwingPoints: PropTypes.array,
  customEvents: PropTypes.array,
  customEventDefinitions: PropTypes.array,
  customEventDefinitionsLoaded: PropTypes.bool,
  tradeSignals: PropTypes.array,
  tradeOverlays: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  systemTradeSignals: PropTypes.array,
  userTradeSignals: PropTypes.array,
  tradeJournalTrades: PropTypes.array,
  showDailyTrendlines: PropTypes.bool,
  showWeeklyTrendlines: PropTypes.bool,
  cycleTrendlinesBeta: PropTypes.bool,
  showHourlyTrendlines: PropTypes.bool,
  onToggleDailyTrendlines: PropTypes.func,
  onToggleWeeklyTrendlines: PropTypes.func,
  onToggleHourlyTrendlines: PropTypes.func,
  cycleWindows: PropTypes.shape({ low: PropTypes.object, high: PropTypes.object, verification: PropTypes.object }),
  pastCycleWindows: PropTypes.shape({ low: PropTypes.array, high: PropTypes.array }),
  onShowPastWindowsChange: PropTypes.func,
  weinsteinStage: PropTypes.object,
  onShowStagesChange: PropTypes.func,
  onShowFibChange: PropTypes.func,
  cycleWindowState: PropTypes.shape({ low: PropTypes.object, high: PropTypes.object }),
  weeklyCycleLows: PropTypes.array,
  weeklyCycleHighs: PropTypes.array,
  liveCycleLow: PropTypes.object,
  liveCycleHigh: PropTypes.object,
  liveCycleLowEvents: PropTypes.array,
  liveCycleHighEvents: PropTypes.array,
  // Annotation sidebar integration
  onAnnotationsChange: PropTypes.func,
  onSelectedAnnotationChange: PropTypes.func,
  annotationRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
};

export default DWLFMarketChart; 

/* eslint-enable indent */
